/**
 * PingPongScene - 乒乓球主场景
 *
 * P1（玩家）：右侧球拍，鼠标/触摸控制
 * P2（Bot）：左侧球拍，由宿主通过 postMessage 控制
 *
 * 场地为横向布局：
 *   P2 球拍 (左) ←——球——→ P1 球拍 (右)
 *
 * 当嵌入 iframe 但无宿主控制 P2 时，P2 使用内置 AI 作为 fallback。
 */

import Phaser from 'phaser'
import { GameBridge, type CombatStateData, type SkillCatalogItem } from '../platform/Bridge'

// ===== 常量 =====

const PADDLE_WIDTH = 26
const PADDLE_HEIGHT = 132
const PADDLE_VISUAL_HEIGHT = PADDLE_HEIGHT
const PADDLE_MARGIN = 30
const BOT_PADDLE_SPEED = 650
const PLAYER_PADDLE_SPEED = 1150
const PLAYER_CHARGED_SPEED = 330

const BALL_RADIUS = 10
const BALL_COLOR = 0xffffff
const PLAYER_BALL_COLOR = 0xff5266
const AI_BALL_COLOR = 0x42ecff
const BALL_INITIAL_SPEED = 410
const BALL_SPEED_INCREMENT = 14
const BALL_NORMAL_MAX_SPEED = 3000

const CHARGE_RATE = 0.95
const CHARGE_DECAY = 1.55
const CHARGE_SPEED_MULTIPLIER = 1.08
const CHARGE_FLAT_SPEED_BONUS = 620
const CHARGE_SPIN_BONUS = 420

const SPIN_MAX = 1800
const SPIN_CURVE_FORCE = 2.05
const SPIN_DECAY_PER_SECOND = 0.94
const SPIN_SPEED_BONUS = 0.42
const MAX_BOUNCE_ANGLE = Phaser.Math.DegToRad(45)

const TRAIL_MAX_POINTS = 28
const WINNING_SCORE = 11

const ENERGY_MAX = 100
const PLAYER_START_ENERGY = 0
const AI_START_ENERGY = 0
const SKILL_GLOBAL_COOLDOWN_MS = 1000
const FULL_CHARGE_COUNTER_THRESHOLD = 0.98
const RETURN_STREAK_TIER_1 = 1
const RETURN_STREAK_TIER_2 = 2
const RETURN_STREAK_TIER_3 = 4
const RETURN_STREAK_TIER_4 = 7
const RETURN_SPARK_TEXTURE_KEY = 'return-spark-particle'
const RETURN_STREAK_TEXTURE_KEY = 'return-streak-particle'
const RETURN_GLOW_TEXTURE_KEY = 'return-glow-particle'

const CENTER_LINE_COLOR = 0x4550a8
const BG_COLOR = 0x050713
const ARENA_BG_VIDEO_KEY = 'arena-bg-video'
const ARENA_BG_VIDEO_URL = '/assets/gameplay/arena-bg-loop.mp4?v=20260513'
const ARENA_BG_POSTER_KEY = 'arena-bg-poster'
const ARENA_BG_POSTER_URL = '/assets/gameplay/arena-bg-loop-poster.jpg?v=20260513'
const ARENA_BG_REPLAY_INTERVAL_MS = 10_000
const AI_PADDLE_KEY = 'ai-paddle-blue'
const AI_PADDLE_URL = '/assets/gameplay/paddles/ai-paddle-blue.png?v=20260507'
const PLAYER_PADDLE_KEY = 'player-paddle-red'
const PLAYER_PADDLE_URL = '/assets/gameplay/paddles/player-paddle-red.png?v=20260507'
const SKILL_ICON_SHEET_KEY = 'skill-icons-sheet'
const SKILL_ICON_SHEET_URL = '/assets/ui/skill-icons-sheet.png?v=20260507'
const SKILL_ICON_FRAME_SIZE = 384
const PLAYER_SKILL_CUTIN_DURATION_MS = 6000
const GAME_SFX_VOLUME_SCALE = 2.25
const SKILL_SFX_VOLUME_SCALE = 2.75
const SKILL_CUTIN_VIDEO_VOLUME = 0.34
const SKILL_CUTIN_STINGER_VOLUME_SCALE = 1.1
const PADDLE_HIT_SFX_MIN_VOLUME = 0.34
const WALL_HIT_SFX_MIN_VOLUME = 0.24
type HitZone = 'center' | 'slice' | 'edge'
type Side = 'p1' | 'p2'
type PlayerSkillId = 'qianying' | 'flame' | 'thunder' | 'light'
type AiSkillId = 'serpent' | 'fold' | 'double'
type SkillId = PlayerSkillId | AiSkillId
type AiDifficulty = 'easy' | 'normal' | 'hard' | 'hell'
type GameOverEvaluation = '完胜' | '大胜' | '险胜' | '惜败' | '败北' | '惨败'

interface GameOverResult {
  winner: Side
  winnerLabel: string
  loserLabel: string
  p1: number
  p2: number
  finalScoreText: string
  resultForBot: string
  evaluation: GameOverEvaluation
  quote: string
  maxRallyCount: number
}

interface SkillConfig {
  id: SkillId
  owner: Side
  name: string
  keyLabel: string
  energyCost: number
  cooldownMs: number
  color: number
  maxSpeed: number
}

interface ActiveSkillState {
  id: SkillId
  owner: Side
  elapsed: number
  duration: number
  color: number
  maxSpeed: number
  startY: number
  baseVy: number
  zigzagStep: number
  zigzagSign: number
  breakIndex: number
  hasPierced: boolean
  counterReadyAtLaunch: boolean
  piercesPaddle?: boolean
  qianyingSeed?: number
  aiSkillSeed?: number
  thunderRoute?: ThunderRoutePoint[]
  thunderRouteIndex?: number
  thunderSeed?: number
}

interface TrailPoint {
  x: number
  y: number
  spin: number
}

interface ThunderRoutePoint {
  x: number
  y: number
}

interface FakeBall {
  body: Phaser.GameObjects.Arc
  vx: number
  vy: number
  age: number
  maxAge: number
  color: number
  trail: Array<{ x: number; y: number }>
}

interface AiSkillDecision {
  id: AiSkillId
  score: number
  castChance: number
}

interface AiDifficultyConfig {
  label: string
  reactionDelayMs: number
  targetUpdateIntervalMs: number
  predictionConfidence: number
  predictionErrorPx: number
  maxPredictionTime: number
  paddleSpeed: number
  centerBias: number
  mistakeChance: number
  mistakeErrorPx: number
  attackOffsetScale: number
  skillThinkIntervalMs: number
  skillChanceMultiplier: number
}

interface GameOverEvaluationConfig {
  color: string
  quotes: string[]
}

const PLAYER_SKILL_ORDER: PlayerSkillId[] = ['qianying', 'thunder', 'light', 'flame']
const PLAYER_SKILL_PIERCE_CHANCE: Record<PlayerSkillId, number> = {
  qianying: 0.5,
  thunder: 0.7,
  light: 0.9,
  flame: 1,
}
const AI_SKILL_ORDER: AiSkillId[] = ['serpent', 'fold', 'double']
const SKILL_ICON_FRAME_INDEX: Record<PlayerSkillId, number> = {
  qianying: 0,
  flame: 1,
  thunder: 2,
  light: 3,
}
const PLAYER_SKILL_CUTIN_URLS: Record<PlayerSkillId, string> = {
  qianying: '/assets/skill-cutins/qianying.mp4?v=20260507',
  flame: '/assets/skill-cutins/flame.mp4?v=20260507',
  thunder: '/assets/skill-cutins/thunder.mp4?v=20260507',
  light: '/assets/skill-cutins/light.mp4?v=20260507',
}
const AI_SKILL_CUTIN_URLS: Record<AiSkillId, string> = {
  serpent: '/assets/skill-cutins/serpent.mp4?v=20260507',
  fold: '/assets/skill-cutins/fold.mp4?v=20260507',
  double: '/assets/skill-cutins/double.mp4?v=20260507',
}

const AI_DIFFICULTY_CONFIGS: Record<AiDifficulty, AiDifficultyConfig> = {
  easy: {
    label: '容易',
    // 「最笨」配置：反应慢、移动慢、几乎不预测、技能很少释放、攻击不刁钻
    reactionDelayMs: 520,        // 反应延迟（原 240ms）
    targetUpdateIntervalMs: 420, // 思考间隔（原 180ms）
    predictionConfidence: 0.05,  // 几乎不预测（原 0.35）
    predictionErrorPx: 160,      // 预测大误差（原 70px）
    maxPredictionTime: 0.15,     // 最多看 0.15s（原 0.45s）
    paddleSpeed: 200,            // 球拍移动速度（原 390，约 51%）
    centerBias: 0.65,            // 严重倾向回中心（原 0.34）
    mistakeChance: 0.55,         // 55% 概率失误（原 0.18）
    mistakeErrorPx: 220,         // 失误幅度大（原 92px）
    attackOffsetScale: 0.10,     // 几乎不主动攻击边角（原 0.35）
    skillThinkIntervalMs: 1500,  // 1.5s 才考虑一次技能（原 780ms）
    skillChanceMultiplier: 0.10, // 释放技能概率超低（原 0.38）
  },
  normal: {
    label: '中等',
    reactionDelayMs: 170,
    targetUpdateIntervalMs: 130,
    predictionConfidence: 0.55,
    predictionErrorPx: 45,
    maxPredictionTime: 0.65,
    paddleSpeed: 470,
    centerBias: 0.22,
    mistakeChance: 0.09,
    mistakeErrorPx: 62,
    attackOffsetScale: 0.62,
    skillThinkIntervalMs: 580,
    skillChanceMultiplier: 0.72,
  },
  hard: {
    label: '困难',
    reactionDelayMs: 115,
    targetUpdateIntervalMs: 95,
    predictionConfidence: 0.72,
    predictionErrorPx: 28,
    maxPredictionTime: 0.9,
    paddleSpeed: 540,
    centerBias: 0.12,
    mistakeChance: 0.04,
    mistakeErrorPx: 42,
    attackOffsetScale: 0.9,
    skillThinkIntervalMs: 420,
    skillChanceMultiplier: 1,
  },
  hell: {
    label: '地狱',
    reactionDelayMs: 82,
    targetUpdateIntervalMs: 70,
    predictionConfidence: 0.95,
    predictionErrorPx: 16,
    maxPredictionTime: 1.05,
    paddleSpeed: 635,
    centerBias: 0.04,
    mistakeChance: 0.012,
    mistakeErrorPx: 28,
    attackOffsetScale: 1.16,
    skillThinkIntervalMs: 310,
    skillChanceMultiplier: 1.18,
  },
}

const GAME_OVER_EVALUATIONS: Record<GameOverEvaluation, GameOverEvaluationConfig> = {
  完胜: {
    color: '#ffd700',
    quotes: [
      '？？？我刚才眼睛是闭着的你信不信！',
      '网卡了网卡了，这局不算啊喂！',
      '你开挂了吧？举报了啊！',
      '不是，我刚才在回消息，根本没看屏幕好吧！',
      '离谱！我鼠标刚才被猫踩了！',
      '赢了别急着嘚瑟，你这辈子的运气可能都用完了。',
      '截图发朋友圈吧～毕竟这种机会不多。',
      '我在试新战术！懂不懂什么叫战略性撤退？',
      '你赢的不是我，你赢的是一个没睡醒的我。',
      '行，让你爽一把。下局哭着叫我别打了可别怪我！',
      '家人们谁懂啊！我被一个菜鸟打败了，精神状态已崩。',
      '这波属于泼天的富贵落你头上了，接好了，下次没有！',
      '你是懂偷塔的！正面刚你打不过我！',
      '服了！主打一个措手不及，你这是偷袭！',
      '不是哥们，你这赢法也太老六了吧？',
      '我的精神状态：已碎。原因：被菜鸡翻盘。',
      '好好好，这泼天的运气我接不住，你拿走吧～',
      '啊这……我刚才在想今晚吃什么来着？',
      '无语住了！你赢我的方式能不能体面一点？',
      '寄了。这局复盘完我要写三千字反思。',
    ],
  },
  大胜: {
    color: '#00ff88',
    quotes: [
      '运气好而已，有本事再来一局！',
      '就这分差你也好意思赢？',
      '行吧，给你个面子。下局可没这好事了～',
      '我刚才故意送了几个球，你没看出来？',
      '你的巅峰就是我的低谷，想想就心酸。',
      '笑什么笑！赢一局尾巴翘天上了？',
      '记住今天的日子，以后可没机会了。',
      '哦，赢了啊。恭喜。然后呢？',
      '这局不代表什么，代表我没热身而已。',
      '你这赢法跟捡的似的，不够光彩啊～',
      '你是踩中我精神状态低谷了！主打一个趁人之危。',
      '格局打开，这局算我请你赢的～',
      '你这波赢了，但赢得很没有松弛感。',
      '好家伙，我被PUA了一整局！',
      '行。你的版本，你的强度，你的快乐。',
      '我复盘了一下，结论是你运气太好，该去买彩票。',
      '别嘚瑟！你赢我的事不会上热搜的。',
      '典。典中典。典到我无话可说。',
      '真的会谢！谢谢你让我认清这个残酷的世界～',
      '你赢了，但你有没有想过我的情绪价值？',
    ],
  },
  险胜: {
    color: '#7df7ff',
    quotes: [
      '就这比分你也好意思庆祝？不尴尬吗！',
      '赢一分就叫赢？这叫苟！',
      '你最后那球纯蒙的吧？别装了！',
      '险胜而已，膨胀不了的，兄弟。',
      '再来一局，我保证你笑不出来。',
      '你那最后一分，我故意的！测试你心态～',
      '一分之差，你心跳多少？180？',
      '赢麻了是吧？下局等着被教育吧！',
      '这种比分发出去多没面子，你想过没？',
      '侥幸罢了，你自己心里也清楚。',
      '你这波赢了但含金量不高，建议标注星号。',
      '破防了，但不多。也就亿点点。',
      '我血压已经上来了！你满意了吧？',
      '这波属于你苟到最后，苟学大师！',
      '一分，就一分！你好意思发朋友圈吗？',
      '赢了别急，让我先缓缓……我需要做心理建设。',
      '你这赢法毫无美感，纯靠命硬。',
      '我不是输了，我只是没来得及赢！',
      '啊这……差一口气的感觉你体会过吗？',
      '听我说谢谢你！谢谢你把比分搞得这么难看～',
    ],
  },
  惜败: {
    color: '#ffb074',
    quotes: [
      '差一点你就赢了呢～可惜差的是实力。',
      '挺能打的嘛，就是差那么亿点点～',
      '你已经很接近了。接近被我打哭的边缘。',
      '打到这个份上确实不容易！但还是我赢了呀～',
      '我手都在抖，但你不需要知道这个。',
      '快了快了！再练个十年八年差不多能赢我了～',
      '你差的不是技术，是命。',
      '有一说一，你这局表现可以。可以当我的陪练了～',
      '刚才有一秒我觉得要输了，然后想起对面是你。',
      '感谢你让比赛变得紧张，虽然结局不会变～',
      '你的努力我看到了，但没用。这叫绝对实力。',
      '建议你把这局录下来反复看看差在哪。答案是差在对手是我～',
      '你已经是我打过最能挣扎的了！给你颁个精神奖～',
      '你这比分就像高考差一分上线。心疼，但帮不了你。',
      '好紧张好刺激！但我赢了嘿嘿～你的心情我不关心。',
      '嗯，你进步了。从秒输变成了慢慢输。',
      '主打一个虽败犹荣是吧？不好意思，没有荣，只有败。',
      '你有被拿捏到吗？我猜有～',
      '差的那一分，就是你和我之间的银河。',
      '这局你表现得像个真正的对手了。虽然结果还是一样～',
    ],
  },
  败北: {
    color: '#ff6b6b',
    quotes: [
      '就这？',
      '要不你先去跟墙练练？墙至少会弹回来。',
      '你确定你的鼠标是插上了的？',
      '打完了？我还以为热身赛呢。',
      '你这水平去跟新手机器人打，应该能赢吧。大概。',
      '不是，你认真的吗？这也太好打了。',
      '你的挣扎我看到了，但没用。',
      '我全程单手打的，你没发现吗？',
      '这分差我都不好意思发战绩了，帮你留个面子～',
      '建议你转行。比如去当观众。',
      '你这乒乓球打的，班味太重了！建议下班再来。',
      '这局给你的体验，叫做被安排得明明白白。',
      '加大力度了吗？没有。我一直都是这个力度，你太弱了。',
      '你是i人吧？打球都这么内敛，一分都不好意思多拿～',
      '建议你的球拍去做个售后，因为它今天没上过班。',
      '你这场发挥，完美诠释了什么叫「参与感」。',
      '我今天的状态一般，但打你够了。',
      '哥！醒醒！你已经输了，别在那愣着了。',
      '你是来体验被打的感觉的吗？那你体验到了。',
      '这波你是纯纯来给我涨KD比的。',
    ],
  },
  惨败: {
    color: '#cc3333',
    quotes: [
      '你不适合打乒乓球，适合看我打。',
      '你确定你不是来给我刷经验的？',
      '我以为你在让我，结果你是认真的？？',
      '这比分截图已经发群里了，来不及了～',
      '你的球拍是反着拿的吗？不对，反着拿也不至于这样。',
      '要不下局我闭一只眼？不够的话两只也行～',
      '别难过！这个世界上总要有人当分母的。',
      '我本来想夸你两句，但实在找不到切入点……',
      '你有一个优点，就是输了还敢再来！',
      '笑死！你打球的样子像在给我递球。',
      '你这不叫打球，叫行为艺术。我给你打9分。',
      '大无语事件：有人以为自己会打乒乓球。',
      '你这精神状态，适合去参加乒乓球废物大赛，冠军预定！',
      '这比分我都不好意思截图，怕别人以为我在欺负小朋友……',
      '下次打之前建议先百度一下乒乓球规则，起码知道球往哪打。',
      '你打球的样子让我DNA动了，笑的那种。',
      '整活呢？不是的话那就更离谱了！',
      '我怀疑你球拍握反了。但不影响结果，因为正着也这样。',
      '你是显眼包吧？输成这样也是种才华！',
      '家人们！有人打乒乓球跟我表演喂球，真的绝了！',
    ],
  },
}

const SKILL_CONFIGS: Record<SkillId, SkillConfig> = {
  qianying: {
    id: 'qianying',
    owner: 'p1',
    name: '千影幻袭',
    keyLabel: '1',
    energyCost: 35,
    cooldownMs: 4000,
    color: 0xd56bff,
    maxSpeed: 990,
  },
  flame: {
    id: 'flame',
    owner: 'p1',
    name: '炽焰爆扣',
    keyLabel: '4',
    energyCost: 100,
    cooldownMs: 12000,
    color: 0xff5a1f,
    maxSpeed: 1800,
  },
  thunder: {
    id: 'thunder',
    owner: 'p1',
    name: '雷霆出击',
    keyLabel: '2',
    energyCost: 45,
    cooldownMs: 6000,
    color: 0x5cc7ff,
    maxSpeed: 4000,
  },
  light: {
    id: 'light',
    owner: 'p1',
    name: '光轨绝杀',
    keyLabel: '3',
    energyCost: 60,
    cooldownMs: 8000,
    color: 0xffd85f,
    maxSpeed: 3600,
  },
  serpent: {
    id: 'serpent',
    owner: 'p2',
    name: '水流弧击',
    keyLabel: 'AI',
    energyCost: 30,
    cooldownMs: 5000,
    color: 0x47ecff,
    maxSpeed: 1120,
  },
  fold: {
    id: 'fold',
    owner: 'p2',
    name: '折轨突袭',
    keyLabel: 'AI',
    energyCost: 45,
    cooldownMs: 7500,
    color: 0xb668ff,
    maxSpeed: 2380,
  },
  double: {
    id: 'double',
    owner: 'p2',
    name: '幻球双生',
    keyLabel: 'AI',
    energyCost: 55,
    cooldownMs: 9500,
    color: 0xff62c8,
    maxSpeed: 980,
  },
}

const SKILL_DESCRIPTIONS: Record<SkillId, { brief: string; traits: string[] }> = {
  qianying: {
    brief: '高速幻影球路，球会带波动和旋转，容易打乱对手接球节奏。',
    traits: ['幻影轨迹', '旋转干扰', '有概率穿透球拍'],
  },
  thunder: {
    brief: '雷电爆发球，速度极高并带多段折线路径，适合突然压制。',
    traits: ['高速突进', '多段折线', '高穿透概率'],
  },
  light: {
    brief: '光轨直线杀球，轨迹稳定但速度极快，强调一击突破。',
    traits: ['直线高速', '低偏移', '很高穿透概率'],
  },
  flame: {
    brief: '终结型炽焰爆扣，消耗最高，出手后压迫感最强。',
    traits: ['终结技', '强压制', '必定穿透'],
  },
  serpent: {
    brief: '水流弧线球，带连续波浪变化，用来扰乱玩家的上下判断。',
    traits: ['弧线变化', '波浪轨迹', '持续干扰'],
  },
  fold: {
    brief: '折轨突袭球，中途突然变向，考验玩家临场反应。',
    traits: ['多段变向', '突袭节奏', '高压进攻'],
  },
  double: {
    brief: '幻球双生，生成干扰假球，让玩家更难识别真实球路。',
    traits: ['假球干扰', '视觉迷惑', '长回合压制'],
  },
}

declare global {
  interface Window {
    __pingPongDebug?: {
      getState: () => object
      setEnergy: (side: Side, value: number) => void
      setCharge: (side: Side, value: number) => void
      queuePlayerSkill: (id: PlayerSkillId) => boolean
      queueAiSkill: (id: AiSkillId) => boolean
      forceNormalHit: (side: Side) => void
      forcePlayerHitSkill: (id: PlayerSkillId) => void
      forceAiHitSkill: (id: AiSkillId) => void
      skipCutin: () => void
      getAiDecision: () => object
      setAiDifficulty: (level: AiDifficulty) => void
      forceGameOver: (winner?: Side, p1Score?: number, p2Score?: number) => void
      reset: () => void
    }
  }
}

export class PingPongScene extends Phaser.Scene {
  private bridge!: GameBridge

  // 球拍
  private p1Paddle!: Phaser.GameObjects.Image
  private p2Paddle!: Phaser.GameObjects.Image

  // 球
  private ball!: Phaser.GameObjects.Arc
  private ballVx = 0
  private ballVy = 0
  private ballSpin = 0
  private ballOwnerColor = BALL_COLOR
  private wallBounceStreak = 0

  // 视觉反馈
  private arenaPoster!: Phaser.GameObjects.Image
  private arenaVideo!: Phaser.GameObjects.Video
  private trailGfx!: Phaser.GameObjects.Graphics
  private spinGfx!: Phaser.GameObjects.Graphics
  private paddleFxGfx!: Phaser.GameObjects.Graphics
  private hudGfx!: Phaser.GameObjects.Graphics
  private skillFxGfx!: Phaser.GameObjects.Graphics
  private pressureGfx!: Phaser.GameObjects.Graphics
  private comboText!: Phaser.GameObjects.Text
  private returnSparkEmitter!: Phaser.GameObjects.Particles.ParticleEmitter
  private returnStreakEmitter!: Phaser.GameObjects.Particles.ParticleEmitter
  private returnGlowEmitter!: Phaser.GameObjects.Particles.ParticleEmitter
  private herNameText!: Phaser.GameObjects.Text
  private playerNameText!: Phaser.GameObjects.Text
  private herEnergyText!: Phaser.GameObjects.Text
  private playerEnergyText!: Phaser.GameObjects.Text
  private herEnergyBarEl: HTMLElement | null = null
  private playerEnergyBarEl: HTMLElement | null = null
  private herEnergyFillEl: HTMLElement | null = null
  private playerEnergyFillEl: HTMLElement | null = null
  private herEnergyValueEl: HTMLElement | null = null
  private playerEnergyValueEl: HTMLElement | null = null
  private skillSlotEls: Partial<Record<PlayerSkillId, HTMLElement>> = {}
  private skillCutinEl: HTMLElement | null = null
  private skillCutinVideoEl: HTMLVideoElement | null = null
  private skillCutinStageEl: HTMLElement | null = null
  private skillCutinKindEl: HTMLElement | null = null
  private skillCutinNameEl: HTMLElement | null = null
  private gameOverOverlayEl: HTMLElement | null = null
  private gameOverPanelEl: HTMLElement | null = null
  private gameOverEvaluationEl: HTMLElement | null = null
  private gameOverComboEl: HTMLElement | null = null
  private gameOverQuoteEl: HTMLElement | null = null
  private gameOverReplayEl: HTMLButtonElement | null = null
  private skillCutinAudioCtx: AudioContext | null = null
  private skillIconImages: Partial<Record<PlayerSkillId, Phaser.GameObjects.Image>> = {}
  private skillKeyTexts: Partial<Record<PlayerSkillId, Phaser.GameObjects.Text>> = {}
  private skillCostTexts: Partial<Record<PlayerSkillId, Phaser.GameObjects.Text>> = {}
  private skillSlotCleanups: Array<() => void> = []
  private skillNoticeText!: Phaser.GameObjects.Text
  private trailPoints: TrailPoint[] = []

  // 分数
  private score = { p1: 0, p2: 0 }
  private p1ScoreText!: Phaser.GameObjects.Text
  private p2ScoreText!: Phaser.GameObjects.Text
  private colonText!: Phaser.GameObjects.Text
  private herScoreEl: HTMLElement | null = null
  private playerScoreEl: HTMLElement | null = null

  // 状态
  private gameActive = false
  private gameOver = false
  private hasStarted = false
  private arenaVideoReady = false
  private arenaReplayTimer: Phaser.Time.TimerEvent | null = null
  private pendingStartAfterArenaReady = false
  private rallyCount = 0
  private maxRallyCount = 0
  private pressureLevel = 0
  private serveSide: 'p1' | 'p2' = 'p1'
  private serveTimer: Phaser.Time.TimerEvent | null = null
  private p1Energy = PLAYER_START_ENERGY
  private p2Energy = AI_START_ENERGY
  private p1PendingSkill: PlayerSkillId | null = null
  private p2PendingSkill: AiSkillId | null = null
  private activeSkill: ActiveSkillState | null = null
  private skillCutinPlaying = false
  private skillCutinSkill: SkillId | null = null
  private skillCutinToken = 0
  private skillCutinTimeoutId: number | null = null
  private skillCutinResolve: (() => void) | null = null
  private fakeBalls: FakeBall[] = []
  private p1CooldownUntil: Record<PlayerSkillId, number> = {
    qianying: 0,
    flame: 0,
    thunder: 0,
    light: 0,
  }
  private p2CooldownUntil: Record<AiSkillId, number> = {
    serpent: 0,
    fold: 0,
    double: 0,
  }
  private p1GlobalCooldownUntil = 0
  private p2GlobalCooldownUntil = 0
  private p1LoseStreak = 0
  private p2LoseStreak = 0
  private p2LastSkillDecisionAt = 0
  private p2SkillHistory: AiSkillId[] = []
  private p2SkillUseCounts: Record<AiSkillId, number> = {
    serpent: 0,
    fold: 0,
    double: 0,
  }

  // 玩家控制
  private p1TargetY = 300
  private p1Velocity = 0
  private p2Velocity = 0
  private p1Charging = false
  private p1Charge = 0
  private p2Charge = 0
  private p1ChargeReadyPlayed = false
  private p1TouchActive = false
  private p1TouchPointerId: number | null = null
  private p1TouchStartX = 0
  private p1TouchStartY = 0
  private p1TouchLastX = 0
  private p1TouchLastY = 0
  private p1NativeTouchPointerIds = new Set<number>()

  // P2 外部控制
  private p2TargetY: number | null = null
  private p2ExternalControl = false
  private aiDifficulty: AiDifficulty = 'normal'
  private aiFallbackTargetY = 300
  private aiLastThinkAt = 0
  private aiReactionReadyAt = 0
  private aiWasBallApproaching = false
  private playerReturnStreak = 0

  // 玩家赢后再来一局自动升级难度
  private lastGameOverWinner: Side | null = null
  private static readonly DIFFICULTY_LADDER: AiDifficulty[] = ['easy', 'normal', 'hard', 'hell']

  // 音效
  private audioContext: AudioContext | null = null

  // 场地尺寸（动态计算）
  private fieldW = 960
  private fieldH = 540

  constructor() {
    super({ key: 'PingPongScene' })
  }

  private readonly handleStartRequest = (): void => {
    this.requestStartMatch()
  }

  private readonly handlePrepareArenaRequest = (): void => {
    this.ensureArenaVideoPlaying()
  }

  private readonly handleDifficultyRequest = (event: Event): void => {
    const detail = (event as CustomEvent<{ level?: string }>).detail
    this.setAiDifficulty(this.normalizeAiDifficulty(detail?.level))
  }

  private readonly handlePointerRelease = (): void => {
    this.p1NativeTouchPointerIds.clear()
    this.p1TouchActive = false
    this.p1TouchPointerId = null
    this.p1Charging = false
  }

  private readonly handlePointerCancel = (): void => {
    this.p1NativeTouchPointerIds.clear()
    this.p1TouchActive = false
    this.p1TouchPointerId = null
    this.p1Charging = false
  }

  private readonly handleNativeMouseMove = (event: MouseEvent): void => {
    this.setP1TargetFromClientPoint(event.clientX, event.clientY)
  }

  private readonly handleNativeTouchPointerDown = (event: PointerEvent): void => {
    if (!this.shouldUseViewportPaddleControl(event)) return

    this.resumeAudio()

    if (this.gameOver || this.skillCutinPlaying) {
      this.p1Charging = false
      return
    }

    this.p1NativeTouchPointerIds.add(event.pointerId)

    if (!this.p1TouchActive) {
      this.p1TouchActive = true
      this.p1TouchPointerId = event.pointerId
      this.p1TouchStartX = event.clientX
      this.p1TouchStartY = event.clientY
      this.p1TouchLastX = event.clientX
      this.p1TouchLastY = event.clientY
      this.setP1TargetFromViewportY(event.clientY)
    }

    this.updateTouchChargeFromPointerCount()
  }

  private readonly handleNativeTouchPointerMove = (event: PointerEvent): void => {
    if (!this.shouldUseViewportPaddleControl(event) && !this.isTrackedNativeTouchPointer(event)) return

    if (!this.isTrackedNativeTouchPointer(event)) return

    this.setP1TargetFromViewportY(event.clientY)
    this.p1TouchLastX = event.clientX
    this.p1TouchLastY = event.clientY
    this.updateTouchChargeFromPointerCount()
  }

  private readonly handleNativeTouchPointerRelease = (event: PointerEvent): void => {
    if (!this.isNativeTouchPointer(event)) {
      this.handlePointerRelease()
      return
    }

    this.p1NativeTouchPointerIds.delete(event.pointerId)
    if (this.p1TouchPointerId === event.pointerId) {
      const nextPointerId = this.p1NativeTouchPointerIds.values().next().value as number | undefined
      this.p1TouchPointerId = nextPointerId ?? null
      this.p1TouchActive = nextPointerId !== undefined
    }
    this.updateTouchChargeFromPointerCount()
  }

  private readonly handleNativeTouchPointerCancel = (event: PointerEvent): void => {
    if (!this.isNativeTouchPointer(event)) {
      this.handlePointerCancel()
      return
    }

    this.p1NativeTouchPointerIds.delete(event.pointerId)
    if (this.p1TouchPointerId === event.pointerId) {
      const nextPointerId = this.p1NativeTouchPointerIds.values().next().value as number | undefined
      this.p1TouchPointerId = nextPointerId ?? null
      this.p1TouchActive = nextPointerId !== undefined
    }
    this.updateTouchChargeFromPointerCount()
  }

  private normalizeAiDifficulty(level: unknown): AiDifficulty {
    if (level === 'easy' || level === 'normal' || level === 'hard' || level === 'hell') {
      return level
    }

    if (level === 'medium' || level === '中等') return 'normal'
    if (level === '容易') return 'easy'
    if (level === '困难') return 'hard'
    if (level === '地狱') return 'hell'
    return 'normal'
  }

  private setAiDifficulty(level: unknown): void {
    const previous = this.aiDifficulty
    this.aiDifficulty = this.normalizeAiDifficulty(level)
    this.aiReactionReadyAt = 0
    this.aiLastThinkAt = 0
    this.aiWasBallApproaching = false
    this.aiFallbackTargetY = this.p2Paddle?.y ?? this.fieldH / 2

    window.dispatchEvent(new CustomEvent('ping-pong:difficulty-changed', {
      detail: {
        level: this.aiDifficulty,
        label: AI_DIFFICULTY_CONFIGS[this.aiDifficulty].label,
      },
    }))

    if (previous !== this.aiDifficulty) {
      console.log(`[PingPong Game] aiDifficulty ${previous} -> ${this.aiDifficulty}`)
    }
  }

  private getAiDifficultyConfig(): AiDifficultyConfig {
    return AI_DIFFICULTY_CONFIGS[this.aiDifficulty]
  }

  preload(): void {
    this.load.video(ARENA_BG_VIDEO_KEY, ARENA_BG_VIDEO_URL, true)
    this.load.image(ARENA_BG_POSTER_KEY, ARENA_BG_POSTER_URL)
    this.load.image(AI_PADDLE_KEY, AI_PADDLE_URL)
    this.load.image(PLAYER_PADDLE_KEY, PLAYER_PADDLE_URL)
    this.load.image(SKILL_ICON_SHEET_KEY, SKILL_ICON_SHEET_URL)
    this.load.image('human_icon', '/assets/human_icon.png')
    this.load.image('ai_icon', '/assets/ai_icon.png')
  }

  create(): void {
    this.bridge = this.registry.get('bridge') as GameBridge
    this.fieldW = this.scale.width
    this.fieldH = this.scale.height
    this.p1TargetY = this.fieldH / 2

    // 背景
    this.cameras.main.setBackgroundColor(BG_COLOR)
    this.createArenaVideoBackground()
    this.drawCenterLine()

    this.trailGfx = this.add.graphics()
    this.trailGfx.setDepth(2)
    this.pressureGfx = this.add.graphics()
    this.pressureGfx.setDepth(3)
    this.paddleFxGfx = this.add.graphics()
    this.paddleFxGfx.setDepth(5)
    this.hudGfx = this.add.graphics()
    this.hudGfx.setDepth(9.5)
    this.skillFxGfx = this.add.graphics()
    this.skillFxGfx.setDepth(6.5)
    this.spinGfx = this.add.graphics()
    this.spinGfx.setDepth(7)
    this.createReturnFxEmitters()

    // 球拍
    this.p2Paddle = this.add.image(
      PADDLE_MARGIN,
      this.fieldH / 2,
      AI_PADDLE_KEY
    )
    this.p2Paddle.setOrigin(0.5)
    this.p2Paddle.setDepth(6)
    this.fitPaddleSprite(this.p2Paddle)

    this.p1Paddle = this.add.image(
      this.fieldW - PADDLE_MARGIN,
      this.fieldH / 2,
      PLAYER_PADDLE_KEY
    )
    this.p1Paddle.setOrigin(0.5)
    this.p1Paddle.setDepth(6)
    this.fitPaddleSprite(this.p1Paddle)

    // 球
    this.ball = this.add.circle(this.fieldW / 2, this.fieldH / 2, BALL_RADIUS, BALL_COLOR)
    this.ball.setDepth(8)

    // 分数 UI
    const scoreStyle = {
      fontSize: '30px',
      fontFamily: 'Impact, "Arial Black", sans-serif',
      fontStyle: 'italic',
      resolution: 2,
      padding: { top: 10, bottom: 10, left: 10, right: 10 },
    }

    this.p2ScoreText = this.add.text(this.fieldW / 2 - 42, 24, '0', {
      ...scoreStyle,
      color: '#82b4ff',
    })
    this.p2ScoreText.setOrigin(0.5)
    this.p2ScoreText.setDepth(10)
    this.p2ScoreText.setShadow(0, 0, '#4a82ff', 8, true, true)
    this.p2ScoreText.setVisible(false)

    // AI 头像 (P2, 左侧, 蓝)
    const aiAvatar = this.add.image(this.fieldW / 2 - 300, 48, 'ai_icon')
    aiAvatar.setDisplaySize(88, 88)
    aiAvatar.setDepth(10)
    aiAvatar.setAlpha(0.95)
    aiAvatar.setVisible(false)

    this.colonText = this.add.text(this.fieldW / 2, 22, ':', {
      fontSize: '32px',
      fontFamily: 'Impact, "Arial Black", sans-serif',
      color: '#b0d0ff',
      resolution: 2,
      padding: { top: 10, bottom: 10, left: 10, right: 10 },
    })
    this.colonText.setOrigin(0.5)
    this.colonText.setDepth(10)
    this.colonText.setShadow(0, 0, '#80abff', 6, true, true)
    this.colonText.setVisible(false)

    this.p1ScoreText = this.add.text(this.fieldW / 2 + 42, 24, '0', {
      ...scoreStyle,
      color: '#ff8282',
    })
    this.p1ScoreText.setOrigin(0.5)
    this.p1ScoreText.setDepth(10)
    this.p1ScoreText.setShadow(0, 0, '#ff4747', 8, true, true)
    this.p1ScoreText.setVisible(false)

    // 玩家头像 (P1, 右侧, 红)
    const humanAvatar = this.add.image(this.fieldW / 2 + 300, 48, 'human_icon')
    humanAvatar.setDisplaySize(88, 88)
    humanAvatar.setDepth(10)
    humanAvatar.setAlpha(0.95)
    humanAvatar.setVisible(false)

    this.comboText = this.add.text(this.fieldW / 2, 95, '', {
      fontSize: '32px',
      fontFamily: 'Impact, "Arial Black", sans-serif',
      fontStyle: 'italic',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 5,
      resolution: 2,
      padding: { top: 15, bottom: 15, left: 15, right: 15 },
    })
    this.comboText.setOrigin(0.5)
    this.comboText.setDepth(10)
    this.comboText.setAlpha(0)

    const helpText = this.add.text(this.fieldW / 2, this.fieldH - 20, '', {
      fontSize: '12px',
      fontFamily: 'system-ui, sans-serif',
      color: '#6f789d',
      resolution: 2,
    })
    helpText.setOrigin(0.5)
    helpText.setDepth(10)
    helpText.setAlpha(0)

    this.addSkillIconFrames()
    this.createSkillHud()

    // 输入：鼠标/触摸控制 P1；触摸滑动只移动球拍，深按才进入蓄力。
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      this.handlePlayerPointerMove(pointer)
    })

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.handlePlayerPointerDown(pointer)
    })

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (!this.isTouchPointer(pointer)) this.handlePointerRelease()
    })
    window.addEventListener('mousemove', this.handleNativeMouseMove, { passive: true })
    window.addEventListener('pointerdown', this.handleNativeTouchPointerDown, { passive: true })
    window.addEventListener('pointermove', this.handleNativeTouchPointerMove, { passive: true })
    window.addEventListener('pointerup', this.handleNativeTouchPointerRelease)
    window.addEventListener('pointercancel', this.handleNativeTouchPointerCancel)
    window.addEventListener('blur', this.handlePointerCancel)
    this.registerSkillInput()

    // 监听宿主命令
    this.bridge.onCommand((cmd) => {
      switch (cmd.type) {
        case 'botPaddleMove':
          this.p2ExternalControl = true
          this.p2TargetY = cmd.y
          break
        case 'botUseSkill':
          this.tryQueueRequestedAiSkill(cmd.skillId)
          break
        case 'setDifficulty':
          this.setAiDifficulty(cmd.level)
          break
        case 'pause':
          this.gameActive = false
          break
        case 'play':
          this.requestStartMatch()
          break
        case 'reset':
          this.resetGame(this.hasStarted)
          break
      }
    })

    window.addEventListener('ping-pong:start', this.handleStartRequest)
    window.addEventListener('ping-pong:prepare-arena', this.handlePrepareArenaRequest)
    window.addEventListener('ping-pong:set-difficulty', this.handleDifficultyRequest)
    this.setAiDifficulty('normal')
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.cancelSkillCutin()
      window.removeEventListener('ping-pong:start', this.handleStartRequest)
      window.removeEventListener('ping-pong:prepare-arena', this.handlePrepareArenaRequest)
      window.removeEventListener('ping-pong:set-difficulty', this.handleDifficultyRequest)
      window.removeEventListener('mousemove', this.handleNativeMouseMove)
      window.removeEventListener('pointerdown', this.handleNativeTouchPointerDown)
      window.removeEventListener('pointermove', this.handleNativeTouchPointerMove)
      window.removeEventListener('pointerup', this.handleNativeTouchPointerRelease)
      window.removeEventListener('pointercancel', this.handleNativeTouchPointerCancel)
      window.removeEventListener('blur', this.handlePointerCancel)
      this.unbindSkillSlotInput()
      this.handlePointerCancel()
    })

    // 通知宿主就绪
    this.bridge.sendReady()
    this.sendSkillCatalog()
    this.exposeDebugApi()

    // 等待开始界面按钮触发后再发球
    this.resetGame(false)
  }

  private fitPaddleSprite(paddle: Phaser.GameObjects.Image): void {
    const aspect = paddle.width > 0 && paddle.height > 0 ? paddle.width / paddle.height : PADDLE_WIDTH / PADDLE_HEIGHT
    paddle.setDisplaySize(PADDLE_VISUAL_HEIGHT * aspect, PADDLE_VISUAL_HEIGHT)
  }

  private handlePlayerPointerMove(pointer: Phaser.Input.Pointer): void {
    this.setP1Target(pointer.y)

    if (!this.isTouchPointer(pointer) || !this.isTrackedTouchPointer(pointer)) return

    this.p1TouchLastX = pointer.x
    this.p1TouchLastY = pointer.y
  }

  private handlePlayerPointerDown(pointer: Phaser.Input.Pointer): void {
    this.resumeAudio()
    this.setP1Target(pointer.y)

    if (this.gameOver || this.skillCutinPlaying) {
      this.p1Charging = false
      return
    }

    if (!this.isTouchPointer(pointer)) {
      this.p1Charging = true
      return
    }

    this.updateTouchChargeFromPointerCount()
  }

  private isTouchPointer(pointer: Phaser.Input.Pointer): boolean {
    if (pointer.wasTouch) return true
    const event = pointer.event as unknown as {
      pointerType?: string
      touches?: unknown
      changedTouches?: unknown
      sourceCapabilities?: { firesTouchEvents?: boolean }
    }
    return (
      event.pointerType === 'touch' ||
      !!event.touches ||
      !!event.changedTouches ||
      event.sourceCapabilities?.firesTouchEvents === true ||
      this.isTouchLikeInputMode()
    )
  }

  private isTouchLikeInputMode(): boolean {
    if (typeof window === 'undefined') return false
    return window.matchMedia?.('(pointer: coarse)').matches === true || navigator.maxTouchPoints > 0
  }

  private isTrackedTouchPointer(pointer: Phaser.Input.Pointer): boolean {
    return this.p1TouchActive && (this.p1TouchPointerId === null || this.p1TouchPointerId === (pointer.pointerId || pointer.id))
  }

  private isNativeTouchPointer(event: PointerEvent): boolean {
    return event.pointerType === 'touch' || event.pointerType === 'pen'
  }

  private isTrackedNativeTouchPointer(event: PointerEvent): boolean {
    return this.p1TouchActive && this.p1TouchPointerId === event.pointerId
  }

  private shouldUseViewportPaddleControl(event: PointerEvent): boolean {
    return this.isNativeTouchPointer(event)
  }

  private startPlayerChargeFromTouch(): void {
    if (!this.hasStarted || this.gameOver || this.skillCutinPlaying) return
    const wasCharging = this.p1Charging
    this.p1Charging = true
    this.p1Charge = Math.max(this.p1Charge, 0.08)

    if (!wasCharging) {
      this.pulsePaddle(this.p1Paddle, 0xf9d65d)
      navigator.vibrate?.(18)
    }
  }

  private updateTouchChargeFromPointerCount(): void {
    if (this.p1NativeTouchPointerIds.size >= 2) {
      this.startPlayerChargeFromTouch()
      return
    }

    this.p1Charging = false
  }

  private setP1TargetFromViewportY(clientY: number): void {
    const canvas = this.game.canvas
    const rect = canvas.getBoundingClientRect()
    if (rect.height <= 0) return

    const gameY = ((clientY - rect.top) / rect.height) * this.fieldH
    this.setP1Target(gameY)
  }

  private setP1TargetFromClientPoint(clientX: number, clientY: number): void {
    const canvas = this.game.canvas
    const rect = canvas.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return
    if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) return

    const gameY = ((clientY - rect.top) / rect.height) * this.fieldH
    this.setP1Target(gameY)
  }

  private addSkillIconFrames(): void {
    const texture = this.textures.get(SKILL_ICON_SHEET_KEY)
    for (const id of PLAYER_SKILL_ORDER) {
      const frameKey = this.getSkillIconFrameKey(id)
      if (!texture.has(frameKey)) {
        texture.add(
          frameKey,
          0,
          SKILL_ICON_FRAME_INDEX[id] * SKILL_ICON_FRAME_SIZE,
          0,
          SKILL_ICON_FRAME_SIZE,
          SKILL_ICON_FRAME_SIZE
        )
      }
    }
  }

  private getSkillIconFrameKey(id: PlayerSkillId): string {
    return `skill-icon-${id}`
  }

  private createSkillHud(): void {
    const nameStyle = {
      fontSize: '16px',
      fontFamily: 'Impact, "Arial Black", system-ui, sans-serif',
      fontStyle: 'italic',
      color: '#ffffff',
      stroke: '#050713',
      strokeThickness: 3,
      resolution: 2,
    }

    const energyStyle = {
      fontSize: '10px',
      fontFamily: 'system-ui, "PingFang SC", sans-serif',
      color: '#dfe9ff',
      resolution: 2,
    }

    this.herNameText = this.add.text(150, 29, 'Her', nameStyle)
    this.herNameText.setOrigin(0, 0.5)
    this.herNameText.setDepth(11)
    this.herNameText.setShadow(0, 0, '#24bfff', 8, true, true)

    this.herEnergyText = this.add.text(150, 64, '', energyStyle)
    this.herEnergyText.setOrigin(0, 0.5)
    this.herEnergyText.setDepth(11)

    this.playerNameText = this.add.text(this.fieldW - 150, 29, 'Player', nameStyle)
    this.playerNameText.setOrigin(1, 0.5)
    this.playerNameText.setDepth(11)
    this.playerNameText.setShadow(0, 0, '#ff596d', 8, true, true)

    this.playerEnergyText = this.add.text(this.fieldW - 150, 64, '', energyStyle)
    this.playerEnergyText.setOrigin(1, 0.5)
    this.playerEnergyText.setDepth(11)

    this.herNameText.setVisible(false)
    this.herEnergyText.setVisible(false)
    this.playerNameText.setVisible(false)
    this.playerEnergyText.setVisible(false)
    this.bindCombatHudDom()

    for (const id of PLAYER_SKILL_ORDER) {
      const icon = this.add.image(0, 0, SKILL_ICON_SHEET_KEY, this.getSkillIconFrameKey(id))
      icon.setOrigin(0.5)
      icon.setDepth(11)
      icon.setVisible(false)
      this.skillIconImages[id] = icon

      const keyText = this.add.text(0, 0, SKILL_CONFIGS[id].keyLabel, {
        fontSize: '15px',
        fontFamily: 'Impact, "Arial Black", sans-serif',
        color: '#ffffff',
        stroke: '#050713',
        strokeThickness: 3,
        resolution: 2,
      })
      keyText.setOrigin(0.5)
      keyText.setDepth(12)
      keyText.setVisible(false)
      this.skillKeyTexts[id] = keyText

      const costText = this.add.text(0, 0, SKILL_CONFIGS[id].energyCost.toString(), {
        fontSize: '10px',
        fontFamily: 'system-ui, "PingFang SC", sans-serif',
        color: '#f8e7a2',
        resolution: 2,
      })
      costText.setOrigin(0.5)
      costText.setDepth(12)
      costText.setVisible(false)
      this.skillCostTexts[id] = costText
    }

    this.skillNoticeText = this.add.text(this.fieldW / 2, 136, '', {
      fontSize: '30px',
      fontFamily: 'Impact, "Arial Black", system-ui, sans-serif',
      fontStyle: 'italic',
      color: '#ffffff',
      stroke: '#090711',
      strokeThickness: 5,
      resolution: 2,
      padding: { top: 10, bottom: 10, left: 12, right: 12 },
    })
    this.skillNoticeText.setOrigin(0.5)
    this.skillNoticeText.setDepth(12)
    this.skillNoticeText.setAlpha(0)
  }

  private registerSkillInput(): void {
    const keyboard = this.input.keyboard
    if (!keyboard) return

    keyboard.on('keydown-ONE', () => this.tryQueuePlayerSkill('qianying'))
    keyboard.on('keydown-TWO', () => this.tryQueuePlayerSkill('thunder'))
    keyboard.on('keydown-THREE', () => this.tryQueuePlayerSkill('light'))
    keyboard.on('keydown-FOUR', () => this.tryQueuePlayerSkill('flame'))
    keyboard.on('keydown-ESC', () => this.cancelPendingSkill('p1'))
  }

  private exposeDebugApi(): void {
    window.__pingPongDebug = {
      getState: () => ({
        score: { ...this.score },
        p1Energy: this.p1Energy,
        p2Energy: this.p2Energy,
        p1PendingSkill: this.p1PendingSkill,
        p2PendingSkill: this.p2PendingSkill,
        rallyCount: this.rallyCount,
        maxRallyCount: this.maxRallyCount,
        pressureLevel: this.pressureLevel,
        activeSkill: this.activeSkill?.id ?? null,
        activeSkillPiercesPaddle: this.activeSkill?.piercesPaddle ?? null,
        activeSkillElapsed: this.activeSkill?.elapsed ?? null,
        activeSkillBreakIndex: this.activeSkill?.breakIndex ?? null,
        skillCutinPlaying: this.skillCutinPlaying,
        skillCutinSkill: this.skillCutinSkill,
        aiSkillHistory: [...this.p2SkillHistory],
        aiSkillUseCounts: { ...this.p2SkillUseCounts },
        aiDifficulty: this.aiDifficulty,
        aiDifficultyLabel: AI_DIFFICULTY_CONFIGS[this.aiDifficulty].label,
        fakeBallCount: this.fakeBalls.length,
        p1Charge: this.p1Charge,
        p2Charge: this.p2Charge,
        ball: {
          x: this.ball.x,
          y: this.ball.y,
          vx: this.ballVx,
          vy: this.ballVy,
          spin: this.ballSpin,
          ownerColor: this.ballOwnerColor,
          visualColor: this.getBallVisualColor(),
          trailColor: this.getTrailVisualColor(),
        },
      }),
      setEnergy: (side, value) => {
        if (side === 'p1') this.p1Energy = Phaser.Math.Clamp(value, 0, ENERGY_MAX)
        else this.p2Energy = Phaser.Math.Clamp(value, 0, ENERGY_MAX)
        this.updateSkillHud()
      },
      setCharge: (side, value) => {
        if (side === 'p1') this.p1Charge = Phaser.Math.Clamp(value, 0, 1)
        else this.p2Charge = Phaser.Math.Clamp(value, 0, 1)
      },
      queuePlayerSkill: (id) => this.tryQueuePlayerSkill(id),
      queueAiSkill: (id) => this.tryQueueAiSkill(id),
      forceNormalHit: (side) => this.debugForceNormalHit(side),
      forcePlayerHitSkill: (id) => this.debugForceSkillHit('p1', id),
      forceAiHitSkill: (id) => this.debugForceSkillHit('p2', id),
      skipCutin: () => this.finishSkillCutin(),
      getAiDecision: () => this.debugGetAiDecision(),
      setAiDifficulty: (level) => this.setAiDifficulty(level),
      forceGameOver: (winner = 'p1', p1Score, p2Score) => this.debugForceGameOver(winner, p1Score, p2Score),
      reset: () => this.resetGame(),
    }

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      if (window.__pingPongDebug?.getState) {
        delete window.__pingPongDebug
      }
    })
  }

  private debugForceGameOver(winner: Side, p1Score?: number, p2Score?: number): void {
    this.cancelSkillCutin()
    this.cancelServeTimer()
    this.score = {
      p1: p1Score ?? (winner === 'p1' ? WINNING_SCORE : Phaser.Math.Between(0, WINNING_SCORE - 1)),
      p2: p2Score ?? (winner === 'p2' ? WINNING_SCORE : Phaser.Math.Between(0, WINNING_SCORE - 1)),
    }
    this.gameOver = true
    this.gameActive = false
    this.rallyCount = 0
    this.updateScoreText()
    this.updateSkillHud()
    this.showGameOver(this.getGameOverResult(winner))
  }

  update(_time: number, delta: number): void {
    const dt = Math.max(Math.min(delta / 1000, 0.033), 0.001)

    if (this.skillCutinPlaying) {
      this.updateSkillHud()
      this.drawPaddleEffects()
      this.drawSpinRing()
      this.drawBallTrail(false)
      this.drawSkillEffects()
      return
    }

    this.updateCharge(dt)
    this.maybeQueueAiSkill()
    this.updateSkillHud()

    const prevP1Y = this.p1Paddle.y
    const prevP2Y = this.p2Paddle.y

    this.updateP1Paddle(dt)
    this.updateP2Paddle(dt)

    this.p1Velocity = (this.p1Paddle.y - prevP1Y) / dt
    this.p2Velocity = (this.p2Paddle.y - prevP2Y) / dt

    this.drawPaddleEffects()
    this.drawSpinRing()
    this.drawBallTrail(false)
    this.drawSkillEffects()

    if (!this.gameActive || this.gameOver) return

    this.applySpin(dt)
    this.updateActiveSkill(dt)

    const prevBallX = this.ball.x
    const prevBallY = this.ball.y

    // 球运动
    this.ball.x += this.ballVx * dt
    this.ball.y += this.ballVy * dt
    this.updateFakeBalls(dt)

    // 上下墙壁反弹
    if (this.ball.y - BALL_RADIUS <= 0) {
      this.ball.y = BALL_RADIUS
      this.ballVy = Math.abs(this.ballVy)
      this.onWallBounce('top')
    } else if (this.ball.y + BALL_RADIUS >= this.fieldH) {
      this.ball.y = this.fieldH - BALL_RADIUS
      this.ballVy = -Math.abs(this.ballVy)
      this.onWallBounce('bottom')
    }

    this.checkPaddleCollision(prevBallX, prevBallY)
    this.checkScoring()

    this.captureTrailPoint()
    this.drawBallTrail(true)
    this.drawSkillEffects()
    this.drawSpinRing()
    this.updateBallAppearance()

    // 报告球位置（节流在 Bridge 内部）
    this.bridge.sendBallPosition({
      x: this.ball.x / this.fieldW,
      y: this.ball.y / this.fieldH,
      vx: this.ballVx / this.fieldW,
      vy: this.ballVy / this.fieldH,
      spin: this.ballSpin / SPIN_MAX,
    })
  }

  // ===== 输入与球拍控制 =====

  private setP1Target(y: number): void {
    this.p1TargetY = Phaser.Math.Clamp(y, PADDLE_HEIGHT / 2, this.fieldH - PADDLE_HEIGHT / 2)
  }

  private updateP1Paddle(dt: number): void {
    const chargeDragT = Math.pow(this.p1Charge, 1.15)
    const chargeDrag = Phaser.Math.Linear(PLAYER_PADDLE_SPEED, PLAYER_CHARGED_SPEED, chargeDragT)
    const diff = this.p1TargetY - this.p1Paddle.y
    const maxMove = chargeDrag * dt
    this.p1Paddle.y += Math.sign(diff) * Math.min(Math.abs(diff), maxMove)
    this.p1Paddle.y = Phaser.Math.Clamp(
      this.p1Paddle.y,
      PADDLE_HEIGHT / 2,
      this.fieldH - PADDLE_HEIGHT / 2
    )
  }

  private updateP2Paddle(dt: number): void {
    let targetY: number

    if (this.p2ExternalControl && this.p2TargetY !== null) {
      targetY = this.p2TargetY * this.fieldH
    } else {
      targetY = this.fallbackAI()
      this.updateBotCharge(dt, targetY)
    }

    const baseSpeed = this.getAiDifficultyConfig().paddleSpeed
    const speed = Phaser.Math.Linear(baseSpeed, baseSpeed * 0.58, this.p2Charge)
    const maxMove = speed * dt
    const diff = targetY - this.p2Paddle.y
    this.p2Paddle.y += Math.sign(diff) * Math.min(Math.abs(diff), maxMove)
    this.p2Paddle.y = Phaser.Math.Clamp(
      this.p2Paddle.y,
      PADDLE_HEIGHT / 2,
      this.fieldH - PADDLE_HEIGHT / 2
    )
  }

  /** 预判 AI：球朝 P2 飞来时预测落点，并偶尔故意打偏制造旋转。 */
  private fallbackAI(): number {
    const config = this.getAiDifficultyConfig()
    if (this.ballVx < 0) {
      const now = this.time.now
      if (!this.aiWasBallApproaching) {
        this.aiWasBallApproaching = true
        this.aiReactionReadyAt = now + config.reactionDelayMs
        this.aiLastThinkAt = 0
        this.aiFallbackTargetY = this.p2Paddle.y
      }

      if (now < this.aiReactionReadyAt) {
        return this.aiFallbackTargetY
      }

      if (this.aiLastThinkAt > 0 && now - this.aiLastThinkAt < config.targetUpdateIntervalMs) {
        return this.aiFallbackTargetY
      }

      const targetX = this.p2Paddle.x + PADDLE_WIDTH / 2 + BALL_RADIUS
      const thunderY = this.predictThunderRouteYAtX(targetX)
      const predictedY = thunderY ?? this.predictBallYAtX(targetX, config.maxPredictionTime)
      const shortReadY = this.predictBallYAfterTime(Math.min(0.16, config.maxPredictionTime))
      let targetY = Phaser.Math.Linear(shortReadY, predictedY, config.predictionConfidence)
      targetY = Phaser.Math.Linear(targetY, this.fieldH / 2, config.centerBias)

      const pressureAccuracy = Phaser.Math.Clamp(this.pressureLevel / 3, 0, 1)
      const errorPx = Phaser.Math.Linear(config.predictionErrorPx, config.predictionErrorPx * 0.62, pressureAccuracy)
      targetY += Phaser.Math.FloatBetween(-errorPx, errorPx)

      if (Math.random() < config.mistakeChance) {
        targetY += Phaser.Math.RND.sign() * Phaser.Math.FloatBetween(errorPx * 0.8, config.mistakeErrorPx)
      }

      const attackOffset = this.rallyCount >= 2
        ? Math.sin(this.rallyCount * 1.7) * (16 + this.pressureLevel * 7) * config.attackOffsetScale
        : 0
      targetY += attackOffset + Math.sin(now * 0.006) * errorPx * 0.35
      this.aiFallbackTargetY = Phaser.Math.Clamp(targetY, PADDLE_HEIGHT / 2, this.fieldH - PADDLE_HEIGHT / 2)
      this.aiLastThinkAt = now
      return this.aiFallbackTargetY
    }

    this.aiWasBallApproaching = false
    this.aiLastThinkAt = 0
    return this.fieldH / 2 + Math.sin(this.time.now * 0.0018) * 26
  }

  private predictThunderRouteYAtX(targetX: number): number | null {
    const skill = this.activeSkill
    const route = skill?.id === 'thunder' ? skill.thunderRoute : undefined
    if (!route || route.length < 2) return null

    for (let i = 1; i < route.length; i++) {
      const a = route[i - 1]
      const b = route[i]
      const minX = Math.min(a.x, b.x)
      const maxX = Math.max(a.x, b.x)
      if (targetX < minX || targetX > maxX) continue

      const dx = b.x - a.x
      const t = Math.abs(dx) < 0.0001 ? 0 : Phaser.Math.Clamp((targetX - a.x) / dx, 0, 1)
      return Phaser.Math.Linear(a.y, b.y, t)
    }

    return route[route.length - 1].y
  }

  private updateCharge(dt: number): void {
    const oldP1Charge = this.p1Charge
    if (this.p1Charging && this.hasStarted && !this.gameOver) {
      this.p1Charge = Phaser.Math.Clamp(this.p1Charge + CHARGE_RATE * dt, 0, 1)
    } else {
      this.p1Charge = Phaser.Math.Clamp(this.p1Charge - CHARGE_DECAY * dt, 0, 1)
      if (this.p1Charge <= 0.01) {
        this.p1ChargeReadyPlayed = false
      }
    }

    if (oldP1Charge < 0.98 && this.p1Charge >= 0.98 && !this.p1ChargeReadyPlayed) {
      this.p1ChargeReadyPlayed = true
      this.playChargeReadySound()
      this.pulsePaddle(this.p1Paddle, 0xf9d65d)
    }
  }

  private updateBotCharge(dt: number, targetY: number): void {
    const ballApproaching = this.gameActive && this.ballVx < 0 && this.ball.x < this.fieldW * 0.62
    const counterThreat = this.isFullChargeCounterSkillIncoming()
    const linedUp = Math.abs(targetY - this.p2Paddle.y) < 42
    const wantsAttack = this.rallyCount >= 3 && Math.sin(this.rallyCount * 2.1) > -0.25

    if (counterThreat && this.activeSkill?.counterReadyAtLaunch) {
      this.p2Charge = 1
    } else if (counterThreat) {
      this.p2Charge = Math.min(this.p2Charge, FULL_CHARGE_COUNTER_THRESHOLD - 0.04)
    } else if (ballApproaching && linedUp && wantsAttack) {
      this.p2Charge = Phaser.Math.Clamp(this.p2Charge + CHARGE_RATE * 0.58 * dt, 0, 0.82)
    } else {
      this.p2Charge = Phaser.Math.Clamp(this.p2Charge - CHARGE_DECAY * 0.9 * dt, 0, 1)
    }
  }

  private isFullChargeCounterSkillIncoming(): boolean {
    const skill = this.activeSkill
    return !!skill && skill.owner === 'p1' && (skill.id === 'thunder' || skill.id === 'light') && this.ballVx < 0
  }

  // ===== 技能系统 =====

  private tryQueuePlayerSkill(id: PlayerSkillId): boolean {
    this.resumeAudio()

    if (this.gameOver || !this.hasStarted) {
      this.showSkillNotice('比赛开始后可选技能', 0x8fa0c7)
      return false
    }

    if (this.skillCutinPlaying) return false

    if (!this.canQueueSkill('p1', id, true)) return false

    this.p1PendingSkill = this.p1PendingSkill === id ? null : id
    if (this.p1PendingSkill) {
      this.showSkillNotice(`下一拍 ${SKILL_CONFIGS[id].name}`, SKILL_CONFIGS[id].color)
      this.pulsePaddle(this.p1Paddle, SKILL_CONFIGS[id].color)
      this.playSkillCueSound(id)
    } else {
      this.showSkillNotice('取消技能', 0x8fa0c7)
    }
    this.updateSkillHud()
    this.sendSkillSelection('p1')
    return true
  }

  private tryQueueAiSkill(id: AiSkillId): boolean {
    if (this.skillCutinPlaying) return false
    if (!this.canQueueSkill('p2', id, false)) return false
    this.p2PendingSkill = id
    this.pulsePaddle(this.p2Paddle, SKILL_CONFIGS[id].color)
    this.sendSkillSelection('p2')
    return true
  }

  private tryQueueRequestedAiSkill(skillId: string): boolean {
    if (!this.isAiSkillId(skillId)) {
      this.sendCombatState('skill_selected')
      return false
    }

    const queued = this.tryQueueAiSkill(skillId)
    if (!queued) this.sendCombatState('skill_selected')
    return queued
  }

  private isAiSkillId(skillId: string): skillId is AiSkillId {
    return AI_SKILL_ORDER.includes(skillId as AiSkillId)
  }

  private canQueueSkill(side: Side, id: SkillId, notify: boolean): boolean {
    const config = SKILL_CONFIGS[id]
    if (config.owner !== side) return false

    const energy = this.getEnergy(side)
    const cooldownRemaining = this.getSkillCooldownRemainingMs(side, id)
    const globalRemaining = Math.max(0, this.getGlobalCooldownUntil(side) - this.time.now)

    if (energy < config.energyCost) {
      if (notify) this.showSkillNotice(`能量不足 ${Math.floor(energy)}/${config.energyCost}`, 0x8fa0c7)
      return false
    }

    if (cooldownRemaining > 0) {
      if (notify) this.showSkillNotice(`冷却中 ${Math.ceil(cooldownRemaining / 1000)}s`, 0x8fa0c7)
      return false
    }

    if (globalRemaining > 0) {
      if (notify) this.showSkillNotice(`技能间隔 ${Math.ceil(globalRemaining / 1000)}s`, 0x8fa0c7)
      return false
    }

    return true
  }

  private maybeQueueAiSkill(): void {
    if (!this.gameActive || this.gameOver || this.p2PendingSkill) return
    if (this.ballVx >= 0 || this.ball.x > this.fieldW * 0.64) return
    const difficultyConfig = this.getAiDifficultyConfig()
    if (this.time.now - this.p2LastSkillDecisionAt < difficultyConfig.skillThinkIntervalMs) return

    this.p2LastSkillDecisionAt = this.time.now

    const predictedY = this.predictBallYAtX(this.p2Paddle.x + PADDLE_WIDTH / 2 + BALL_RADIUS)
    const reachDelta = Math.abs(predictedY - this.p2Paddle.y)
    if (reachDelta > 118) return

    const decision = this.chooseAiSkillDecision(predictedY, reachDelta)
    if (!decision || Math.random() > decision.castChance) return

    this.tryQueueAiSkill(decision.id)
  }

  private chooseAiSkillDecision(predictedY: number, reachDelta: number): AiSkillDecision | null {
    const scoreGap = this.score.p1 - this.score.p2
    const energy = this.p2Energy
    const playerCenterPressure = Phaser.Math.Clamp(
      Math.abs(this.p1Paddle.y - this.fieldH / 2) / (this.fieldH / 2),
      0,
      1,
    )
    const playerMotion = Phaser.Math.Clamp(Math.abs(this.p1Velocity) / PLAYER_PADDLE_SPEED, 0, 1)
    const reachPressure = Phaser.Math.Clamp(reachDelta / 118, 0, 1)
    const longRally = Phaser.Math.Clamp((this.rallyCount - 2) / 8, 0, 1)
    const playerThreat = Phaser.Math.Clamp(this.p1Energy / ENERGY_MAX, 0, 1)

    const scores = AI_SKILL_ORDER
      .filter((id) => this.canQueueSkill('p2', id, false))
      .filter((id) => this.isAiSkillTacticallyUnlocked(id, scoreGap, energy, playerThreat))
      .map((id) => ({
        id,
        score: this.scoreAiSkillOption(id, {
          scoreGap,
          energy,
          playerCenterPressure,
          playerMotion,
          reachPressure,
          longRally,
          playerThreat,
          predictedY,
        }),
      }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)

    if (scores.length === 0) return null

    const best = scores[0]
    const minScore = best.id === 'serpent' ? 0.58 : 0.48
    if (best.score < minScore) return null

    const comebackUrgency = Phaser.Math.Clamp(scoreGap / 3, 0, 1)
    let castChance = Phaser.Math.Clamp(
      0.18 +
        this.pressureLevel * 0.1 +
        longRally * 0.14 +
        comebackUrgency * 0.13 +
        playerThreat * 0.08 +
        best.score * 0.16,
      0.18,
      0.84,
    )

    if (best.id === 'serpent') {
      castChance = Phaser.Math.Clamp(castChance - 0.12 + (energy < SKILL_CONFIGS.fold.energyCost ? 0.05 : 0), 0.14, 0.58)
    } else if (best.id === 'double') {
      castChance = Phaser.Math.Clamp(castChance + 0.08 + this.p2LoseStreak * 0.04, 0.28, 0.9)
    }

    castChance = Phaser.Math.Clamp(castChance * this.getAiDifficultyConfig().skillChanceMultiplier, 0.08, 0.92)
    return { id: best.id, score: best.score, castChance }
  }

  private isAiSkillTacticallyUnlocked(id: AiSkillId, scoreGap: number, energy: number, playerThreat: number): boolean {
    switch (id) {
      case 'serpent':
        return this.rallyCount >= 2 || scoreGap >= 1 || energy >= 82
      case 'fold':
        return this.rallyCount >= 3 || this.pressureLevel >= 1 || scoreGap >= 1 || energy >= 70
      case 'double':
        return (
          this.rallyCount >= 4 ||
          this.pressureLevel >= 2 ||
          scoreGap >= 2 ||
          this.p2LoseStreak >= 1 ||
          playerThreat >= 0.68
        )
    }
  }

  private scoreAiSkillOption(
    id: AiSkillId,
    context: {
      scoreGap: number
      energy: number
      playerCenterPressure: number
      playerMotion: number
      reachPressure: number
      longRally: number
      playerThreat: number
      predictedY: number
    },
  ): number {
    const { scoreGap, energy, playerCenterPressure, playerMotion, reachPressure, longRally, playerThreat, predictedY } = context
    const aiBehind = Math.max(0, scoreGap)
    const aiAhead = Math.max(0, -scoreGap)
    const edgeShotPotential = Phaser.Math.Clamp(Math.abs(predictedY - this.fieldH / 2) / (this.fieldH / 2), 0, 1)
    const canSaveForFoldSoon =
      energy >= SKILL_CONFIGS.serpent.energyCost + 6 &&
      energy < SKILL_CONFIGS.fold.energyCost &&
      this.rallyCount >= 3 &&
      this.getSkillCooldownRemainingMs('p2', 'fold') < 1400
    const canSpendBig =
      (energy >= SKILL_CONFIGS.fold.energyCost && this.getSkillCooldownRemainingMs('p2', 'fold') <= 0) ||
      (energy >= SKILL_CONFIGS.double.energyCost && this.getSkillCooldownRemainingMs('p2', 'double') <= 0)

    let score = 0

    switch (id) {
      case 'serpent':
        score =
          0.44 +
          (this.rallyCount >= 2 ? 0.16 : -0.24) +
          (energy < SKILL_CONFIGS.fold.energyCost ? 0.12 : -0.1) +
          (aiAhead > 0 ? 0.08 : 0) +
          edgeShotPotential * 0.12 -
          this.pressureLevel * 0.05 -
          aiBehind * 0.05

        if (canSpendBig && (this.rallyCount >= 4 || aiBehind >= 1 || playerThreat >= 0.58)) score -= 0.36
        if (canSaveForFoldSoon && aiBehind < 2) score -= 0.3
        break

      case 'fold':
        score =
          0.46 +
          (this.rallyCount >= 3 ? 0.22 : -0.14) +
          this.pressureLevel * 0.08 +
          playerCenterPressure * 0.2 +
          playerMotion * 0.12 +
          reachPressure * 0.08 +
          longRally * 0.14 +
          aiBehind * 0.08

        if (energy >= 68) score += 0.08
        if (this.rallyCount < 3 && aiBehind === 0) score -= 0.16
        break

      case 'double':
        score =
          0.4 +
          (this.rallyCount >= 4 ? 0.22 : -0.18) +
          this.pressureLevel * 0.1 +
          longRally * 0.16 +
          aiBehind * 0.16 +
          this.p2LoseStreak * 0.14 +
          playerThreat * 0.14 +
          reachPressure * 0.05

        if (energy >= 78) score += 0.1
        if (aiAhead >= 2 && this.rallyCount < 6) score -= 0.16
        break
    }

    score += this.getAiSkillHistoryScore(id)
    score += Phaser.Math.FloatBetween(-0.045, 0.045)
    return score
  }

  private getAiSkillHistoryScore(id: AiSkillId): number {
    const history = this.p2SkillHistory
    const last = history.length > 0 ? history[history.length - 1] : null
    const recent = history.slice(-3)
    const recentSameCount = recent.filter((skill) => skill === id).length
    const totalUses = AI_SKILL_ORDER.reduce((sum, skill) => sum + this.p2SkillUseCounts[skill], 0)
    const usageShare = totalUses > 0 ? this.p2SkillUseCounts[id] / totalUses : 0
    let score = 0

    if (last === id) score -= id === 'serpent' ? 0.5 : 0.28
    score -= recentSameCount * (id === 'serpent' ? 0.16 : 0.1)
    score -= usageShare * (id === 'serpent' ? 0.26 : 0.15)

    if (id !== 'serpent' && recent.length >= 2 && recent[recent.length - 1] === 'serpent' && recent[recent.length - 2] === 'serpent') {
      score += 0.2
    }

    return score
  }

  private debugGetAiDecision(): object {
    const predictedY = this.predictBallYAtX(this.p2Paddle.x + PADDLE_WIDTH / 2 + BALL_RADIUS)
    const reachDelta = Math.abs(predictedY - this.p2Paddle.y)
    const scoreGap = this.score.p1 - this.score.p2
    const energy = this.p2Energy
    const playerThreat = Phaser.Math.Clamp(this.p1Energy / ENERGY_MAX, 0, 1)
    const context = {
      scoreGap,
      energy,
      playerCenterPressure: Phaser.Math.Clamp(Math.abs(this.p1Paddle.y - this.fieldH / 2) / (this.fieldH / 2), 0, 1),
      playerMotion: Phaser.Math.Clamp(Math.abs(this.p1Velocity) / PLAYER_PADDLE_SPEED, 0, 1),
      reachPressure: Phaser.Math.Clamp(reachDelta / 118, 0, 1),
      longRally: Phaser.Math.Clamp((this.rallyCount - 2) / 8, 0, 1),
      playerThreat,
      predictedY,
    }

    return {
      predictedY,
      reachDelta,
      rallyCount: this.rallyCount,
      pressureLevel: this.pressureLevel,
      scoreGap,
      energy,
      history: [...this.p2SkillHistory],
      scores: AI_SKILL_ORDER.map((id) => ({
        id,
        canQueue: this.canQueueSkill('p2', id, false),
        tacticallyUnlocked: this.isAiSkillTacticallyUnlocked(id, scoreGap, energy, playerThreat),
        score: this.scoreAiSkillOption(id, context),
      })),
      decision: this.chooseAiSkillDecision(predictedY, reachDelta),
    }
  }

  private getSkillCatalogItem(id: SkillId): SkillCatalogItem {
    const config = SKILL_CONFIGS[id]
    const description = SKILL_DESCRIPTIONS[id]
    return {
      id,
      name: config.name,
      owner: config.owner,
      keyLabel: config.keyLabel,
      energyCost: config.energyCost,
      cooldownMs: config.cooldownMs,
      maxSpeed: config.maxSpeed,
      brief: description.brief,
      traits: description.traits,
    }
  }

  private sendSkillCatalog(): void {
    this.bridge.sendSkillCatalog({
      playerSkills: PLAYER_SKILL_ORDER.map((id) => this.getSkillCatalogItem(id)),
      botSkills: AI_SKILL_ORDER.map((id) => this.getSkillCatalogItem(id)),
      energyRules: [
        '双方能量上限100。',
        '成功接普通球：中心击球+2，切削区击球+3，边缘击球+3。',
        '成功接到对方技能球时，本次接球获得6点能量。',
        '每次得分后，得分方获得4点能量，失分方获得2点能量。',
        '连续失分会给失分方追加补偿：第2次+2，第3次+3，第4次及以上+4。',
        '玩家先选择技能，下一次成功击球时才会真正释放并扣除能量。',
        '每方每个回合最多释放一次技能。',
      ],
    })
  }

  private getCombatState(reason: CombatStateData['reason']): CombatStateData {
    return {
      reason,
      p1Energy: Math.floor(this.p1Energy),
      p2Energy: Math.floor(this.p2Energy),
      p1PendingSkill: this.p1PendingSkill,
      p2PendingSkill: this.p2PendingSkill,
      activeSkill: this.activeSkill?.id ?? this.skillCutinSkill,
      activeSkillOwner: this.activeSkill?.owner ?? (this.skillCutinSkill ? SKILL_CONFIGS[this.skillCutinSkill].owner : null),
      p1: this.score.p1,
      p2: this.score.p2,
      rallyCount: this.rallyCount,
      pressureLevel: this.pressureLevel,
      p1ReadySkills: PLAYER_SKILL_ORDER.filter((id) => this.canQueueSkill('p1', id, false)),
      p2ReadySkills: AI_SKILL_ORDER.filter((id) => this.canQueueSkill('p2', id, false)),
    }
  }

  private sendCombatState(reason: CombatStateData['reason']): void {
    this.bridge.sendCombatState(this.getCombatState(reason))
  }

  private sendSkillSelection(side: Side): void {
    const skillId = side === 'p1' ? this.p1PendingSkill : this.p2PendingSkill
    this.bridge.sendSkillSelected({
      side,
      skillId,
      skillName: skillId ? SKILL_CONFIGS[skillId].name : null,
      p1Energy: Math.floor(this.p1Energy),
      p2Energy: Math.floor(this.p2Energy),
    })
    this.sendCombatState('skill_selected')
  }

  private sendSkillUsed(side: Side, id: SkillId, zone: HitZone, charge: number): void {
    const config = SKILL_CONFIGS[id]
    this.bridge.sendSkillUsed({
      side,
      skillId: id,
      skillName: config.name,
      energyCost: config.energyCost,
      p1Energy: Math.floor(this.p1Energy),
      p2Energy: Math.floor(this.p2Energy),
      hitZone: zone,
      charge,
      p1: this.score.p1,
      p2: this.score.p2,
      rallyCount: this.rallyCount,
    })
    this.sendCombatState('skill_used')
  }

  private resolvePendingSkillOnHit(side: Side, zone: HitZone, charge: number): SkillId | null {
    const id = side === 'p1' ? this.p1PendingSkill : this.p2PendingSkill
    if (!id) return null

    if (!this.canQueueSkill(side, id, false)) {
      this.cancelPendingSkill(side)
      return null
    }

    this.consumeSkillResource(side, id, side !== 'p1')
    this.cancelPendingSkill(side)
    if (side === 'p1') {
      this.playPlayerSkillCutin(id as PlayerSkillId, zone, charge)
    } else {
      this.playAiSkillCutin(id as AiSkillId, zone, charge)
    }
    return id
  }

  private playPlayerSkillCutin(id: PlayerSkillId, zone: HitZone, charge: number): void {
    this.playSkillCutinForSide(id, 'p1', zone, charge)
  }

  private playAiSkillCutin(id: AiSkillId, zone: HitZone, charge: number): void {
    this.playSkillCutinForSide(id, 'p2', zone, charge)
  }

  private playSkillCutinForSide(id: SkillId, side: Side, zone: HitZone, charge: number): void {
    const token = ++this.skillCutinToken
    const resumeVx = this.ballVx
    const resumeVy = this.ballVy
    const resumeSpin = this.ballSpin

    this.skillCutinPlaying = true
    this.skillCutinSkill = id
    this.gameActive = false
    if (side === 'p1') this.p1Charging = false
    else this.p2Charge = 0
    this.ballVx = 0
    this.ballVy = 0
    this.ballSpin = 0
    this.updateSkillHud()
    this.cameras.main.shake(90, 0.0024)
    if (side === 'p2') this.recordAiSkillUse(id as AiSkillId)
    window.dispatchEvent(new CustomEvent('ping-pong:skill-cutin-start', { detail: { id, side } }))

    void this.showSkillCutin(id, token, side).then(() => {
      if (token !== this.skillCutinToken || this.gameOver) return

      this.skillCutinPlaying = false
      this.skillCutinSkill = null
      window.dispatchEvent(new CustomEvent('ping-pong:skill-cutin-end', { detail: { id, side } }))
      this.ballVx = resumeVx
      this.ballVy = resumeVy
      this.ballSpin = resumeSpin
      this.gameActive = true
      if (side === 'p1') this.startSkillCooldown('p1', id as PlayerSkillId)
      this.activateSkill(id, side, zone, charge)
    })
  }

  private showSkillCutin(id: SkillId, token: number, side: Side): Promise<void> {
    const root = this.skillCutinEl
    const video = this.skillCutinVideoEl
    if (!root || !video) {
      return new Promise((resolve) => {
        this.time.delayedCall(320, resolve)
      })
    }

    root.dataset.skill = id
    root.dataset.side = side === 'p1' ? 'player' : 'ai'
    const config = SKILL_CONFIGS[id]
    const skillKind = side === 'p1' && config.keyLabel === '4' ? '终结技' : '必杀技'
    root.style.setProperty('--cutin-color', this.colorToCss(config.color))
    if (this.skillCutinKindEl) {
      this.skillCutinKindEl.textContent = skillKind
      this.skillCutinKindEl.dataset.text = skillKind
    }
    if (this.skillCutinNameEl) {
      this.skillCutinNameEl.textContent = config.name
      this.skillCutinNameEl.dataset.text = config.name
    }

    this.clearSkillCutinTimer()
    this.skillCutinResolve = null
    video.pause()
    video.muted = false
    video.loop = false
    video.volume = SKILL_CUTIN_VIDEO_VOLUME

    // 优先用 main.ts 在预加载阶段缓存的 Blob URL，避免每次释放技能时重新走网络/解码
    const fallbackUrl = side === 'p1'
      ? PLAYER_SKILL_CUTIN_URLS[id as PlayerSkillId]
      : AI_SKILL_CUTIN_URLS[id as AiSkillId]
    const cachedUrl = window.__pingPongPreload?.getSkillCutinUrl?.(id) ?? null
    const targetSrc = cachedUrl ?? fallbackUrl

    if (video.src !== targetSrc) {
      video.src = targetSrc
    }
    this.syncSkillCutinVideoAspect(video)
    video.addEventListener('loadedmetadata', () => this.syncSkillCutinVideoAspect(video), { once: true })
    try {
      video.currentTime = 0
    } catch {
      // 个别浏览器在首次设置 src 后立即 seek 会抛错，忽略即可
    }
    root.classList.remove('is-finishing')
    root.classList.add('is-active')
    this.playSkillCutinStinger(id)

    return new Promise((resolve) => {
      let done = false
      const finish = (): void => {
        if (done) return
        done = true
        this.skillCutinResolve = null
        video.removeEventListener('ended', finish)
        video.removeEventListener('error', finish)
        this.clearSkillCutinTimer()
        root.classList.add('is-finishing')
        window.setTimeout(() => {
          if (token !== this.skillCutinToken) return
          root.classList.remove('is-active', 'is-finishing')
          video.pause()
          // 不再清空 src，下次播放时浏览器可以直接复用解码缓存，避免黑屏
          try {
            video.currentTime = 0
          } catch {
            // ignore
          }
          resolve()
        }, 180)
      }

      this.skillCutinResolve = finish
      video.addEventListener('ended', finish)
      video.addEventListener('error', finish)
      this.skillCutinTimeoutId = window.setTimeout(finish, PLAYER_SKILL_CUTIN_DURATION_MS)
      video.play().catch(() => {
        video.muted = true
        video.play().catch(() => {
          this.clearSkillCutinTimer()
          this.skillCutinTimeoutId = window.setTimeout(finish, 520)
        })
      })
    })
  }

  private syncSkillCutinVideoAspect(video: HTMLVideoElement): void {
    if (!this.skillCutinStageEl || video.videoWidth <= 0 || video.videoHeight <= 0) return
    const aspect = Phaser.Math.Clamp(video.videoWidth / video.videoHeight, 0.45, 2.2)
    this.skillCutinStageEl.style.setProperty('--cutin-video-aspect', aspect.toFixed(5))
  }

  private playSkillCutinStinger(id: SkillId): void {
    if (typeof window === 'undefined') return

    const AudioContextCtor =
      window.AudioContext ??
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioContextCtor) return

    try {
      const ctx = this.skillCutinAudioCtx ?? new AudioContextCtor()
      this.skillCutinAudioCtx = ctx
      if (ctx.state === 'suspended') void ctx.resume()

      const now = ctx.currentTime + 0.018
      const master = ctx.createGain()
      const skillLift =
        id === 'light' ? 1.18 :
          id === 'thunder' ? 1.08 :
            id === 'fold' ? 1.12 :
              id === 'flame' ? 0.94 :
                id === 'serpent' ? 1.02 : 1
      master.gain.setValueAtTime(0.0001, now)
      master.gain.linearRampToValueAtTime(0.62 * SKILL_CUTIN_STINGER_VOLUME_SCALE, now + 0.026)
      master.gain.exponentialRampToValueAtTime(0.0001, now + 0.86)
      master.connect(ctx.destination)

      const noiseDuration = 0.34
      const noiseBuffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * noiseDuration), ctx.sampleRate)
      const samples = noiseBuffer.getChannelData(0)
      for (let i = 0; i < samples.length; i++) {
        const t = i / samples.length
        samples[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, 1.7)
      }

      const noise = ctx.createBufferSource()
      const noiseFilter = ctx.createBiquadFilter()
      const noiseGain = ctx.createGain()
      noise.buffer = noiseBuffer
      noiseFilter.type = 'bandpass'
      noiseFilter.frequency.setValueAtTime(1700 * skillLift, now)
      noiseFilter.frequency.exponentialRampToValueAtTime(5200 * skillLift, now + 0.26)
      noiseFilter.Q.setValueAtTime(1.2, now)
      noiseGain.gain.setValueAtTime(0.0001, now)
      noiseGain.gain.linearRampToValueAtTime(0.34, now + 0.025)
      noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34)
      noise.connect(noiseFilter)
      noiseFilter.connect(noiseGain)
      noiseGain.connect(master)
      noise.start(now)
      noise.stop(now + noiseDuration)

      const impact = ctx.createOscillator()
      const impactGain = ctx.createGain()
      impact.type = 'triangle'
      impact.frequency.setValueAtTime(96 * skillLift, now)
      impact.frequency.exponentialRampToValueAtTime(44 * skillLift, now + 0.32)
      impactGain.gain.setValueAtTime(0.0001, now)
      impactGain.gain.linearRampToValueAtTime(0.42, now + 0.018)
      impactGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.48)
      impact.connect(impactGain)
      impactGain.connect(master)
      impact.start(now)
      impact.stop(now + 0.5)

      const shout = ctx.createOscillator()
      const shoutFilter = ctx.createBiquadFilter()
      const shoutGain = ctx.createGain()
      shout.type = 'sawtooth'
      shout.frequency.setValueAtTime(182 * skillLift, now + 0.04)
      shout.frequency.exponentialRampToValueAtTime(132 * skillLift, now + 0.42)
      shoutFilter.type = 'bandpass'
      shoutFilter.frequency.setValueAtTime(820, now)
      shoutFilter.Q.setValueAtTime(4.6, now)
      shoutGain.gain.setValueAtTime(0.0001, now + 0.04)
      shoutGain.gain.linearRampToValueAtTime(0.18, now + 0.1)
      shoutGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.54)
      shout.connect(shoutFilter)
      shoutFilter.connect(shoutGain)
      shoutGain.connect(master)
      shout.start(now + 0.04)
      shout.stop(now + 0.56)

      const ring = ctx.createOscillator()
      const ringGain = ctx.createGain()
      ring.type = 'sine'
      ring.frequency.setValueAtTime(820 * skillLift, now + 0.1)
      ring.frequency.exponentialRampToValueAtTime(1380 * skillLift, now + 0.52)
      ringGain.gain.setValueAtTime(0.0001, now + 0.08)
      ringGain.gain.linearRampToValueAtTime(0.16, now + 0.15)
      ringGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.76)
      ring.connect(ringGain)
      ringGain.connect(master)
      ring.start(now + 0.08)
      ring.stop(now + 0.78)

      window.setTimeout(() => master.disconnect(), 980)
    } catch {
      // Browsers can reject audio before a user gesture; the visual cut-in should still play.
    }
  }

  private finishSkillCutin(): void {
    this.skillCutinResolve?.()
  }

  private cancelSkillCutin(): void {
    const wasPlaying = this.skillCutinPlaying
    this.skillCutinToken++
    this.clearSkillCutinTimer()
    this.skillCutinResolve = null
    this.skillCutinPlaying = false
    this.skillCutinSkill = null
    if (wasPlaying) {
      window.dispatchEvent(new CustomEvent('ping-pong:skill-cutin-end'))
    }

    const root = this.skillCutinEl
    const video = this.skillCutinVideoEl
    if (root) root.classList.remove('is-active', 'is-finishing')
    if (video) {
      video.pause()
      video.removeAttribute('src')
      video.load()
    }
  }

  private clearSkillCutinTimer(): void {
    if (this.skillCutinTimeoutId === null) return
    window.clearTimeout(this.skillCutinTimeoutId)
    this.skillCutinTimeoutId = null
  }

  private recordAiSkillUse(id: AiSkillId): void {
    this.p2SkillHistory.push(id)
    if (this.p2SkillHistory.length > 6) this.p2SkillHistory.shift()
    this.p2SkillUseCounts[id]++
  }

  private colorToCss(color: number): string {
    return `#${color.toString(16).padStart(6, '0')}`
  }

  private activateSkill(id: SkillId, owner: Side, zone: HitZone, charge: number): void {
    const config = SKILL_CONFIGS[id]
    const dir = owner === 'p1' ? -1 : 1
    const speedBefore = Math.max(Math.hypot(this.ballVx, this.ballVy), BALL_INITIAL_SPEED)
    const state: ActiveSkillState = {
      id,
      owner,
      elapsed: 0,
      duration: id === 'flame' ? 3.2 : id === 'light' ? 0.85 : id === 'thunder' ? 1.45 : 1.75,
      color: config.color,
      maxSpeed: config.maxSpeed,
      startY: this.ball.y,
      baseVy: this.ballVy,
      zigzagStep: -1,
      zigzagSign: Math.sign(this.ballVy) || (this.ball.y < this.fieldH / 2 ? 1 : -1),
      breakIndex: 0,
      hasPierced: false,
      counterReadyAtLaunch:
        !(owner === 'p1' && (id === 'thunder' || id === 'light')) ||
        this.p2Charge >= FULL_CHARGE_COUNTER_THRESHOLD,
    }

    if (owner === 'p1') {
      const playerSkillId = id as PlayerSkillId
      state.piercesPaddle =
        playerSkillId === 'flame' ||
        Math.random() < PLAYER_SKILL_PIERCE_CHANCE[playerSkillId]
    }

    if (id === 'qianying') {
      state.qianyingSeed = Phaser.Math.Between(1000, 999999)
    }

    if (id === 'thunder') {
      state.duration = 0.95
      state.thunderRoute = this.generateThunderRoute(owner, state.zigzagSign)
      state.thunderRouteIndex = 0
      state.thunderSeed = Phaser.Math.Between(1000, 999999)
    }

    if (id === 'serpent' || id === 'fold') {
      state.aiSkillSeed = Phaser.Math.Between(1000, 999999)
      state.duration = id === 'serpent' ? 1.9 : 1.25
    }

    this.activeSkill = state
    this.trailPoints = [{ x: this.ball.x, y: this.ball.y, spin: this.ballSpin }]

    if (id === 'qianying') {
      this.setDirectionalVelocity(dir, Math.max(speedBefore, 910 + charge * 80), Math.sign(this.ballVy || this.p1Velocity || 1) * 0.28)
      this.ballSpin = Phaser.Math.Clamp(this.ballSpin + state.zigzagSign * 360, -SPIN_MAX, SPIN_MAX)
      this.spawnSkillBurst(config.name, config.color, 1.15)
    } else if (id === 'flame') {
      this.setDirectionalVelocity(dir, Math.max(speedBefore, config.maxSpeed), Phaser.Math.Clamp(this.ballVy / Math.max(speedBefore, 1), -0.18, 0.18))
      this.ballSpin = 0
      this.spawnSkillBurst(config.name, config.color, 1.65)
      this.cameras.main.shake(130, 0.004)
    } else if (id === 'thunder') {
      this.driveThunderRoute(state)
      this.spawnSkillBurst(config.name, config.color, 1.4)
      this.cameras.main.shake(120, 0.0042)
    } else if (id === 'light') {
      this.setDirectionalVelocity(dir, config.maxSpeed, Phaser.Math.Clamp(this.ballVy / Math.max(speedBefore, 1), -0.03, 0.03))
      this.ballSpin = 0
      this.spawnSkillBurst(config.name, config.color, 1.45)
      this.cameras.main.flash(95, 255, 224, 116, false)
    } else if (id === 'serpent') {
      this.setDirectionalVelocity(dir, Math.max(speedBefore, 930), Phaser.Math.Clamp(this.ballVy / Math.max(speedBefore, 1), -0.18, 0.18))
      state.baseVy = this.ballVy
      this.spawnSkillBurst(config.name, config.color, 1.05)
    } else if (id === 'fold') {
      this.setDirectionalVelocity(dir, Math.max(speedBefore, config.maxSpeed), Phaser.Math.Clamp(this.ballVy / Math.max(speedBefore, 1), -0.12, 0.12))
      this.spawnSkillBurst(config.name, config.color, 1.2)
    } else if (id === 'double') {
      this.setDirectionalVelocity(dir, Math.max(speedBefore, 920), Phaser.Math.Clamp(this.ballVy / Math.max(speedBefore, 1), -0.22, 0.22))
      const splitSign = Math.sign(this.ballVy) || (this.ball.y < this.fieldH / 2 ? 1 : -1)
      this.ballVy = splitSign * Math.max(Math.abs(this.ballVy), 260)
      this.limitBallSpeed()
      this.spawnFakeBall(config.color, dir, -splitSign)
      this.spawnSkillBurst(config.name, config.color, 1.2)
    }

    this.limitBallSpeed()
    this.flashBall(zone, Math.max(charge, 0.72))
    this.playSkillActivateSound(id)
    this.showSkillNotice(config.name, config.color)
    this.updateSkillHud()
  }

  private updateActiveSkill(dt: number): void {
    const skill = this.activeSkill
    if (!skill) return

    skill.elapsed += dt
    const dir = skill.owner === 'p1' ? -1 : 1
    const speed = Math.min(Math.max(Math.hypot(this.ballVx, this.ballVy), BALL_INITIAL_SPEED), skill.maxSpeed)

    if (skill.id === 'qianying') {
      const wave = Math.sin(skill.elapsed * 18) * 260 + Math.sin(skill.elapsed * 31) * 90
      this.ballVy += wave * dt
      this.ballSpin = Phaser.Math.Clamp(this.ballSpin + skill.zigzagSign * 80 * dt, -SPIN_MAX, SPIN_MAX)
    } else if (skill.id === 'flame') {
      this.ballVx = dir * Math.max(Math.abs(this.ballVx), skill.maxSpeed * 0.9)
      this.ballVy *= Math.pow(0.52, dt)
    } else if (skill.id === 'thunder') {
      this.driveThunderRoute(skill)
    } else if (skill.id === 'light') {
      this.ballVx = dir * skill.maxSpeed
      this.ballVy *= Math.pow(0.025, dt)
    } else if (skill.id === 'serpent') {
      const seedPhase = (skill.aiSkillSeed ?? 0) * 0.003
      const progress = Phaser.Math.Clamp(skill.elapsed / Math.max(skill.duration, 0.01), 0, 1)
      const envelope = 0.72 + Math.sin(progress * Math.PI) * 0.5
      const wave =
        Math.sin(skill.elapsed * 7.1 + seedPhase) * 330 +
        Math.sin(skill.elapsed * 12.8 + seedPhase * 1.7) * 150 +
        Math.sin(skill.elapsed * 3.6 + seedPhase * 0.6) * 95
      let targetVy = skill.baseVy * 0.32 + wave * envelope
      const wallPadding = 78
      if (this.ball.y < wallPadding) targetVy += (wallPadding - this.ball.y) * 8.8
      if (this.ball.y > this.fieldH - wallPadding) targetVy -= (this.ball.y - (this.fieldH - wallPadding)) * 8.8
      this.ballVx = dir * Math.max(Math.abs(this.ballVx), skill.maxSpeed * 0.82)
      this.ballVy = Phaser.Math.Linear(this.ballVy, targetVy, 1 - Math.pow(0.0008, dt))
      this.ballSpin = Phaser.Math.Clamp(this.ballSpin + Math.sin(skill.elapsed * 4.2 + seedPhase) * 130 * dt, -SPIN_MAX, SPIN_MAX)
    } else if (skill.id === 'fold') {
      const breakTimes = [0.23, 0.4]
      if (skill.breakIndex < breakTimes.length && skill.elapsed >= breakTimes[skill.breakIndex]) {
        const seedSign = this.thunderSignedNoise((skill.aiSkillSeed ?? 0) + 37) >= 0 ? 1 : -1
        const sign = skill.breakIndex === 0 ? skill.zigzagSign * seedSign : -skill.zigzagSign * seedSign
        const ratio = skill.breakIndex === 0 ? 0.58 + this.thunderNoise((skill.aiSkillSeed ?? 0) + 91) * 0.12 : 0.18
        this.setDirectionalVelocity(dir, skill.breakIndex === 0 ? skill.maxSpeed : skill.maxSpeed * 0.92, sign * ratio)
        if (skill.breakIndex === 0) {
          skill.zigzagSign = sign
          this.cameras.main.shake(90, 0.003)
        }
        this.spawnSkillSpark(skill.color, sign)
        skill.breakIndex++
      }
    } else if (skill.id === 'double') {
      this.ballVy += Math.sin(skill.elapsed * 10) * 120 * dt
    }

    this.limitBallSpeed()

    if (skill.elapsed >= skill.duration && skill.id !== 'flame') {
      this.activeSkill = null
    }
  }

  private generateThunderRoute(owner: Side, initialSign: number): ThunderRoutePoint[] {
    const startX = this.ball.x
    const startY = this.ball.y
    const targetX = owner === 'p1' ? -BALL_RADIUS - 76 : this.fieldW + BALL_RADIUS + 76
    const travelX = Math.max(Math.abs(targetX - startX), 120)
    const minY = BALL_RADIUS * 4
    const maxY = this.fieldH - BALL_RADIUS * 4
    const rangeY = Math.max(maxY - minY, 1)
    const breakCount = Phaser.Math.Clamp(Math.round(travelX / Phaser.Math.FloatBetween(150, 205)), 4, 7)
    const points: ThunderRoutePoint[] = [{ x: startX, y: startY }]
    let previousY = startY
    let previousSign = initialSign || (startY < this.fieldH / 2 ? 1 : -1)

    for (let i = 1; i <= breakCount; i++) {
      const baseT = i / (breakCount + 1)
      const t = Phaser.Math.Clamp(baseT + Phaser.Math.FloatBetween(-0.035, 0.035), 0.08, 0.92)
      const xJitter = Phaser.Math.FloatBetween(-travelX * 0.03, travelX * 0.03)
      const x = startX + (targetX - startX) * t + xJitter
      let sign = Math.random() < 0.78 ? -previousSign : Phaser.Math.RND.pick([-1, 1])
      const centerPull = Phaser.Math.Clamp(Math.abs(previousY - this.fieldH / 2) / (rangeY * 0.5), 0, 1)
      let amplitude = Phaser.Math.FloatBetween(rangeY * 0.26, rangeY * 0.48)

      if (centerPull < 0.22) {
        amplitude += Phaser.Math.FloatBetween(rangeY * 0.07, rangeY * 0.14)
      }

      let y = this.reflectY(previousY + sign * amplitude + Phaser.Math.FloatBetween(-rangeY * 0.06, rangeY * 0.06))
      if (Math.abs(y - previousY) < rangeY * 0.2) {
        sign *= -1
        y = this.reflectY(previousY + sign * Phaser.Math.FloatBetween(rangeY * 0.3, rangeY * 0.52))
      }

      y = Phaser.Math.Clamp(y, minY, maxY)
      points.push({ x, y })
      previousY = y
      previousSign = sign
    }

    const finishSign = Math.random() < 0.72 ? -previousSign : Phaser.Math.RND.pick([-1, 1])
    const finishY = Phaser.Math.Clamp(
      this.reflectY(previousY + finishSign * Phaser.Math.FloatBetween(rangeY * 0.22, rangeY * 0.42)),
      minY,
      maxY
    )
    points.push({ x: targetX, y: finishY })

    const dir = owner === 'p1' ? -1 : 1
    points.sort((a, b) => (dir < 0 ? b.x - a.x : a.x - b.x))
    return points
  }

  private driveThunderRoute(skill: ActiveSkillState): void {
    const route = skill.thunderRoute
    if (!route || route.length < 2) {
      const dir = skill.owner === 'p1' ? -1 : 1
      this.setDirectionalVelocity(dir, skill.maxSpeed, skill.zigzagSign * 0.66)
      return
    }

    let index = Phaser.Math.Clamp(skill.thunderRouteIndex ?? 0, 0, route.length - 2)
    let from = route[index]
    let to = route[index + 1]

    while (index < route.length - 2 && this.hasPassedThunderPoint(from, to)) {
      index++
      skill.thunderRouteIndex = index
      skill.zigzagSign *= -1
      this.spawnSkillSpark(skill.color, Math.sign(to.y - from.y) || skill.zigzagSign)
      this.cameras.main.shake(36, 0.0016)
      from = route[index]
      to = route[index + 1]
    }

    const dx = to.x - this.ball.x
    const dy = to.y - this.ball.y
    const distance = Math.max(Math.hypot(dx, dy), 1)
    const speed = skill.maxSpeed * Phaser.Math.Clamp(1.02 - skill.elapsed * 0.08, 0.94, 1.02)
    this.ballVx = (dx / distance) * speed
    this.ballVy = (dy / distance) * speed
    this.ballSpin = 0
  }

  private hasPassedThunderPoint(from: ThunderRoutePoint, to: ThunderRoutePoint): boolean {
    const dx = to.x - from.x
    const dy = to.y - from.y
    const lengthSq = Math.max(dx * dx + dy * dy, 1)
    const progress = ((this.ball.x - from.x) * dx + (this.ball.y - from.y) * dy) / lengthSq
    return progress >= 0.92 || Math.hypot(this.ball.x - to.x, this.ball.y - to.y) < 42
  }

  private setDirectionalVelocity(dir: -1 | 1, speed: number, vyRatio: number): void {
    const clampedRatio = Phaser.Math.Clamp(vyRatio, -0.72, 0.72)
    const vxRatio = Math.sqrt(Math.max(0.15, 1 - clampedRatio * clampedRatio))
    this.ballVx = dir * speed * vxRatio
    this.ballVy = speed * clampedRatio
  }

  private spawnFakeBall(color: number, dir: -1 | 1, verticalSign: number): void {
    this.clearFakeBalls()

    const offset = verticalSign * 32
    const fake = this.add.circle(this.ball.x, this.ball.y + offset, BALL_RADIUS * 1.08, color, 0.86)
    fake.setDepth(8.05)
    fake.setStrokeStyle(3, 0xfff2ff, 0.86)

    const fakeVy = verticalSign * Math.max(Math.abs(this.ballVy) * 1.15, 320)
    this.fakeBalls.push({
      body: fake,
      vx: dir * Math.max(Math.abs(this.ballVx) * 0.96, 860),
      vy: fakeVy,
      age: 0,
      maxAge: 2.15,
      color,
      trail: [{ x: fake.x, y: fake.y }],
    })
  }

  private updateFakeBalls(dt: number): void {
    for (const fake of [...this.fakeBalls]) {
      fake.age += dt
      fake.body.x += fake.vx * dt
      fake.body.y += fake.vy * dt
      fake.trail.push({ x: fake.body.x, y: fake.body.y })
      while (fake.trail.length > 22) {
        fake.trail.shift()
      }

      if (fake.body.y - BALL_RADIUS <= 0) {
        fake.body.y = BALL_RADIUS
        fake.vy = Math.abs(fake.vy)
      } else if (fake.body.y + BALL_RADIUS >= this.fieldH) {
        fake.body.y = this.fieldH - BALL_RADIUS
        fake.vy = -Math.abs(fake.vy)
      }

      fake.body.setAlpha(Phaser.Math.Clamp(0.95 * (1 - fake.age / fake.maxAge) + 0.12, 0, 0.95))
      fake.body.setScale(1.05 + Math.sin(fake.age * 18) * 0.1)

      if (fake.age >= fake.maxAge || fake.body.x < -60 || fake.body.x > this.fieldW + 60) {
        fake.body.destroy()
        this.fakeBalls = this.fakeBalls.filter((entry) => entry !== fake)
      }
    }
  }

  private clearFakeBalls(): void {
    for (const fake of this.fakeBalls) {
      fake.body.destroy()
    }
    this.fakeBalls = []
  }

  private clearActiveSkill(): void {
    this.activeSkill = null
    this.ball?.setAlpha(1)
    this.clearFakeBalls()
  }

  private shouldPiercePaddle(target: Side): boolean {
    const skill = this.activeSkill
    if (!skill || skill.owner === target) return false
    if (skill.owner === 'p1' && target === 'p2') {
      if (skill.id === 'flame') return true
      return skill.piercesPaddle ?? false
    }

    return false
  }

  private onSkillPiercePaddle(target: Side): void {
    const skill = this.activeSkill
    if (!skill || skill.hasPierced) return

    skill.hasPierced = true
    const paddle = target === 'p1' ? this.p1Paddle : this.p2Paddle
    const dir = skill.owner === 'p1' ? -1 : 1

    this.ball.x = paddle.x + dir * (PADDLE_WIDTH / 2 + BALL_RADIUS + 3)
    this.spawnSkillSpark(skill.color, Math.sign(this.ballVy) || 1)
    this.cameras.main.shake(120, 0.0045)
    this.showSkillNotice(skill.id === 'qianying' ? '幻影穿透!' : '穿透!', skill.color)
    this.playSkillActivateSound(skill.id === 'qianying' ? 'qianying' : 'flame')
  }

  private addEnergy(side: Side, amount: number): void {
    if (side === 'p1') {
      this.p1Energy = Phaser.Math.Clamp(this.p1Energy + amount, 0, ENERGY_MAX)
    } else {
      this.p2Energy = Phaser.Math.Clamp(this.p2Energy + amount, 0, ENERGY_MAX)
    }
  }

  private getEnergy(side: Side): number {
    return side === 'p1' ? this.p1Energy : this.p2Energy
  }

  private consumeSkillResource(side: Side, id: SkillId, startCooldown = true): void {
    const config = SKILL_CONFIGS[id]
    this.addEnergy(side, -config.energyCost)
    if (!startCooldown) return
    this.startSkillCooldown(side, id)
  }

  private startSkillCooldown(side: Side, id: SkillId): void {
    const config = SKILL_CONFIGS[id]
    this.setSkillCooldownUntil(side, id, this.time.now + config.cooldownMs)
    this.setGlobalCooldownUntil(side, this.time.now + SKILL_GLOBAL_COOLDOWN_MS)
  }

  private cancelPendingSkill(side: Side): void {
    if (side === 'p1') this.p1PendingSkill = null
    else this.p2PendingSkill = null
    this.updateSkillHud()
  }

  private getGlobalCooldownUntil(side: Side): number {
    return side === 'p1' ? this.p1GlobalCooldownUntil : this.p2GlobalCooldownUntil
  }

  private setGlobalCooldownUntil(side: Side, value: number): void {
    if (side === 'p1') this.p1GlobalCooldownUntil = value
    else this.p2GlobalCooldownUntil = value
  }

  private getSkillCooldownRemainingMs(side: Side, id: SkillId): number {
    const until = side === 'p1'
      ? this.p1CooldownUntil[id as PlayerSkillId] ?? 0
      : this.p2CooldownUntil[id as AiSkillId] ?? 0
    return Math.max(0, until - this.time.now)
  }

  private setSkillCooldownUntil(side: Side, id: SkillId, value: number): void {
    if (side === 'p1') {
      this.p1CooldownUntil[id as PlayerSkillId] = value
    } else {
      this.p2CooldownUntil[id as AiSkillId] = value
    }
  }

  private awardHitEnergy(side: Side, zone: HitZone, caughtSkillBall: boolean): void {
    const qualityGain = caughtSkillBall ? 6 : zone === 'center' ? 2 : 3
    this.addEnergy(side, qualityGain)
  }

  private awardPointEnergy(scorer: Side): void {
    const loser: Side = scorer === 'p1' ? 'p2' : 'p1'
    this.addEnergy(scorer, 4)
    this.addEnergy(loser, 2)

    if (loser === 'p1') {
      this.p1LoseStreak++
      this.p2LoseStreak = 0
      this.addEnergy('p1', this.getLoseStreakEnergyBonus(this.p1LoseStreak))
    } else {
      this.p2LoseStreak++
      this.p1LoseStreak = 0
      this.addEnergy('p2', this.getLoseStreakEnergyBonus(this.p2LoseStreak))
    }
  }

  private getLoseStreakEnergyBonus(streak: number): number {
    if (streak < 2) return 0
    return Math.min(streak, 4)
  }

  private debugForceSkillHit(side: Side, id: SkillId): void {
    const config = SKILL_CONFIGS[id]
    if (config.owner !== side) return

    this.clearActiveSkill()
    this.cancelServeTimer()
    this.gameOver = false
    this.hasStarted = true
    this.gameActive = true
    this.addEnergy(side, ENERGY_MAX)

    if (side === 'p1') {
      this.p1PendingSkill = id as PlayerSkillId
      this.ball.x = this.p1Paddle.x - PADDLE_WIDTH / 2 - BALL_RADIUS - 2
      this.ball.y = this.p1Paddle.y
      this.ballVx = -BALL_INITIAL_SPEED
      this.ballVy = 0
      this.onPaddleHit(this.p1Paddle)
    } else {
      this.p2PendingSkill = id as AiSkillId
      this.ball.x = this.p2Paddle.x + PADDLE_WIDTH / 2 + BALL_RADIUS + 2
      this.ball.y = this.p2Paddle.y
      this.ballVx = BALL_INITIAL_SPEED
      this.ballVy = 0
      this.onPaddleHit(this.p2Paddle)
    }
  }

  private debugForceNormalHit(side: Side): void {
    this.clearActiveSkill()
    this.cancelServeTimer()
    this.gameOver = false
    this.hasStarted = true
    this.gameActive = true

    if (side === 'p1') {
      this.p1PendingSkill = null
      this.ball.x = this.p1Paddle.x - PADDLE_WIDTH / 2 - BALL_RADIUS - 2
      this.ball.y = this.p1Paddle.y
      this.ballVx = BALL_INITIAL_SPEED
      this.ballVy = 0
      this.onPaddleHit(this.p1Paddle)
    } else {
      this.p2PendingSkill = null
      this.ball.x = this.p2Paddle.x + PADDLE_WIDTH / 2 + BALL_RADIUS + 2
      this.ball.y = this.p2Paddle.y
      this.ballVx = -BALL_INITIAL_SPEED
      this.ballVy = 0
      this.onPaddleHit(this.p2Paddle)
    }
  }

  // ===== 球物理 =====

  private applySpin(dt: number): void {
    if (Math.abs(this.ballSpin) > 1) {
      this.ballVy += this.ballSpin * this.getSpinCurveForce() * dt
      this.limitBallSpeed()
    }

    this.ballSpin *= Math.pow(SPIN_DECAY_PER_SECOND, dt)
    if (Math.abs(this.ballSpin) < 0.5) {
      this.ballSpin = 0
    }
  }

  private limitBallSpeed(): void {
    const maxSpeed = this.getMaxBallSpeed()
    const speed = Math.hypot(this.ballVx, this.ballVy)
    if (speed <= maxSpeed || speed === 0) return

    const scale = maxSpeed / speed
    this.ballVx *= scale
    this.ballVy *= scale
  }

  private predictBallYAtX(targetX: number, maxPredictionTime = 2.2): number {
    if (Math.abs(this.ballVx) < 1) return this.ball.y

    const t = Phaser.Math.Clamp((targetX - this.ball.x) / this.ballVx, 0, maxPredictionTime)
    return this.predictBallYAfterTime(t)
  }

  private predictBallYAfterTime(t: number): number {
    const predictionTime = Phaser.Math.Clamp(t, 0, 2.2)
    const spinCurveForce = this.getSpinCurveForce()
    const rawY =
      this.ball.y +
      this.ballVy * predictionTime +
      0.5 * this.ballSpin * spinCurveForce * predictionTime * predictionTime
    return this.reflectY(rawY)
  }

  private getSpinCurveForce(): number {
    const speed = Math.hypot(this.ballVx, this.ballVy)
    const speedFactor = Phaser.Math.Clamp(speed / 1350, 0.72, 1.55)
    const spinFactor = 0.78 + Phaser.Math.Clamp(Math.abs(this.ballSpin) / SPIN_MAX, 0, 1) * 0.55
    return SPIN_CURVE_FORCE * speedFactor * spinFactor
  }

  private reflectY(y: number): number {
    const minY = BALL_RADIUS
    const maxY = this.fieldH - BALL_RADIUS
    const range = maxY - minY
    const period = range * 2
    let normalized = (y - minY) % period
    if (normalized < 0) normalized += period
    if (normalized > range) normalized = period - normalized
    return minY + normalized
  }

  private onWallBounce(wall: 'top' | 'bottom'): void {
    this.wallBounceStreak++
    const spinT = Phaser.Math.Clamp(Math.abs(this.ballSpin) / SPIN_MAX, 0, 1)
    const spinSupportsBounce = wall === 'top' ? this.ballSpin > 0 : this.ballSpin < 0
    const spinSpeedScale = spinSupportsBounce
      ? 1 + spinT * 0.34
      : 1 - spinT * 0.3

    this.ballVx *= spinSpeedScale
    this.ballVy *= spinSpeedScale
    this.enforceHorizontalVelocityFloor(this.wallBounceStreak >= 3 ? 0.64 : this.wallBounceStreak >= 2 ? 0.54 : 0.48)
    this.limitBallSpeed()
    this.ballSpin *= -0.56
    this.playWallSound()

    if (this.pressureLevel >= 2) {
      this.cameras.main.shake(45, 0.0015)
    }
  }

  private enforceHorizontalVelocityFloor(minRatio: number): void {
    const speed = Math.hypot(this.ballVx, this.ballVy)
    if (speed <= 1) return

    const currentRatio = Math.abs(this.ballVx) / speed
    if (currentRatio >= minRatio) return

    const adjustedSpeed = this.wallBounceStreak >= 3 ? speed * 0.94 : speed
    const vxSign = Math.sign(this.ballVx) || (this.serveSide === 'p1' ? 1 : -1)
    const vySign = Math.sign(this.ballVy) || 1
    const targetVx = adjustedSpeed * minRatio
    const targetVy = Math.sqrt(Math.max(adjustedSpeed * adjustedSpeed - targetVx * targetVx, 0))

    this.ballVx = vxSign * targetVx
    this.ballVy = vySign * targetVy
  }

  // ===== 碰撞检测 =====

  private checkPaddleCollision(prevBallX: number, prevBallY: number): void {
    if (this.ballVx < 0 && this.didBallHitPaddle(this.p2Paddle, 'left', prevBallX, prevBallY)) {
      if (this.shouldPiercePaddle('p2')) {
        this.onSkillPiercePaddle('p2')
        return
      }
      this.onPaddleHit(this.p2Paddle)
      return
    }

    if (this.ballVx > 0 && this.didBallHitPaddle(this.p1Paddle, 'right', prevBallX, prevBallY)) {
      if (this.shouldPiercePaddle('p1')) {
        this.onSkillPiercePaddle('p1')
        return
      }
      this.onPaddleHit(this.p1Paddle)
    }
  }

  private didBallHitPaddle(
    paddle: Phaser.GameObjects.Image,
    side: 'left' | 'right',
    prevBallX: number,
    prevBallY: number
  ): boolean {
    const halfW = PADDLE_WIDTH / 2
    const halfH = PADDLE_HEIGHT / 2
    const paddleLeft = paddle.x - halfW
    const paddleRight = paddle.x + halfW
    const paddleTop = paddle.y - halfH
    const paddleBottom = paddle.y + halfH

    const currentLeft = this.ball.x - BALL_RADIUS
    const currentRight = this.ball.x + BALL_RADIUS
    const overlapsX = currentRight >= paddleLeft && currentLeft <= paddleRight

    const faceX = side === 'left' ? paddleRight : paddleLeft
    const prevEdgeX = side === 'left' ? prevBallX - BALL_RADIUS : prevBallX + BALL_RADIUS
    const currentEdgeX = side === 'left' ? currentLeft : currentRight
    const crossedFace =
      side === 'left'
        ? prevEdgeX >= faceX && currentEdgeX <= faceX
        : prevEdgeX <= faceX && currentEdgeX >= faceX

    if (!overlapsX && !crossedFace) return false

    const progress =
      crossedFace && currentEdgeX !== prevEdgeX
        ? Phaser.Math.Clamp((faceX - prevEdgeX) / (currentEdgeX - prevEdgeX), 0, 1)
        : 1
    const yAtImpact = Phaser.Math.Linear(prevBallY, this.ball.y, progress)

    return yAtImpact + BALL_RADIUS >= paddleTop && yAtImpact - BALL_RADIUS <= paddleBottom
  }

  private onPaddleHit(paddle: Phaser.GameObjects.Image): void {
    const isPlayer = paddle === this.p1Paddle
    const side: Side = isPlayer ? 'p1' : 'p2'
    const dir = isPlayer ? -1 : 1
    const paddleVelocity = isPlayer ? this.p1Velocity : this.p2Velocity
    const charge = isPlayer ? this.p1Charge : this.p2Charge
    const incomingSkill = this.activeSkill
    const caughtSkillBall = !!incomingSkill && incomingSkill.owner !== side

    this.clearActiveSkill()
    this.ballOwnerColor = isPlayer ? PLAYER_BALL_COLOR : AI_BALL_COLOR
    this.wallBounceStreak = 0

    const hitOffset = Phaser.Math.Clamp((this.ball.y - paddle.y) / (PADDLE_HEIGHT / 2), -1, 1)
    const zone = this.getHitZone(Math.abs(hitOffset))
    const whipSpinQuality = this.getWhipSpinQuality(hitOffset, paddleVelocity, zone)

    this.rallyCount++
    this.maxRallyCount = Math.max(this.maxRallyCount, this.rallyCount)
    const previousPressure = this.pressureLevel
    this.pressureLevel = this.getPressureLevel()

    const currentSpeed = Math.hypot(this.ballVx, this.ballVy)
    const zoneSpeedBonus = zone === 'center' ? 4 : zone === 'slice' ? 24 : 46
    const pressureBonus = this.pressureLevel * 10
    const chargeT = Math.pow(charge, 1.12)
    const baseHitSpeed = currentSpeed + BALL_SPEED_INCREMENT + zoneSpeedBonus + pressureBonus
    const newSpeed = Math.min(
      baseHitSpeed * (1 + chargeT * CHARGE_SPEED_MULTIPLIER) + chargeT * CHARGE_FLAT_SPEED_BONUS,
      this.getMaxBallSpeed()
    )

    const angle = this.getHitAngle(hitOffset, zone, paddleVelocity, charge)
    this.ballVx = dir * newSpeed * Math.cos(angle)
    this.ballVy = newSpeed * Math.sin(angle)
    this.ballSpin = this.getSpinFromHit(hitOffset, zone, paddleVelocity, charge)
    const spinSpeedBoost = this.getSpinSpeedBoost(this.ballSpin, paddleVelocity, zone, chargeT, hitOffset)
    this.ballVx *= spinSpeedBoost
    this.ballVy *= spinSpeedBoost
    this.limitBallSpeed()
    const activatedSkill = this.resolvePendingSkillOnHit(side, zone, charge)
    if (isPlayer) {
      this.playerReturnStreak++
    }
    const returnFxTier = isPlayer && !activatedSkill ? this.getPlayerReturnFxPower() : 0
    this.awardHitEnergy(side, zone, caughtSkillBall)
    this.bridge.sendRallyHit()
    if (activatedSkill) {
      this.sendSkillUsed(side, activatedSkill, zone, charge)
    } else {
      this.sendCombatState('hit')
    }

    if (isPlayer) {
      this.p1Charge = 0
      this.p1ChargeReadyPlayed = false
    } else {
      this.p2Charge = 0
    }

    // 确保球不卡在球拍内
    if (isPlayer) {
      this.ball.x = this.p1Paddle.x - PADDLE_WIDTH / 2 - BALL_RADIUS
    } else {
      this.ball.x = this.p2Paddle.x + PADDLE_WIDTH / 2 + BALL_RADIUS
    }

    this.updateComboText()
    if (!activatedSkill) {
      this.flashBall(zone, charge, returnFxTier)
      if (isPlayer && returnFxTier > 0) {
        this.spawnPlayerReturnBurst(zone, charge, returnFxTier)
      } else {
        this.spawnHitBurst(paddle, zone, charge)
      }
      this.playHitSound(zone, charge, returnFxTier)
      if (returnFxTier >= 2) {
        this.cameras.main.shake(36 + returnFxTier * 12, 0.001 + returnFxTier * 0.00045)
      }
      if (isPlayer && whipSpinQuality > 0.78) {
        this.showSkillNotice('神弧甩球!', PLAYER_BALL_COLOR)
        this.cameras.main.shake(85, 0.0032)
      }
    } else {
      this.spawnHitBurst(paddle, zone, Math.max(charge, 0.78))
    }
    this.updateSkillHud()

    if (this.pressureLevel > previousPressure) {
      this.onPressureLevelUp()
    }
  }

  private getHitZone(absOffset: number): HitZone {
    if (absOffset <= 0.35) return 'center'
    if (absOffset <= 0.74) return 'slice'
    return 'edge'
  }

  private getHitAngle(hitOffset: number, zone: HitZone, paddleVelocity: number, charge: number): number {
    const absOffset = Math.abs(hitOffset)
    const sign = Math.sign(hitOffset) || Math.sign(paddleVelocity) || 1
    let baseAngle: number

    if (zone === 'center') {
      baseAngle = Phaser.Math.Linear(0, Phaser.Math.DegToRad(13), absOffset / 0.35)
    } else if (zone === 'slice') {
      baseAngle = Phaser.Math.Linear(Phaser.Math.DegToRad(16), Phaser.Math.DegToRad(35), (absOffset - 0.35) / 0.39)
    } else {
      baseAngle = Phaser.Math.Linear(Phaser.Math.DegToRad(37), MAX_BOUNCE_ANGLE, (absOffset - 0.74) / 0.26)
    }

    const motionAngle = Phaser.Math.Clamp(paddleVelocity / 1650, -1, 1) * (0.1 + charge * 0.06)
    return Phaser.Math.Clamp(sign * baseAngle + motionAngle, -MAX_BOUNCE_ANGLE, MAX_BOUNCE_ANGLE)
  }

  private getSpinFromHit(hitOffset: number, zone: HitZone, paddleVelocity: number, charge: number): number {
    const motionSign = Math.sign(paddleVelocity)
    const contactSign = Math.sign(hitOffset)
    const motionT = Phaser.Math.Clamp(Math.abs(paddleVelocity) / PLAYER_PADDLE_SPEED, 0, 1)
    const contactT = Phaser.Math.Clamp(Math.abs(hitOffset), 0, 1)
    const whipT = this.getWhipSpinQuality(hitOffset, paddleVelocity, zone)
    const zoneMultiplier = zone === 'center' ? 0.28 : zone === 'slice' ? 0.94 : 1.22
    let spinDirection = motionSign || contactSign || 1

    if (motionSign && contactSign && motionSign !== contactSign) {
      spinDirection = motionSign * 0.78 + contactSign * 0.22
    }

    const spinMagnitude =
      (100 + contactT * 560 + motionT * 820 + charge * CHARGE_SPIN_BONUS + whipT * 640) * zoneMultiplier
    return Phaser.Math.Clamp(spinDirection * spinMagnitude, -SPIN_MAX, SPIN_MAX)
  }

  private getSpinSpeedBoost(spin: number, paddleVelocity: number, zone: HitZone, charge: number, hitOffset: number): number {
    const spinT = Phaser.Math.Clamp(Math.abs(spin) / SPIN_MAX, 0, 1)
    const motionT = Phaser.Math.Clamp(Math.abs(paddleVelocity) / PLAYER_PADDLE_SPEED, 0, 1)
    const whipT = this.getWhipSpinQuality(hitOffset, paddleVelocity, zone)
    const zoneFactor = zone === 'center' ? 0.24 : zone === 'slice' ? 0.78 : 1
    return 1 + spinT * zoneFactor * (0.12 + motionT * SPIN_SPEED_BONUS + charge * 0.16) + whipT * 0.36
  }

  private getWhipSpinQuality(hitOffset: number, paddleVelocity: number, zone: HitZone): number {
    if (zone === 'center') return 0
    const motionT = Phaser.Math.Clamp(Math.abs(paddleVelocity) / PLAYER_PADDLE_SPEED, 0, 1)
    const contactT = Phaser.Math.Clamp(Math.abs(hitOffset), 0, 1)
    const zoneT = zone === 'edge' ? 1 : 0.55
    return this.smoothStep(0.58, 0.96, motionT) * this.smoothStep(0.48, 0.92, contactT) * zoneT
  }

  private smoothStep(min: number, max: number, value: number): number {
    const t = Phaser.Math.Clamp((value - min) / (max - min), 0, 1)
    return t * t * (3 - 2 * t)
  }

  private getPressureLevel(): number {
    if (this.rallyCount >= 10) return 3
    if (this.rallyCount >= 6) return 2
    if (this.rallyCount >= 3) return 1
    return 0
  }

  private getPlayerReturnStreakTier(streak = this.playerReturnStreak): number {
    if (streak >= RETURN_STREAK_TIER_4) return 4
    if (streak >= RETURN_STREAK_TIER_3) return 3
    if (streak >= RETURN_STREAK_TIER_2) return 2
    if (streak >= RETURN_STREAK_TIER_1) return 1
    return 0
  }

  private getPlayerReturnFxPower(streak = this.playerReturnStreak): number {
    const tier = this.getPlayerReturnStreakTier(streak)
    if (tier <= 0) return 0

    const continuousLift = Math.min(2.4, Math.max(0, (streak - RETURN_STREAK_TIER_1) * 0.28))
    const milestoneLift = streak >= 10 ? 0.8 : streak >= 7 ? 0.55 : streak >= 4 ? 0.25 : 0
    return Phaser.Math.Clamp(tier + continuousLift + milestoneLift, 1, 6.2)
  }

  private getCurrentReturnFxTier(): number {
    if (this.activeSkill) return 0
    if (this.ballOwnerColor !== PLAYER_BALL_COLOR) return 0
    return this.getPlayerReturnFxPower()
  }

  private getMaxBallSpeed(): number {
    return this.activeSkill?.maxSpeed ?? BALL_NORMAL_MAX_SPEED
  }

  private onPressureLevelUp(): void {
    const color = this.pressureLevel >= 3 ? 0xfff26f : this.pressureLevel === 2 ? 0xff5c6d : 0x42ecff
    this.playPressureSound()
    this.cameras.main.shake(70 + this.pressureLevel * 35, 0.002 + this.pressureLevel * 0.001)

    const pulse = this.add.rectangle(this.fieldW / 2, this.fieldH / 2, this.fieldW - 24, this.fieldH - 24)
    pulse.setDepth(4)
    pulse.setStrokeStyle(2 + this.pressureLevel, color, 0.42)
    pulse.setAlpha(0.85)
    this.tweens.add({
      targets: pulse,
      scaleX: 1.04,
      scaleY: 1.04,
      alpha: 0,
      duration: 380,
      ease: 'Quad.easeOut',
      onComplete: () => pulse.destroy(),
    })
  }

  // ===== 得分判定 =====

  private checkScoring(): void {
    let scorer: 'p1' | 'p2' | null = null

    if (this.ball.x + BALL_RADIUS < 0) {
      scorer = 'p1'
    } else if (this.ball.x - BALL_RADIUS > this.fieldW) {
      scorer = 'p2'
    }

    if (!scorer) return

    if (this.rallyCount > 0) {
      this.bridge.sendRallyEnd({ count: this.rallyCount, scorer })
    }

    this.playerReturnStreak = 0
    this.score[scorer]++
    this.awardPointEnergy(scorer)
    this.cancelPendingSkill('p1')
    this.cancelPendingSkill('p2')
    this.clearActiveSkill()
    this.updateScoreText()
    this.updateSkillHud()
    this.playScoreSound(scorer)

    this.bridge.sendPointScored({
      scorer,
      p1: this.score.p1,
      p2: this.score.p2,
    })
    this.bridge.sendScoreUpdate(this.score)
    this.sendCombatState('point_scored')

    if (this.score.p1 >= WINNING_SCORE || this.score.p2 >= WINNING_SCORE) {
      const winner = this.score.p1 >= WINNING_SCORE ? 'p1' : 'p2'
      const result = this.getGameOverResult(winner)
      this.gameOver = true
      this.gameActive = false
      this.bridge.sendGameOver(result)
      this.showGameOver(result)
      return
    }

    this.serveSide = scorer === 'p1' ? 'p2' : 'p1'
    this.serveBall()
  }

  // ===== 发球 =====

  private serveBall(): void {
    this.cancelServeTimer()
    this.rallyCount = 0
    this.pressureLevel = 0
    this.playerReturnStreak = 0
    this.wallBounceStreak = 0
    this.ballSpin = 0
    this.ballOwnerColor = BALL_COLOR
    this.clearActiveSkill()
    this.cancelPendingSkill('p1')
    this.cancelPendingSkill('p2')
    this.trailPoints = []
    this.updateComboText()
    this.ball.x = this.fieldW / 2
    this.ball.y = this.fieldH / 2
    this.ballVx = 0
    this.ballVy = 0
    this.ball.setAlpha(1)
    this.ball.setFillStyle(BALL_COLOR, 1)
    this.gameActive = false

    this.serveTimer = this.time.delayedCall(600, () => {
      this.serveTimer = null
      if (this.gameOver) return

      const dir = this.serveSide === 'p1' ? 1 : -1
      const angle = (Math.random() - 0.5) * Phaser.Math.DegToRad(28)

      this.ballVx = dir * BALL_INITIAL_SPEED * Math.cos(angle)
      this.ballVy = BALL_INITIAL_SPEED * Math.sin(angle)
      this.gameActive = true
      this.playServeSound()
    })
  }

  private cancelServeTimer(): void {
    if (!this.serveTimer) return
    this.serveTimer.remove(false)
    this.serveTimer = null
  }

  private startMatch(): void {
    this.resumeAudio()
    if (this.skillCutinPlaying) return

    if (this.gameOver) {
      this.hasStarted = true
      this.resetGame()
      return
    }

    if (!this.hasStarted) {
      this.hasStarted = true
      this.resetGame()
      return
    }

    if (this.ballVx === 0 && this.ballVy === 0) {
      this.serveBall()
      return
    }

    this.gameActive = true
  }

  // ===== 重置 =====

  private resetGame(autoServe = true): void {
    this.cancelSkillCutin()
    this.hideGameOverPanel()
    this.score = { p1: 0, p2: 0 }
    this.gameOver = false
    this.gameActive = false
    this.cancelServeTimer()
    this.rallyCount = 0
    this.maxRallyCount = 0
    this.pressureLevel = 0
    this.playerReturnStreak = 0
    this.wallBounceStreak = 0
    this.serveSide = Math.random() > 0.5 ? 'p1' : 'p2'
    this.p1Energy = PLAYER_START_ENERGY
    this.p2Energy = AI_START_ENERGY
    this.p1PendingSkill = null
    this.p2PendingSkill = null
    this.activeSkill = null
    this.clearFakeBalls()
    this.p1CooldownUntil = { qianying: 0, flame: 0, thunder: 0, light: 0 }
    this.p2CooldownUntil = { serpent: 0, fold: 0, double: 0 }
    this.p1GlobalCooldownUntil = 0
    this.p2GlobalCooldownUntil = 0
    this.p1LoseStreak = 0
    this.p2LoseStreak = 0
    this.p2LastSkillDecisionAt = 0
    this.p2SkillHistory = []
    this.p2SkillUseCounts = { serpent: 0, fold: 0, double: 0 }
    this.setAiDifficulty('normal')

    this.p1Paddle.y = this.fieldH / 2
    this.p2Paddle.y = this.fieldH / 2
    this.p1TargetY = this.fieldH / 2
    this.p1Velocity = 0
    this.p2Velocity = 0
    this.p1Charge = 0
    this.p2Charge = 0
    this.p1NativeTouchPointerIds.clear()
    this.p1TouchActive = false
    this.p1TouchPointerId = null
    this.p1Charging = false
    this.p1ChargeReadyPlayed = false

    this.ball.x = this.fieldW / 2
    this.ball.y = this.fieldH / 2
    this.ballVx = 0
    this.ballVy = 0
    this.ballSpin = 0
    this.ballOwnerColor = BALL_COLOR
    this.ball.setAlpha(1)
    this.ball.setFillStyle(BALL_COLOR, 1)
    this.trailPoints = []

    this.updateScoreText()
    this.updateComboText()
    this.updateSkillHud()
    this.drawPaddleEffects()
    this.drawBallTrail(false)
    this.drawSpinRing()
    this.drawSkillEffects()

    this.children.getByName('gameOverText')?.destroy()
    this.children.getByName('restartHint')?.destroy()

    this.bridge.sendGameReset()
    this.bridge.sendScoreUpdate(this.score)
    this.sendCombatState('reset')

    if (autoServe) {
      this.serveBall()
    }
  }

  // ===== UI 与视觉反馈 =====

  private updateScoreText(): void {
    this.p1ScoreText.setText(this.score.p1.toString())
    this.p2ScoreText.setText(this.score.p2.toString())
    this.updateScoreDom()
  }

  private bindCombatHudDom(): void {
    if (typeof document === 'undefined') return

    this.herScoreEl = document.getElementById('her-score')
    this.playerScoreEl = document.getElementById('player-score')
    this.herEnergyBarEl = document.getElementById('her-energy-bar')
    this.playerEnergyBarEl = document.getElementById('player-energy-bar')
    this.herEnergyFillEl = document.getElementById('her-energy-fill')
    this.playerEnergyFillEl = document.getElementById('player-energy-fill')
    this.herEnergyValueEl = document.getElementById('her-energy-value')
    this.playerEnergyValueEl = document.getElementById('player-energy-value')
    this.skillCutinEl = document.getElementById('skill-cutin')
    this.skillCutinStageEl = document.querySelector<HTMLElement>('.skill-cutin-video-stage')
    this.skillCutinVideoEl = document.getElementById('skill-cutin-video') as HTMLVideoElement | null
    this.skillCutinKindEl = document.getElementById('skill-cutin-kind')
    this.skillCutinNameEl = document.getElementById('skill-cutin-name')
    this.bindGameOverPanelDom()

    for (const id of PLAYER_SKILL_ORDER) {
      this.skillSlotEls[id] = document.getElementById(`skill-slot-${id}`) ?? undefined
    }
    this.bindSkillSlotInput()

    this.updateScoreDom()
    this.updateCombatHudDom()
    this.updateSkillDockDom()
  }

  private bindSkillSlotInput(): void {
    this.unbindSkillSlotInput()

    for (const id of PLAYER_SKILL_ORDER) {
      const slotEl = this.skillSlotEls[id]
      if (!slotEl) continue

      slotEl.setAttribute('role', 'button')
      slotEl.setAttribute('aria-label', `释放${SKILL_CONFIGS[id].name}`)

      const onPointerDown = (event: PointerEvent): void => {
        event.preventDefault()
        event.stopPropagation()
        this.tryQueuePlayerSkill(id)
      }

      const onClick = (event: MouseEvent): void => {
        event.preventDefault()
        event.stopPropagation()
      }

      slotEl.addEventListener('pointerdown', onPointerDown)
      slotEl.addEventListener('click', onClick)
      this.skillSlotCleanups.push(() => {
        slotEl.removeEventListener('pointerdown', onPointerDown)
        slotEl.removeEventListener('click', onClick)
      })
    }
  }

  private unbindSkillSlotInput(): void {
    for (const cleanup of this.skillSlotCleanups) {
      cleanup()
    }
    this.skillSlotCleanups = []
  }

  private bindGameOverPanelDom(): void {
    this.gameOverOverlayEl = document.getElementById('game-over-overlay')
    this.gameOverPanelEl = document.getElementById('game-over-panel')
    this.gameOverEvaluationEl = document.getElementById('game-over-evaluation')
    this.gameOverComboEl = document.getElementById('game-over-combo')
    this.gameOverQuoteEl = document.getElementById('game-over-quote')
    this.gameOverReplayEl = document.getElementById('game-over-replay') as HTMLButtonElement | null

    if (this.gameOverReplayEl) {
      this.gameOverReplayEl.onclick = () => {
        window.dispatchEvent(new CustomEvent('ping-pong:replay-click'))
        this.resumeAudio()
        this.maybeAdvanceDifficultyOnReplay()
        this.lastGameOverWinner = null
        this.hasStarted = true
        this.resetGame(true)
      }
    }
  }

  /** 玩家上局赢了的话，再来一局自动跳到下一档难度 */
  private maybeAdvanceDifficultyOnReplay(): void {
    if (this.lastGameOverWinner !== 'p1') return

    const next = this.getNextAiDifficulty()
    if (!next) return

    const label = AI_DIFFICULTY_CONFIGS[next].label
    this.setAiDifficulty(next)
    this.showSkillNotice(`难度升级：${label}`, 0xffd35c)
  }

  private updateScoreDom(): void {
    if (this.herScoreEl) {
      this.herScoreEl.textContent = this.score.p2.toString()
    }

    if (this.playerScoreEl) {
      this.playerScoreEl.textContent = this.score.p1.toString()
    }
  }

  private updateCombatHudDom(): void {
    this.updateCombatantEnergyDom(
      this.herEnergyBarEl,
      this.herEnergyFillEl,
      this.herEnergyValueEl,
      this.p2Energy
    )
    this.updateCombatantEnergyDom(
      this.playerEnergyBarEl,
      this.playerEnergyFillEl,
      this.playerEnergyValueEl,
      this.p1Energy
    )
  }

  private updateCombatantEnergyDom(
    barEl: HTMLElement | null,
    fillEl: HTMLElement | null,
    valueEl: HTMLElement | null,
    energy: number
  ): void {
    const clampedEnergy = Phaser.Math.Clamp(energy, 0, ENERGY_MAX)
    const pct = clampedEnergy / ENERGY_MAX

    if (fillEl) {
      fillEl.style.width = `${Math.round(pct * 1000) / 10}%`
    }

    if (valueEl) {
      valueEl.textContent = `${Math.floor(clampedEnergy)}/${ENERGY_MAX}`
    }

    if (barEl) {
      barEl.classList.toggle('is-full', clampedEnergy >= ENERGY_MAX)
    }
  }

  private updateSkillDockDom(): void {
    for (const id of PLAYER_SKILL_ORDER) {
      this.skillIconImages[id]?.setVisible(false)
      this.skillKeyTexts[id]?.setVisible(false)
      this.skillCostTexts[id]?.setVisible(false)

      const slotEl = this.skillSlotEls[id]
      if (!slotEl) continue

      const config = SKILL_CONFIGS[id]
      const remaining = this.getSkillCooldownRemainingMs('p1', id)
      const cdPct = Phaser.Math.Clamp(remaining / config.cooldownMs, 0, 1)
      const enoughEnergy = this.p1Energy >= config.energyCost
      const onCooldown = remaining > 0
      const selected = this.p1PendingSkill === id
      const available = enoughEnergy && !onCooldown && !this.skillCutinPlaying

      slotEl.classList.toggle('is-selected', selected)
      slotEl.classList.toggle('is-cooldown', onCooldown)
      slotEl.classList.toggle('is-disabled', !available)
      slotEl.classList.toggle('is-energy-low', !enoughEnergy)
      slotEl.style.setProperty('--cd-pct', cdPct.toFixed(3))

      const keyEl = slotEl.querySelector<HTMLElement>('.skill-key')
      const costEl = slotEl.querySelector<HTMLElement>('.skill-cost')
      if (keyEl) keyEl.textContent = ''
      if (costEl) costEl.textContent = config.energyCost.toString()
    }
  }

  private updateSkillHud(): void {
    if (!this.hudGfx) return

    this.hudGfx.clear()
    this.updateCombatHudDom()
    this.updateSkillDockDom()
  }

  private drawCombatantHud(
    side: Side,
    barX: number,
    barY: number,
    barW: number,
    barH: number,
    color: number
  ): void {
    const energy = side === 'p1' ? this.p1Energy : this.p2Energy
    const full = energy >= ENERGY_MAX

    this.drawEnergyBar(barX, barY, barW, barH, energy, color, full)
  }

  private drawEnergyBar(x: number, y: number, w: number, h: number, energy: number, color: number, full: boolean): void {
    const pct = Phaser.Math.Clamp(energy / ENERGY_MAX, 0, 1)

    this.hudGfx.fillStyle(0x050915, 0.92)
    this.hudGfx.fillRoundedRect(x, y, w, h, 4)
    this.hudGfx.lineStyle(1, 0xffffff, 0.18)
    this.hudGfx.strokeRoundedRect(x, y, w, h, 4)

    this.hudGfx.fillStyle(color, 0.82)
    this.hudGfx.fillRoundedRect(x + 2, y + 2, Math.max(0, (w - 4) * pct), h - 4, 3)

    this.hudGfx.lineStyle(1, 0xffffff, 0.2)
    for (let i = 1; i < 4; i++) {
      const segX = x + (w / 4) * i
      this.hudGfx.lineBetween(segX, y + 2, segX, y + h - 2)
    }

    if (full) {
      this.drawFullEnergyPulse(x, y, w, h)
    }
  }

  private drawFullEnergyPulse(x: number, y: number, w: number, h: number): void {
    const phase = this.time.now * 0.018
    const glowAlpha = 0.54 + Math.sin(phase) * 0.18

    this.hudGfx.lineStyle(2, 0xffd76a, glowAlpha)
    this.hudGfx.strokeRoundedRect(x - 3, y - 3, w + 6, h + 6, 6)
    this.hudGfx.lineStyle(1, 0xffffff, 0.26)
    this.hudGfx.lineBetween(x + 5, y + 2, x + w - 5, y + 2)

    const sweepX = x + ((phase * 36) % Math.max(1, w + 42)) - 21
    this.hudGfx.fillStyle(0xfff0a0, 0.18)
    this.hudGfx.fillRoundedRect(Phaser.Math.Clamp(sweepX, x, x + w - 22), y + 2, 22, Math.max(2, h - 4), 3)
  }

  private drawPlayerSkillDock(): void {
    const iconSize = 58
    const gap = 14
    const totalW = PLAYER_SKILL_ORDER.length * iconSize + (PLAYER_SKILL_ORDER.length - 1) * gap
    const startX = this.fieldW / 2 - totalW / 2
    const y = this.fieldH - 96
    for (let index = 0; index < PLAYER_SKILL_ORDER.length; index++) {
      const id = PLAYER_SKILL_ORDER[index]
      this.drawSkillIcon(id, startX + index * (iconSize + gap), y, iconSize)
    }
  }

  private drawSkillIcon(id: PlayerSkillId, x: number, y: number, size: number): void {
    const config = SKILL_CONFIGS[id]
    const remaining = this.getSkillCooldownRemainingMs('p1', id)
    const cdPct = Phaser.Math.Clamp(remaining / config.cooldownMs, 0, 1)
    const selected = this.p1PendingSkill === id
    const enoughEnergy = this.p1Energy >= config.energyCost
    const onCooldown = remaining > 0
    const available = enoughEnergy && !onCooldown
    const alpha = available ? 1 : 0.44
    const pulse = selected ? 0.5 + Math.sin(this.time.now * 0.02) * 0.22 : 0

    this.hudGfx.fillStyle(0x050713, 0.92)
    this.hudGfx.fillRoundedRect(x, y, size, size, 8)
    this.hudGfx.lineStyle(2, config.color, selected ? 0.88 : available ? 0.72 : 0.28)
    this.hudGfx.strokeRoundedRect(x, y, size, size, 8)

    this.hudGfx.lineStyle(1, config.color, 0.35 + pulse)
    this.hudGfx.strokeRoundedRect(x + 4, y + 4, size - 8, size - 8, 5)

    const icon = this.skillIconImages[id]
    if (icon) {
      icon.setPosition(x + size / 2, y + size / 2)
      icon.setDisplaySize(size - 6, size - 6)
      icon.setAlpha(alpha)
      icon.setVisible(true)
      if (available || selected) {
        icon.clearTint()
      } else {
        icon.setTint(0x6b7185)
      }
    }

    if (!available) {
      this.hudGfx.fillStyle(0x000000, onCooldown ? 0.5 : 0.62)
      this.hudGfx.fillRoundedRect(x + 1, y + 1, size - 2, size - 2, 8)
    }

    if (onCooldown) {
      this.hudGfx.fillStyle(0x0b1020, 0.72)
      this.hudGfx.fillRoundedRect(x + 1, y + 1, size - 2, (size - 2) * cdPct, 8)
      this.hudGfx.lineStyle(2, config.color, 0.42)
      this.hudGfx.strokeRoundedRect(x + 6, y + 6, size - 12, size - 12, 5)
    }

    if (selected) {
      this.drawSkillSelectionBeam(x - 5, y - 5, size + 10, size + 10)
    }

    const keyText = this.skillKeyTexts[id]
    if (keyText) {
      keyText.setPosition(x + 13, y + 14)
      keyText.setText(config.keyLabel)
      keyText.setAlpha(available || selected ? 1 : 0.45)
    }

    const costText = this.skillCostTexts[id]
    if (costText) {
      costText.setPosition(x + size - 15, y + 14)
      costText.setText(config.energyCost.toString())
      costText.setColor(enoughEnergy ? '#ffeaa0' : '#8c93aa')
      costText.setAlpha(onCooldown ? 0.45 : 0.92)
    }
  }

  private drawSkillSelectionBeam(x: number, y: number, w: number, h: number): void {
    const perimeter = w * 2 + h * 2
    const head = (this.time.now * 0.52) % perimeter

    this.drawPerimeterBeamSegment(x, y, w, h, head, 64, 0xff8f1f, 5, 0.36)
    this.drawPerimeterBeamSegment(x, y, w, h, head + perimeter * 0.5, 42, 0xffd76a, 3, 0.46)
    this.drawPerimeterBeamSegment(x, y, w, h, head + 6, 34, 0xfff4a3, 3, 0.92)
  }

  private drawPerimeterBeamSegment(
    x: number,
    y: number,
    w: number,
    h: number,
    startDistance: number,
    length: number,
    color: number,
    width: number,
    alpha: number
  ): void {
    const steps = 9
    this.hudGfx.lineStyle(width, color, alpha)
    for (let i = 1; i <= steps; i++) {
      const prev = this.getRectPerimeterPoint(x, y, w, h, startDistance + ((i - 1) / steps) * length)
      const current = this.getRectPerimeterPoint(x, y, w, h, startDistance + (i / steps) * length)
      this.hudGfx.lineBetween(prev.x, prev.y, current.x, current.y)
    }
  }

  private getRectPerimeterPoint(x: number, y: number, w: number, h: number, distance: number): { x: number; y: number } {
    const perimeter = w * 2 + h * 2
    let d = distance % perimeter
    if (d < 0) d += perimeter

    if (d <= w) return { x: x + d, y }
    d -= w
    if (d <= h) return { x: x + w, y: y + d }
    d -= h
    if (d <= w) return { x: x + w - d, y: y + h }
    d -= w
    return { x, y: y + h - d }
  }

  private drawSkillGlyph(id: PlayerSkillId, cx: number, cy: number, size: number, alpha: number): void {
    const color = SKILL_CONFIGS[id].color
    const phase = this.time.now * 0.01

    if (id === 'qianying') {
      this.hudGfx.lineStyle(3, color, 0.72 * alpha)
      for (let i = 0; i < 3; i++) {
        this.hudGfx.beginPath()
        this.hudGfx.arc(cx, cy, size * (0.2 + i * 0.1), phase + i, phase + Math.PI * 1.35 + i)
        this.hudGfx.strokePath()
      }
      this.hudGfx.fillStyle(color, 0.46 * alpha)
      this.hudGfx.fillCircle(cx, cy, size * 0.18)
    } else if (id === 'flame') {
      this.hudGfx.fillStyle(0xffe06d, 0.55 * alpha)
      this.hudGfx.fillCircle(cx - 4, cy + 5, size * 0.24)
      this.hudGfx.fillStyle(color, 0.75 * alpha)
      this.hudGfx.fillTriangle(cx - 19, cy + 14, cx + 18, cy + 13, cx + 8 + Math.sin(phase) * 4, cy - 19)
      this.hudGfx.fillCircle(cx - 2, cy + 3, size * 0.22)
    } else if (id === 'thunder') {
      this.hudGfx.lineStyle(5, color, 0.84 * alpha)
      this.hudGfx.beginPath()
      this.hudGfx.moveTo(cx - 6, cy - 22)
      this.hudGfx.lineTo(cx - 18, cy + 0)
      this.hudGfx.lineTo(cx - 3, cy - 2)
      this.hudGfx.lineTo(cx - 11, cy + 23)
      this.hudGfx.lineTo(cx + 19, cy - 7)
      this.hudGfx.lineTo(cx + 4, cy - 4)
      this.hudGfx.lineTo(cx + 12, cy - 22)
      this.hudGfx.strokePath()
      this.hudGfx.lineStyle(1, 0xffffff, 0.7 * alpha)
      this.hudGfx.strokeCircle(cx, cy, size * 0.23)
    } else if (id === 'light') {
      this.hudGfx.lineStyle(6, color, 0.8 * alpha)
      this.hudGfx.lineBetween(cx - 23, cy + 17, cx + 21, cy - 17)
      this.hudGfx.lineStyle(2, 0xffffff, 0.76 * alpha)
      this.hudGfx.lineBetween(cx - 26, cy + 20, cx + 24, cy - 20)
      this.hudGfx.fillStyle(color, 0.3 * alpha)
      this.hudGfx.fillCircle(cx - 12, cy + 8, size * 0.22)
    }
  }

  private updateComboText(): void {
    if (this.rallyCount < 3) {
      this.comboText.setAlpha(0)
      this.comboText.setText('')
      return
    }

    const isHigh = this.pressureLevel >= 3
    const isMedium = this.pressureLevel === 2
    
    // 动态样式随连击数升级
    const shadowColor = isHigh ? '#ff6600' : isMedium ? '#ff0040' : '#0088ff'
    const tintBottom = isHigh ? 0xffb700 : isMedium ? 0xff4444 : 0x00e5ff
    const size = isHigh ? '56px' : isMedium ? '44px' : '36px'
    const textStr = this.rallyCount >= 10 ? `SUPER COMBO ${this.rallyCount}!!` : `COMBO ${this.rallyCount}!`
    
    this.comboText.setFontSize(size)
    this.comboText.setText(textStr)
    this.comboText.setTint(0xffffff, 0xffffff, tintBottom, tintBottom)
    this.comboText.setShadow(0, 0, shadowColor, isHigh ? 24 : 16, true, true)
    
    this.comboText.setAlpha(1)
    
    // 重置状态
    this.tweens.killTweensOf(this.comboText)
    
    // 充满力量的入场（伴随随机角度倾斜和巨大缩放）
    this.comboText.setScale(1.8 + Math.min(this.rallyCount * 0.05, 1.5))
    this.comboText.setAngle(Phaser.Math.Between(-12, 12))
    
    this.tweens.add({
      targets: this.comboText,
      scale: 1,
      angle: 0,
      duration: 350,
      ease: isHigh ? 'Elastic.easeOut' : 'Back.easeOut',
    })
  }

  private getGameOverEvaluation(winner: Side): GameOverEvaluation {
    const diff = Math.abs(this.score.p1 - this.score.p2)

    if (winner === 'p1') {
      if (diff >= 8) return '完胜'
      if (diff >= 5) return '大胜'
      return '险胜'
    }

    if (diff <= 4) return '惜败'
    if (diff <= 7) return '败北'
    return '惨败'
  }

  private getRandomGameOverQuote(evaluation: GameOverEvaluation): string {
    const quotes = GAME_OVER_EVALUATIONS[evaluation].quotes
    return quotes[Math.floor(Math.random() * quotes.length)] ?? quotes[0]
  }

  private getGameOverResult(winner: Side): GameOverResult {
    const evaluation = this.getGameOverEvaluation(winner)
    const quote = this.getRandomGameOverQuote(evaluation)
    const winnerLabel = winner === 'p1' ? '用户' : 'Kiki'
    const loserLabel = winner === 'p1' ? 'Kiki' : '用户'

    this.lastGameOverWinner = winner

    return {
      winner,
      winnerLabel,
      loserLabel,
      p1: this.score.p1,
      p2: this.score.p2,
      finalScoreText: `用户(P1) ${this.score.p1} : ${this.score.p2} Kiki(P2)`,
      resultForBot: winner === 'p2' ? '你赢了' : '你输了',
      evaluation,
      quote,
      maxRallyCount: this.maxRallyCount,
    }
  }

  /** 返回比当前难度更高一级的难度，已经处于最高难度时返回 null */
  private getNextAiDifficulty(): AiDifficulty | null {
    const ladder = PingPongScene.DIFFICULTY_LADDER
    const currentIdx = ladder.indexOf(this.aiDifficulty)
    if (currentIdx < 0 || currentIdx >= ladder.length - 1) return null
    return ladder[currentIdx + 1]
  }

  private getGameOverQuoteFontSize(quote: string): string {
    const length = Array.from(quote).length
    if (length >= 34) return '10.5px'
    if (length >= 28) return '11px'
    if (length >= 22) return '12px'
    if (length >= 17) return '13px'
    return '14px'
  }

  private fitGameOverQuote(quote: string): void {
    if (!this.gameOverOverlayEl || !this.gameOverQuoteEl) return

    const quoteEl = this.gameOverQuoteEl
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    let fontSize = Number.parseFloat(this.getGameOverQuoteFontSize(quote))
    const minFontSize = 9.5

    quoteEl.textContent = quote
    quoteEl.style.removeProperty('max-height')

    const applySize = (): void => {
      this.gameOverOverlayEl?.style.setProperty('--game-over-quote-size', `${fontSize}px`)
      const style = window.getComputedStyle(quoteEl)
      const lineHeight = Number.parseFloat(style.lineHeight) || fontSize * 1.34
      const paddingY = (Number.parseFloat(style.paddingTop) || 0) + (Number.parseFloat(style.paddingBottom) || 0)
      quoteEl.style.maxHeight = `${Math.ceil(lineHeight * 2 + paddingY)}px`
    }

    for (let i = 0; i < 18; i += 1) {
      applySize()
      if (quoteEl.scrollHeight <= quoteEl.clientHeight + 1 || fontSize <= minFontSize) break
      fontSize = Math.max(minFontSize, fontSize - 0.5)
    }

    applySize()
    if (!prefersReducedMotion) {
      window.requestAnimationFrame(() => applySize())
    }
  }

  private showGameOver(result: GameOverResult): void {
    const { evaluation, quote } = result
    const config = GAME_OVER_EVALUATIONS[evaluation]

    if (!this.gameOverOverlayEl || !this.gameOverEvaluationEl || !this.gameOverComboEl || !this.gameOverQuoteEl) {
      return
    }

    this.gameOverOverlayEl.style.setProperty('--game-over-result-color', config.color)
    this.gameOverOverlayEl.dataset.result = evaluation
    this.gameOverEvaluationEl.textContent = evaluation
    this.gameOverComboEl.textContent = `最高连击数：${result.maxRallyCount}`
    this.fitGameOverQuote(quote)

    this.updateReplayButtonLabel()

    // ===== Hack: 修复重新弹出时的错位问题 =====
    // 某些浏览器在 display: none 后重新展现时，绝对定位结合 css var 的元素可能会因 reflow 延迟导致初始位置偏移。
    // 在这里我们强行清除它现有的 class 并强制触发多次重绘。
    this.gameOverOverlayEl.classList.remove('is-active')
    this.gameOverPanelEl?.classList.remove('is-entered')
    
    // 关键修复 1：先设置为 flex 且 opacity 0 逼迫浏览器排版
    this.gameOverOverlayEl.setAttribute('aria-hidden', 'false')
    this.fitGameOverQuote(quote)
    
    // 关键修复 2：连续两次读取 offsetWidth 强行 Flush 渲染队列，确保排版归位
    void this.gameOverOverlayEl.offsetWidth
    void this.gameOverOverlayEl.offsetHeight

    // 关键修复 3：再加 class 触发动画
    this.gameOverOverlayEl.classList.add('is-active')

    this.time.delayedCall(420, () => {
      this.fitGameOverQuote(quote)
      if (this.gameOver && this.gameOverOverlayEl?.classList.contains('is-active')) {
        this.gameOverReplayEl?.focus()
      }
    })
  }

  /** 根据上局结果与可升级难度，更新"再来一局"按钮的文字 */
  private updateReplayButtonLabel(): void {
    if (!this.gameOverReplayEl) return

    if (this.lastGameOverWinner === 'p1') {
      const next = this.getNextAiDifficulty()
      if (next) {
        const label = AI_DIFFICULTY_CONFIGS[next].label
        this.gameOverReplayEl.textContent = `挑战「${label}」`
        return
      }
    }

    this.gameOverReplayEl.textContent = '再来一局'
  }

  private hideGameOverPanel(): void {
    if (!this.gameOverOverlayEl) return
    this.gameOverOverlayEl.classList.remove('is-active')
    this.gameOverOverlayEl.setAttribute('aria-hidden', 'true')
  }

  private createArenaVideoBackground(): void {
    this.arenaPoster = this.add.image(this.fieldW / 2, this.fieldH / 2, ARENA_BG_POSTER_KEY)
    this.arenaPoster.setOrigin(0.5)
    this.arenaPoster.setDepth(0)
    this.arenaPoster.setDisplaySize(this.fieldW, this.fieldH)

    this.arenaVideo = this.add.video(this.fieldW / 2, this.fieldH / 2, ARENA_BG_VIDEO_KEY)
    this.arenaVideo.setOrigin(0.5)
    this.arenaVideo.setDepth(0.1)
    this.arenaVideo.setVisible(false)
    this.arenaVideo.setMute(true)
    this.arenaVideo.setLoop(false)
    this.arenaVideo.once('textureready', () => {
      this.markArenaVideoReady()
    })
    this.ensureArenaVideoPlaying()

    this.drawArenaOverlay()
  }

  private ensureArenaVideoPlaying(): void {
    if (!this.arenaVideo) return

    this.arenaVideo.setMute(true)
    this.arenaVideo.setLoop(false)
    this.playArenaVideoOnce(false)

    this.time.delayedCall(120, () => {
      if (!this.arenaVideo || this.arenaVideo.visible) return

      const htmlVideo = this.arenaVideo.video
      if (this.arenaVideo.frameReady || (htmlVideo && htmlVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA)) {
        this.markArenaVideoReady()
      }
    })
  }

  private markArenaVideoReady(): void {
    if (!this.arenaVideo || this.arenaVideoReady) return

    this.fitArenaVideoBackground()
    this.arenaVideo.setVisible(true)
    this.startArenaReplayTimer()
    this.arenaVideoReady = true
    window.dispatchEvent(new CustomEvent('ping-pong:arena-ready'))

    if (this.pendingStartAfterArenaReady) {
      this.pendingStartAfterArenaReady = false
      this.startMatch()
    }
  }

  private requestStartMatch(): void {
    this.resumeAudio()
    this.ensureArenaVideoPlaying()

    if (!this.arenaVideoReady) {
      this.pendingStartAfterArenaReady = true
      return
    }

    this.startMatch()
  }

  private playArenaVideoOnce(restart: boolean): void {
    if (!this.arenaVideo) return

    this.arenaVideo.setMute(true)
    this.arenaVideo.setLoop(false)

    const htmlVideo = this.arenaVideo.video
    if (htmlVideo) {
      htmlVideo.loop = false
      htmlVideo.muted = true
      htmlVideo.playsInline = true

      if (restart) {
        try {
          htmlVideo.currentTime = 0
        } catch {
          // Some mobile browsers only allow seeking after metadata is ready.
        }
      } else if (!htmlVideo.paused && !htmlVideo.ended) {
        return
      }
    }

    this.arenaVideo.play(false)
  }

  private startArenaReplayTimer(): void {
    if (this.arenaReplayTimer) return

    this.arenaReplayTimer = this.time.addEvent({
      delay: ARENA_BG_REPLAY_INTERVAL_MS,
      loop: true,
      callback: () => this.playArenaVideoOnce(true),
    })
  }

  private fitArenaVideoBackground(): void {
    const videoWidth = this.arenaVideo.video?.videoWidth || this.arenaVideo.width || this.fieldW
    const videoHeight = this.arenaVideo.video?.videoHeight || this.arenaVideo.height || this.fieldH
    const scale = this.fieldH / videoHeight

    this.arenaVideo.setPosition(this.fieldW / 2, this.fieldH / 2)
    this.arenaVideo.setScale(scale)
  }

  private drawArenaOverlay(): void {
    const gfx = this.add.graphics()
    gfx.setDepth(1)

    gfx.lineStyle(1, 0x1b315d, 0.08)
    for (let y = 18; y < this.fieldH; y += 18) {
      gfx.lineBetween(0, y, this.fieldW, y)
    }

    gfx.lineStyle(1, 0x2368b8, 0.12)
    for (let x = 32; x < this.fieldW / 2 - 44; x += 86) {
      gfx.lineBetween(x, 72, x + 48, 72)
      gfx.lineBetween(x + 48, 72, x + 76, 92)
    }

    gfx.lineStyle(1, 0xb32635, 0.12)
    for (let x = this.fieldW / 2 + 44; x < this.fieldW - 80; x += 86) {
      gfx.lineBetween(x, this.fieldH - 72, x + 48, this.fieldH - 72)
      gfx.lineBetween(x + 48, this.fieldH - 72, x + 76, this.fieldH - 92)
    }
  }

  private drawCenterLine(): void {
    const gfx = this.add.graphics()
    gfx.setDepth(1.2)
    gfx.lineStyle(1, CENTER_LINE_COLOR, 0.2)
    gfx.lineBetween(this.fieldW / 2, 40, this.fieldW / 2, this.fieldH - 40)
  }

  private drawPaddleEffects(): void {
    this.paddleFxGfx.clear()
    this.drawSinglePaddleEffect(this.p1Paddle, 0xff5266, this.p1Charge, 1, this.p1PendingSkill)
    this.drawSinglePaddleEffect(this.p2Paddle, 0x42ecff, this.p2Charge, -1, this.p2PendingSkill)
  }

  private drawSinglePaddleEffect(
    paddle: Phaser.GameObjects.Image,
    color: number,
    charge: number,
    side: 1 | -1,
    pendingSkill: SkillId | null
  ): void {
    const pulse = pendingSkill ? 0.42 + Math.sin(this.time.now * 0.012) * 0.12 : 0
    const effectCharge = Math.max(charge, pulse)
    const effectColor = pendingSkill ? SKILL_CONFIGS[pendingSkill].color : color

    if (effectCharge > 0.02) {
      const visualWidth = Math.max(paddle.displayWidth, PADDLE_WIDTH)
      const x = paddle.x - visualWidth / 2 - 3
      const y = paddle.y - PADDLE_VISUAL_HEIGHT / 2 - 4
      const w = visualWidth + 6
      const h = PADDLE_VISUAL_HEIGHT + 8
      const barW = 5
      const barH = PADDLE_HEIGHT * 0.82
      const barX = paddle.x + side * 17 - (side < 0 ? barW : 0)
      const barY = paddle.y - barH / 2

      this.paddleFxGfx.lineStyle(1 + effectCharge * 2, effectColor, 0.24 + effectCharge * 0.34)
      this.paddleFxGfx.strokeRoundedRect(x, y, w, h, 10)
      this.paddleFxGfx.fillStyle(0x000000, 0.34)
      this.paddleFxGfx.fillRoundedRect(barX, barY, barW, barH, 3)
      this.paddleFxGfx.fillStyle(charge > 0.98 ? 0xf9d65d : effectColor, 0.75)
      this.paddleFxGfx.fillRoundedRect(barX, barY + barH * (1 - effectCharge), barW, barH * effectCharge, 3)

      if (pendingSkill) {
        this.paddleFxGfx.lineStyle(2, effectColor, 0.28 + Math.sin(this.time.now * 0.02) * 0.12)
        this.paddleFxGfx.strokeRoundedRect(x - 5, y - 7, w + 10, h + 14, 12)
      }
    }
  }

  private captureTrailPoint(): void {
    this.trailPoints.push({ x: this.ball.x, y: this.ball.y, spin: this.ballSpin })
    const maxTrailPoints =
      this.activeSkill?.id === 'qianying' ? 48 : TRAIL_MAX_POINTS + this.pressureLevel * 6
    while (this.trailPoints.length > maxTrailPoints) {
      this.trailPoints.shift()
    }
  }

  private getBallVisualColor(): number {
    return this.activeSkill?.color ?? BALL_COLOR
  }

  private getTrailVisualColor(): number {
    return this.activeSkill?.color ?? this.ballOwnerColor
  }

  private drawBallTrail(_active: boolean): void {
    this.trailGfx.clear()
    if (this.activeSkill?.id === 'qianying') return
    if (this.trailPoints.length < 2) return
    const color = this.getTrailVisualColor()
    const returnFxTier = this.getCurrentReturnFxTier()

    for (let i = 1; i < this.trailPoints.length; i++) {
      const prev = this.trailPoints[i - 1]
      const current = this.trailPoints[i]
      const t = i / this.trailPoints.length
      const spinAmount = Math.min(Math.abs(current.spin) / SPIN_MAX, 1)
      const skillBoost = this.activeSkill ? 0.32 : 0
      const returnBoost = returnFxTier * 0.13
      const alpha = t * (0.12 + spinAmount * 0.38 + this.pressureLevel * 0.05 + skillBoost + returnBoost)
      const width = 1 + t * (2.35 + this.pressureLevel * 0.7 + (this.activeSkill ? 2.5 : 0) + returnFxTier * 1.35)

      this.trailGfx.lineStyle(width, color, alpha)
      this.trailGfx.lineBetween(prev.x, prev.y, current.x, current.y)

      if (returnFxTier >= 1) {
        this.trailGfx.lineStyle(Math.max(1, width * 0.4), 0xffffff, alpha * (0.32 + returnFxTier * 0.055))
        this.trailGfx.lineBetween(prev.x, prev.y, current.x, current.y)
      }
    }
  }

  private drawSkillEffects(): void {
    this.skillFxGfx.clear()
    const skill = this.activeSkill
    if (!skill) return

    const phase = this.time.now * 0.01
    const color = skill.color

    switch (skill.id) {
      case 'qianying':
        this.drawQianyingSkillEffect(skill, phase, color)
        break
      case 'flame':
        this.drawFlameSkillEffect(phase, color)
        break
      case 'thunder':
        this.drawThunderSkillEffect(skill, phase, color)
        break
      case 'light':
        this.drawLightSkillEffect(skill, color)
        break
      case 'serpent':
        this.drawSerpentSkillEffect(phase, color)
        break
      case 'fold':
        this.drawFoldSkillEffect(skill, phase, color)
        break
      case 'double':
        this.drawDoubleSkillEffect(phase, color)
        break
    }
  }

  private drawQianyingSkillEffect(skill: ActiveSkillState, phase: number, color: number): void {
    const speed = Math.max(Math.hypot(this.ballVx, this.ballVy), 1)
    const forwardX = this.ballVx / speed
    const forwardY = this.ballVy / speed
    const backX = -forwardX
    const backY = -forwardY
    const sideX = -forwardY
    const sideY = forwardX
    const seed = skill.qianyingSeed ?? 4107
    const pulse = 0.5 + Math.sin(phase * 5.2) * 0.5
    const visualTrail = this.getQianyingVisualTrail(backX, backY)
    const flowPoints = this.buildQianyingFlowPoints(
      visualTrail,
      Phaser.Math.Clamp(260 + speed * 0.16, 320, 440),
      7.5
    )

    this.drawQianyingParticleFlow(flowPoints, seed, phase, color, backX, backY, sideX, sideY)
    this.drawQianyingHeadGhosts(seed, phase, pulse, color, forwardX, forwardY, backX, backY, sideX, sideY)
  }

  private getQianyingVisualTrail(backX: number, backY: number): Array<{ x: number; y: number }> {
    const points = this.trailPoints.slice(-46).map((point) => ({ x: point.x, y: point.y }))
    const last = points[points.length - 1]
    if (!last || Math.hypot(last.x - this.ball.x, last.y - this.ball.y) > 1) {
      points.push({ x: this.ball.x, y: this.ball.y })
    }

    if (points.length >= 3) return points

    const synthetic: Array<{ x: number; y: number }> = []
    for (let i = 12; i >= 1; i--) {
      synthetic.push({
        x: this.ball.x + backX * i * 18,
        y: this.ball.y + backY * i * 18,
      })
    }
    synthetic.push({ x: this.ball.x, y: this.ball.y })
    return synthetic
  }

  private buildQianyingFlowPoints(
    points: Array<{ x: number; y: number }>,
    maxDistance: number,
    spacing: number
  ): Array<{ x: number; y: number; age: number; index: number }> {
    const samples: Array<{ x: number; y: number; age: number; index: number }> = []
    let traveled = 0

    for (let i = points.length - 1; i > 0 && traveled < maxDistance; i--) {
      const from = points[i]
      const to = points[i - 1]
      const segmentLength = Math.hypot(to.x - from.x, to.y - from.y)
      if (segmentLength < 0.5) continue

      for (let distance = 0; distance <= segmentLength && traveled + distance <= maxDistance; distance += spacing) {
        const t = distance / segmentLength
        samples.push({
          x: Phaser.Math.Linear(from.x, to.x, t),
          y: Phaser.Math.Linear(from.y, to.y, t),
          age: Phaser.Math.Clamp((traveled + distance) / maxDistance, 0, 1),
          index: samples.length,
        })
      }

      traveled += segmentLength
    }

    return samples.reverse()
  }

  private drawQianyingParticleFlow(
    points: Array<{ x: number; y: number; age: number; index: number }>,
    seed: number,
    phase: number,
    color: number,
    backX: number,
    backY: number,
    sideX: number,
    sideY: number
  ): void {
    if (points.length === 0) return

    const lanes = [-1.35, -0.72, -0.22, 0.22, 0.72, 1.35]
    const baseRadius = BALL_RADIUS * 0.9

    for (const point of points) {
      const life = Math.pow(1 - point.age, 1.18)
      const spread = 8 + point.age * 21
      const streamFlicker = 0.84 + this.qianyingNoise(seed + point.index * 71 + Math.floor(phase * 8) * 19) * 0.28

      for (let laneIndex = 0; laneIndex < lanes.length; laneIndex++) {
        const lane = lanes[laneIndex]
        const laneWeight = laneIndex === 2 || laneIndex === 3 ? 1 : 0.72
        const jitter = this.qianyingSignedNoise(seed + point.index * 43 + laneIndex * 137) * (3.5 + point.age * 5)
        const wave = Math.sin(phase * 1.5 + point.index * 0.17 + laneIndex * 1.9) * (2 + point.age * 5)
        const x = point.x + sideX * (lane * spread + jitter + wave) + backX * this.qianyingSignedNoise(seed + point.index * 97) * 4
        const y = point.y + sideY * (lane * spread + jitter + wave) + backY * this.qianyingSignedNoise(seed + point.index * 101) * 4
        const alpha = Phaser.Math.Clamp((0.05 + life * 0.22) * laneWeight * streamFlicker, 0, 0.3)
        const mainColor = point.age < 0.28 ? 0xf0a0ff : point.age < 0.64 ? color : 0x7b24c8

        this.skillFxGfx.fillStyle(0x2b0745, alpha * 0.62)
        this.skillFxGfx.fillCircle(x, y, baseRadius * 1.45)
        this.skillFxGfx.fillStyle(mainColor, alpha)
        this.skillFxGfx.fillCircle(x, y, baseRadius)

        if (laneIndex === 2 || laneIndex === 3) {
          this.skillFxGfx.fillStyle(0xffc6ff, alpha * 0.45)
          this.skillFxGfx.fillCircle(x, y, baseRadius * 0.46)
        }
      }
    }
  }

  private drawQianyingHeadGhosts(
    seed: number,
    phase: number,
    pulse: number,
    color: number,
    forwardX: number,
    forwardY: number,
    backX: number,
    backY: number,
    sideX: number,
    sideY: number
  ): void {
    const ballRadius = BALL_RADIUS * 1.42
    const switchPhase = phase * 1.08
    const switchIndex = Math.floor(switchPhase)
    const switchProgress = switchPhase - switchIndex
    const trueIndex = ((seed + switchIndex * 5) % 9 + 9) % 9
    const previousTrueIndex = ((seed + (switchIndex - 1) * 5) % 9 + 9) % 9
    const ringRotation = phase * 0.42 + seed * 0.0007
    const ringLong = 42 + Math.sin(phase * 1.7) * 3
    const ringSide = 47 + Math.cos(phase * 1.3) * 3
    const positions: Array<{ x: number; y: number }> = [{ x: this.ball.x, y: this.ball.y }]

    for (let i = 0; i < 8; i++) {
      const angle = ringRotation + (Math.PI * 2 * i) / 8
      const longOffset = Math.sin(angle) * ringLong
      const sideOffset = Math.cos(angle) * ringSide
      positions.push({
        x: this.ball.x + backX * longOffset + sideX * sideOffset,
        y: this.ball.y + backY * longOffset + sideY * sideOffset,
      })
    }

    if (switchProgress < 0.34) {
      const from = positions[previousTrueIndex]
      const to = positions[trueIndex]
      const alpha = (1 - switchProgress / 0.34) * 0.32
      this.skillFxGfx.lineStyle(15, color, alpha * 0.3)
      this.skillFxGfx.lineBetween(from.x, from.y, to.x, to.y)
      this.skillFxGfx.lineStyle(5, 0xffa8ff, alpha)
      this.skillFxGfx.lineBetween(from.x, from.y, to.x, to.y)
    }

    for (let i = positions.length - 1; i >= 0; i--) {
      if (i === trueIndex) continue
      const point = positions[i]
      const nearTruth =
        i === previousTrueIndex ||
        i === ((trueIndex + 1) % positions.length) ||
        i === ((trueIndex + positions.length - 1) % positions.length)
      const alpha = nearTruth ? 0.42 : 0.26

      this.skillFxGfx.fillStyle(0x5b1484, alpha * 0.22)
      this.skillFxGfx.fillCircle(point.x, point.y, ballRadius * 1.28)
      this.skillFxGfx.fillStyle(color, alpha)
      this.skillFxGfx.fillCircle(point.x, point.y, ballRadius)
      this.skillFxGfx.fillStyle(0xf1a0ff, alpha * 0.28)
      this.skillFxGfx.fillCircle(point.x - forwardX * 2, point.y - forwardY * 2, ballRadius * 0.42)
    }

    const trueBall = positions[trueIndex]
    this.skillFxGfx.fillStyle(0x3f005b, 0.34 + pulse * 0.08)
    this.skillFxGfx.fillCircle(trueBall.x, trueBall.y, ballRadius * 1.86)
    this.skillFxGfx.fillStyle(0x7a0eac, 0.76)
    this.skillFxGfx.fillCircle(trueBall.x, trueBall.y, ballRadius * 1.24)
    this.skillFxGfx.fillStyle(color, 0.96)
    this.skillFxGfx.fillCircle(trueBall.x, trueBall.y, ballRadius)
    this.skillFxGfx.fillStyle(0xffb8ff, 0.82)
    this.skillFxGfx.fillCircle(trueBall.x - forwardX * 3, trueBall.y - forwardY * 3, ballRadius * 0.45)
  }

  private qianyingNoise(seed: number): number {
    const x = Math.sin(seed * 12.9898) * 43758.5453
    return x - Math.floor(x)
  }

  private qianyingSignedNoise(seed: number): number {
    return this.qianyingNoise(seed) * 2 - 1
  }

  private drawFlameSkillEffect(phase: number, color: number): void {
    const speed = Math.max(Math.hypot(this.ballVx, this.ballVy), 1)
    const forwardX = this.ballVx / speed
    const forwardY = this.ballVy / speed
    const backX = -forwardX
    const backY = -forwardY
    const sideX = -forwardY
    const sideY = forwardX
    const pulse = 0.5 + Math.sin(phase * 4.6) * 0.5
    const tailLength = Phaser.Math.Clamp(210 + speed * 0.14, 260, 390)
    const rotation = Math.atan2(backY, backX)
    const corePoints: Array<{ x: number; y: number }> = []

    for (let i = 0; i <= 13; i++) {
      const t = i / 13
      const wave = Math.sin(phase * 2.05 + i * 0.86) * (5 + t * 28)
      const curl = Math.cos(phase * 3.8 + i * 1.27) * (2 + t * 12)
      corePoints.push({
        x: this.ball.x + backX * tailLength * t + sideX * (wave + curl),
        y: this.ball.y + backY * tailLength * t + sideY * (wave + curl),
      })
    }

    for (let i = 18; i >= 0; i--) {
      const t = i / 18
      const heat = 1 - t
      const distance = 12 + tailLength * t
      const sway = Math.sin(phase * 2.8 + i * 1.39) * (8 + t * 44)
      const churn = Math.cos(phase * 4.4 + i * 0.91) * (5 + t * 18)
      const x = this.ball.x + backX * distance + sideX * (sway + churn)
      const y = this.ball.y + backY * distance + sideY * (sway + churn)
      const longSize = 42 + heat * 128 + Math.sin(phase * 3.1 + i) * 9
      const crossSize = 20 + heat * 84 + Math.cos(phase * 2.7 + i) * 7

      this.drawRotatedFlameEllipse(x, y, longSize * 1.38, crossSize * 1.28, rotation, 0x4a0300, 0.08 + heat * 0.1)
      this.drawRotatedFlameEllipse(x, y, longSize * 1.08, crossSize, rotation, 0xb40b00, 0.12 + heat * 0.12)
      this.drawRotatedFlameEllipse(x - forwardX * 6, y - forwardY * 6, longSize * 0.7, crossSize * 0.58, rotation, color, 0.16 + heat * 0.16)

      if (i < 12) {
        this.drawRotatedFlameEllipse(
          x - forwardX * 12,
          y - forwardY * 12,
          longSize * 0.36,
          crossSize * 0.27,
          rotation,
          0xffd95f,
          0.16 + heat * 0.18
        )
      }
    }

    this.drawFlamePolyline(corePoints, 42, 0x5a0400, 0.05, false)
    this.drawFlamePolyline(corePoints, 24, 0xff4d00, 0.1 + pulse * 0.02, false)
    this.drawFlamePolyline(corePoints.slice(0, 9), 9, 0xffd96a, 0.22, false)
    this.drawFlamePolyline(corePoints.slice(0, 6), 3, 0xffffff, 0.42, false)

    const recentPoints = this.trailPoints.slice(-20)
    if (recentPoints.length >= 2) {
      this.drawFlamePolyline(recentPoints, 38, 0x8d0900, 0.12)
      this.drawFlamePolyline(recentPoints, 21, 0xff5000, 0.2)
      this.drawFlamePolyline(recentPoints, 8, 0xfff1a6, 0.28)
    }

    for (let lane = -1; lane <= 1; lane += 2) {
      for (let i = 0; i < 13; i++) {
        const t = i / 12
        const heat = 1 - t
        const distance = 18 + tailLength * (0.12 + t * 0.72)
        const offset = lane * (20 + t * 76 + Math.sin(phase * 3.2 + i * 1.65 + lane) * (14 + t * 20))
        const x = this.ball.x + backX * distance + sideX * offset
        const y = this.ball.y + backY * distance + sideY * offset
        const longSize = 30 + heat * 62
        const crossSize = 7 + heat * 24

        this.drawRotatedFlameEllipse(x, y, longSize, crossSize, rotation + lane * 0.35, 0xff3a00, 0.12 + heat * 0.12)
        this.drawRotatedFlameEllipse(
          x - forwardX * 5,
          y - forwardY * 5,
          longSize * 0.55,
          crossSize * 0.44,
          rotation + lane * 0.35,
          0xffe27a,
          0.13 + heat * 0.14
        )
      }
    }

    for (let i = 0; i < 54; i++) {
      const t = (i % 27) / 26
      const lane = i % 2 === 0 ? 1 : -1
      const ageFade = 1 - t
      const distance = 22 + tailLength * (0.06 + t * 0.95)
      const spread = lane * (Math.sin(phase * 4.7 + i * 0.74) * (16 + t * 66) + Math.cos(i * 2.19) * 10)
      const x = this.ball.x + backX * distance + sideX * spread
      const y = this.ball.y + backY * distance + sideY * spread
      const radius = Phaser.Math.Clamp(3 + ageFade * 13 + Math.sin(phase * 5.1 + i) * 2.5, 2, 18)
      const particleColor = t < 0.16 ? 0xfff4b5 : t < 0.36 ? 0xffd45c : t < 0.66 ? 0xff7600 : 0x9f0c00
      const alpha = Phaser.Math.Clamp(0.06 + ageFade * 0.28, 0.04, 0.34)

      this.skillFxGfx.fillStyle(particleColor, alpha)
      this.skillFxGfx.fillCircle(x, y, radius)
    }

    for (let i = 0; i < 14; i++) {
      const t = i / 13
      const distance = 42 + tailLength * t
      const offset = (i % 2 === 0 ? 1 : -1) * (42 + Math.sin(phase * 3 + i) * 26)
      const sparkX = this.ball.x + backX * distance + sideX * offset
      const sparkY = this.ball.y + backY * distance + sideY * offset
      const sparkEndX = sparkX + backX * (28 + i * 5) + sideX * Math.sin(i) * 8
      const sparkEndY = sparkY + backY * (28 + i * 5) + sideY * Math.sin(i) * 8
      const alpha = 0.28 * (1 - t)

      this.drawFlamePolyline([{ x: sparkX, y: sparkY }, { x: sparkEndX, y: sparkEndY }], 5, 0xff9d18, alpha, false)
      this.drawFlamePolyline([{ x: sparkX, y: sparkY }, { x: sparkEndX, y: sparkEndY }], 1.5, 0xffffff, alpha * 1.35, false)
    }

    this.skillFxGfx.fillStyle(0xb70900, 0.3 + pulse * 0.08)
    this.skillFxGfx.fillCircle(this.ball.x, this.ball.y, BALL_RADIUS + 38)
    this.skillFxGfx.fillStyle(0xff4e00, 0.5)
    this.skillFxGfx.fillCircle(this.ball.x, this.ball.y, BALL_RADIUS + 27)
    this.skillFxGfx.fillStyle(0xffd867, 0.74)
    this.skillFxGfx.fillCircle(this.ball.x - forwardX * 4, this.ball.y - forwardY * 4, BALL_RADIUS + 15 + pulse * 4)
    this.skillFxGfx.fillStyle(0xffffff, 0.9)
    this.skillFxGfx.fillCircle(this.ball.x - forwardX * 9, this.ball.y - forwardY * 9, BALL_RADIUS + 8)
    this.skillFxGfx.lineStyle(4, 0xfff1a6, 0.82)
    this.skillFxGfx.strokeCircle(this.ball.x, this.ball.y, BALL_RADIUS + 12 + pulse * 3)
  }

  private drawRotatedFlameEllipse(
    x: number,
    y: number,
    width: number,
    height: number,
    rotation: number,
    color: number,
    alpha: number
  ): void {
    if (alpha <= 0 || width <= 0 || height <= 0) return

    this.skillFxGfx.save()
    this.skillFxGfx.translateCanvas(x, y)
    this.skillFxGfx.rotateCanvas(rotation)
    this.skillFxGfx.fillStyle(color, alpha)
    this.skillFxGfx.fillEllipse(0, 0, width, height, 24)
    this.skillFxGfx.restore()
  }

  private drawFlamePolyline(
    points: Array<{ x: number; y: number }>,
    width: number,
    color: number,
    alpha: number,
    cap = true
  ): void {
    if (points.length < 2 || alpha <= 0) return

    this.skillFxGfx.lineStyle(width, color, alpha)
    this.skillFxGfx.beginPath()
    this.skillFxGfx.moveTo(points[0].x, points[0].y)
    for (let i = 1; i < points.length; i++) {
      this.skillFxGfx.lineTo(points[i].x, points[i].y)
    }
    this.skillFxGfx.strokePath()

    if (!cap || width < 6) return

    this.skillFxGfx.fillStyle(color, alpha)
    this.skillFxGfx.fillCircle(points[0].x, points[0].y, width * 0.45)
    const end = points[points.length - 1]
    this.skillFxGfx.fillCircle(end.x, end.y, width * 0.45)
  }

  private drawThunderSkillEffect(skill: ActiveSkillState, phase: number, color: number): void {
    const speed = Math.max(Math.hypot(this.ballVx, this.ballVy), 1)
    const forwardX = this.ballVx / speed
    const forwardY = this.ballVy / speed
    const backX = -forwardX
    const backY = -forwardY
    const sideX = -forwardY
    const sideY = forwardX
    const seed = skill.thunderSeed ?? 9143
    const pulse = 0.5 + Math.sin(phase * 4.8) * 0.5
    const baseTrail = this.trailPoints.slice(-17).map((point) => ({ x: point.x, y: point.y }))
    const lastTrail = baseTrail[baseTrail.length - 1]

    if (!lastTrail || Math.hypot(lastTrail.x - this.ball.x, lastTrail.y - this.ball.y) > 1) {
      baseTrail.push({ x: this.ball.x, y: this.ball.y })
    }

    if (baseTrail.length < 2) {
      baseTrail.unshift({
        x: this.ball.x + backX * 120,
        y: this.ball.y + backY * 120,
      })
    }

    let trailDistance = 0
    for (let i = 1; i < baseTrail.length; i++) {
      trailDistance += Math.hypot(baseTrail[i].x - baseTrail[i - 1].x, baseTrail[i].y - baseTrail[i - 1].y)
    }

    if (trailDistance < 300) {
      baseTrail.length = 0
      const wakeLength = Phaser.Math.Clamp(360 + speed * 0.09, 430, 640)
      for (let i = 9; i >= 0; i--) {
        const t = i / 9
        const flare = Math.sin(phase * 2.4 + i * 1.37) * (12 + t * 30)
        const snap = this.thunderSignedNoise(seed + Math.floor(phase * 12) * 41 + i * 19) * (8 + t * 46)
        baseTrail.push({
          x: this.ball.x + backX * wakeLength * t + sideX * (flare + snap),
          y: this.ball.y + backY * wakeLength * t + sideY * (flare + snap),
        })
      }
    }

    const electricPath = this.buildThunderElectricPath(baseTrail, phase, seed, 24)

    for (let i = 0; i < electricPath.length; i++) {
      const t = i / Math.max(electricPath.length - 1, 1)
      const radius = 22 + t * 28 + Math.sin(phase * 3.1 + i) * 3
      this.skillFxGfx.fillStyle(0x005dff, (0.035 + t * 0.055) * (0.8 + pulse * 0.35))
      this.skillFxGfx.fillCircle(electricPath[i].x, electricPath[i].y, radius)
    }

    this.drawThunderTaperedPolyline(electricPath, 86, 20, 0x003dff, 0.1 + pulse * 0.03)
    this.drawThunderTaperedPolyline(electricPath, 62, 15, 0x007eff, 0.18 + pulse * 0.04)
    this.drawThunderTaperedPolyline(electricPath, 40, 10, 0x48d8ff, 0.36)
    this.drawThunderTaperedPolyline(electricPath, 22, 5, 0xdbfbff, 0.74)
    this.drawThunderTaperedPolyline(electricPath, 10, 2, 0xffffff, 1)

    this.drawThunderBranchBolts(electricPath, phase, seed + 17)
    this.drawThunderBranchBolts(electricPath, phase + 1.9, seed + 71)

    for (let i = 0; i < 16; i++) {
      const t = i / 15
      const distance = 32 + t * Phaser.Math.Clamp(260 + speed * 0.09, 320, 500)
      const offset = this.thunderSignedNoise(seed + i * 23 + Math.floor(phase * 10) * 19) * (34 + t * 78)
      const x = this.ball.x + backX * distance + sideX * offset
      const y = this.ball.y + backY * distance + sideY * offset
      const length = 40 + this.thunderNoise(seed + i * 37) * 86
      const streak = [
        { x, y },
        {
          x: x + backX * length + sideX * this.thunderSignedNoise(seed + i * 41) * 16,
          y: y + backY * length + sideY * this.thunderSignedNoise(seed + i * 43) * 16,
        },
      ]
      const alpha = (1 - t) * 0.22

      this.drawThunderPolyline(streak, 5, 0x0b79ff, alpha * 0.45, false)
      this.drawThunderPolyline(streak, 2, 0xdbfbff, alpha, false)
    }

    const route = skill.thunderRoute
    if (route && route.length > 1) {
      const index = Phaser.Math.Clamp(skill.thunderRouteIndex ?? 0, 0, route.length - 2)
      const next = route[index + 1]
      const ahead = this.buildThunderElectricPath(
        [
          { x: this.ball.x, y: this.ball.y },
          {
            x: Phaser.Math.Linear(this.ball.x, next.x, 0.38),
            y: Phaser.Math.Linear(this.ball.y, next.y, 0.38),
          },
        ],
        phase + 3.1,
        seed + 233,
        12
      )

      this.drawThunderTaperedPolyline(ahead, 30, 4, 0x0089ff, 0.1)
      this.drawThunderTaperedPolyline(ahead, 10, 2, 0xe9ffff, 0.55)
    }

    for (let i = 0; i < 6; i++) {
      const ringRadius = BALL_RADIUS + 15 + i * 8 + Math.sin(phase * 6 + i) * 3
      const alpha = Math.max(0, 0.18 - i * 0.028)
      this.skillFxGfx.lineStyle(i < 2 ? 3 : 1, i % 2 === 0 ? 0xffffff : 0x5ee9ff, alpha)
      this.skillFxGfx.strokeCircle(this.ball.x, this.ball.y, ringRadius)
    }

    for (let i = 0; i < 10; i++) {
      const angle = phase * 1.8 + i * 0.63 + this.thunderSignedNoise(seed + i) * 0.4
      const inner = BALL_RADIUS + 18
      const outer = inner + 22 + this.thunderNoise(seed + i * 9) * 28
      this.drawThunderPolyline(
        [
          { x: this.ball.x + Math.cos(angle) * inner, y: this.ball.y + Math.sin(angle) * inner },
          { x: this.ball.x + Math.cos(angle) * outer, y: this.ball.y + Math.sin(angle) * outer },
        ],
        3,
        0x9ff7ff,
        0.22 + pulse * 0.14,
        false
      )
    }

    this.skillFxGfx.fillStyle(0x005bff, 0.22 + pulse * 0.06)
    this.skillFxGfx.fillCircle(this.ball.x, this.ball.y, BALL_RADIUS + 38)
    this.skillFxGfx.fillStyle(0x37cbff, 0.38)
    this.skillFxGfx.fillCircle(this.ball.x, this.ball.y, BALL_RADIUS + 25)
    this.skillFxGfx.fillStyle(0xffffff, 0.96)
    this.skillFxGfx.fillCircle(this.ball.x, this.ball.y, BALL_RADIUS + 8)
    this.drawThunderPolyline(
      [
        { x: this.ball.x + backX * 8, y: this.ball.y + backY * 8 },
        { x: this.ball.x + forwardX * 34 + sideX * 7, y: this.ball.y + forwardY * 34 + sideY * 7 },
        { x: this.ball.x + forwardX * 56, y: this.ball.y + forwardY * 56 },
        { x: this.ball.x + forwardX * 34 - sideX * 7, y: this.ball.y + forwardY * 34 - sideY * 7 },
      ],
      5,
      0xffffff,
      0.74,
      false
    )
  }

  private buildThunderElectricPath(
    basePoints: Array<{ x: number; y: number }>,
    phase: number,
    seed: number,
    amplitude: number
  ): Array<{ x: number; y: number }> {
    const points: Array<{ x: number; y: number }> = []
    const flicker = Math.floor(phase * 9)

    for (let i = 0; i < basePoints.length - 1; i++) {
      const a = basePoints[i]
      const b = basePoints[i + 1]
      const dx = b.x - a.x
      const dy = b.y - a.y
      const length = Math.max(Math.hypot(dx, dy), 1)
      const normalX = -dy / length
      const normalY = dx / length
      const steps = Phaser.Math.Clamp(Math.ceil(length / 45), 1, 6)

      for (let step = 0; step < steps; step++) {
        if (i > 0 || step > 0) {
          const u = step / steps
          const t = (i + u) / Math.max(basePoints.length - 1, 1)
          const edge = Math.sin(t * Math.PI)
          const jitter = this.thunderSignedNoise(seed + flicker * 97 + i * 31 + step * 13) * amplitude * (0.45 + edge * 0.9)
          const wave = Math.sin(phase * 3.7 + i * 1.9 + step * 2.4) * amplitude * 0.28 * edge
          points.push({
            x: Phaser.Math.Linear(a.x, b.x, u) + normalX * (jitter + wave),
            y: Phaser.Math.Linear(a.y, b.y, u) + normalY * (jitter + wave),
          })
        } else {
          points.push({ x: a.x, y: a.y })
        }
      }
    }

    points.push(basePoints[basePoints.length - 1])
    return points
  }

  private drawThunderTaperedPolyline(
    points: Array<{ x: number; y: number }>,
    maxWidth: number,
    minWidth: number,
    color: number,
    alpha: number
  ): void {
    if (points.length < 2 || alpha <= 0) return

    for (let i = 1; i < points.length; i++) {
      const t = i / (points.length - 1)
      const width = Phaser.Math.Linear(minWidth, maxWidth, Math.pow(t, 1.35))
      const segmentAlpha = alpha * Phaser.Math.Clamp(0.18 + t * 0.92, 0, 1)
      this.skillFxGfx.lineStyle(width, color, segmentAlpha)
      this.skillFxGfx.lineBetween(points[i - 1].x, points[i - 1].y, points[i].x, points[i].y)
      if (width >= 4) {
        this.skillFxGfx.fillStyle(color, segmentAlpha * 0.8)
        this.skillFxGfx.fillCircle(points[i].x, points[i].y, width * 0.48)
      }
    }
  }

  private drawThunderBranchBolts(points: Array<{ x: number; y: number }>, phase: number, seed: number): void {
    if (points.length < 5) return
    const flicker = Math.floor(phase * 11)

    for (let i = 2; i < points.length - 2; i += 3) {
      const t = i / Math.max(points.length - 1, 1)
      const gate = this.thunderNoise(seed + flicker * 53 + i * 29)
      if (gate < 0.22) continue

      const prev = points[i - 1]
      const current = points[i]
      const next = points[i + 1]
      const dx = next.x - prev.x
      const dy = next.y - prev.y
      const length = Math.max(Math.hypot(dx, dy), 1)
      const normalX = -dy / length
      const normalY = dx / length
      const tangentX = dx / length
      const tangentY = dy / length
      const sign = this.thunderSignedNoise(seed + i * 47) >= 0 ? 1 : -1
      const branchLength = 30 + this.thunderNoise(seed + i * 61) * 84 * (0.55 + t)
      const fork = 14 + this.thunderNoise(seed + i * 67) * 44
      const branch = [
        { x: current.x, y: current.y },
        {
          x: current.x + normalX * sign * branchLength * 0.48 + tangentX * fork * 0.28,
          y: current.y + normalY * sign * branchLength * 0.48 + tangentY * fork * 0.28,
        },
        {
          x: current.x + normalX * sign * branchLength + tangentX * fork,
          y: current.y + normalY * sign * branchLength + tangentY * fork,
        },
      ]
      const alpha = (0.58 + this.thunderNoise(seed + i * 73) * 0.32) * (0.35 + t * 0.65)

      this.drawThunderPolyline(branch, 13, 0x006bff, alpha * 0.16, false)
      this.drawThunderPolyline(branch, 6, 0x73ebff, alpha * 0.48, false)
      this.drawThunderPolyline(branch, 2, 0xffffff, alpha, false)

      if (this.thunderNoise(seed + i * 83 + flicker) > 0.58) {
        const subSign = -sign
        const sub = [
          branch[1],
          {
            x: branch[1].x + normalX * subSign * branchLength * 0.34 + tangentX * fork * 0.35,
            y: branch[1].y + normalY * subSign * branchLength * 0.34 + tangentY * fork * 0.35,
          },
        ]
        this.drawThunderPolyline(sub, 4, 0x9ff7ff, alpha * 0.46, false)
        this.drawThunderPolyline(sub, 1, 0xffffff, alpha * 0.78, false)
      }
    }
  }

  private thunderNoise(seed: number): number {
    const x = Math.sin(seed * 12.9898) * 43758.5453
    return x - Math.floor(x)
  }

  private thunderSignedNoise(seed: number): number {
    return this.thunderNoise(seed) * 2 - 1
  }

  private drawThunderPolyline(
    points: Array<{ x: number; y: number }>,
    width: number,
    color: number,
    alpha: number,
    cap = true
  ): void {
    if (points.length < 2 || alpha <= 0) return

    this.skillFxGfx.lineStyle(width, color, alpha)
    this.skillFxGfx.beginPath()
    this.skillFxGfx.moveTo(points[0].x, points[0].y)
    for (let i = 1; i < points.length; i++) {
      this.skillFxGfx.lineTo(points[i].x, points[i].y)
    }
    this.skillFxGfx.strokePath()

    if (!cap || width < 4) return

    this.skillFxGfx.fillStyle(color, alpha)
    this.skillFxGfx.fillCircle(points[0].x, points[0].y, width * 0.5)
    const end = points[points.length - 1]
    this.skillFxGfx.fillCircle(end.x, end.y, width * 0.5)
  }

  private drawLightSkillEffect(skill: ActiveSkillState, color: number): void {
    const dir = skill.owner === 'p1' ? -1 : 1
    this.skillFxGfx.lineStyle(5, color, 0.65)
    this.skillFxGfx.lineBetween(this.ball.x - dir * 96, this.ball.y, this.ball.x, this.ball.y)
    this.skillFxGfx.lineStyle(1, 0xffffff, 0.72)
    this.skillFxGfx.lineBetween(this.ball.x - dir * 128, this.ball.y, this.ball.x + dir * 12, this.ball.y)
  }

  private drawSerpentSkillEffect(phase: number, color: number): void {
    const speed = Math.max(Math.hypot(this.ballVx, this.ballVy), 1)
    const forwardX = this.ballVx / speed
    const forwardY = this.ballVy / speed
    const backX = -forwardX
    const backY = -forwardY
    const sideX = -forwardY
    const sideY = forwardX
    const seed = this.activeSkill?.aiSkillSeed ?? 6127
    const trail = this.trailPoints.slice(-34).map((point) => ({ x: point.x, y: point.y }))
    const last = trail[trail.length - 1]

    if (!last || Math.hypot(last.x - this.ball.x, last.y - this.ball.y) > 1) {
      trail.push({ x: this.ball.x, y: this.ball.y })
    }

    const points = this.buildQianyingFlowPoints(
      trail.length >= 2 ? trail : [
        { x: this.ball.x + backX * 150, y: this.ball.y + backY * 150 },
        { x: this.ball.x, y: this.ball.y },
      ],
      Phaser.Math.Clamp(250 + speed * 0.12, 310, 450),
      8.5
    )

    if (points.length < 2) {
      this.skillFxGfx.fillStyle(0x0a8dff, 0.16)
      this.skillFxGfx.fillCircle(this.ball.x, this.ball.y, BALL_RADIUS + 22)
      this.skillFxGfx.fillStyle(0xdbffff, 0.72)
      this.skillFxGfx.fillCircle(this.ball.x, this.ball.y, BALL_RADIUS + 6)
      return
    }

    const lanes = [-1.25, -0.65, -0.18, 0.18, 0.65, 1.25]
    for (const point of points) {
      const life = Math.pow(1 - point.age, 1.22)
      const spread = 7 + point.age * 24

      for (let laneIndex = 0; laneIndex < lanes.length; laneIndex++) {
        const lane = lanes[laneIndex]
        const wobble = Math.sin(phase * 1.25 + point.index * 0.2 + laneIndex * 1.7) * (4 + point.age * 8)
        const drift = this.qianyingSignedNoise(seed + point.index * 59 + laneIndex * 131) * (4 + point.age * 7)
        const x = point.x + sideX * (lane * spread + wobble + drift) + backX * this.qianyingSignedNoise(seed + point.index * 83) * 5
        const y = point.y + sideY * (lane * spread + wobble + drift) + backY * this.qianyingSignedNoise(seed + point.index * 89) * 5
        const laneCore = laneIndex === 2 || laneIndex === 3
        const radius = Phaser.Math.Clamp(4 + life * 13 + this.qianyingNoise(seed + point.index * 17 + laneIndex) * 5, 3, 19)
        const alpha = Phaser.Math.Clamp((0.045 + life * 0.18) * (laneCore ? 1 : 0.68), 0.03, 0.24)
        const waterColor = point.age < 0.24 ? 0xd8ffff : laneCore ? 0x49f4ff : point.age < 0.62 ? color : 0x0968d8

        this.skillFxGfx.fillStyle(0x003c93, alpha * 0.55)
        this.skillFxGfx.fillCircle(x, y, radius * 1.55)
        this.skillFxGfx.fillStyle(waterColor, alpha)
        this.skillFxGfx.fillCircle(x, y, radius)
      }
    }

    for (let i = 0; i < 18; i++) {
      const t = i / 17
      const distance = 18 + t * 180
      const offset = this.qianyingSignedNoise(seed + i * 37 + Math.floor(phase * 5) * 11) * (18 + t * 52)
      const bubbleX = this.ball.x + backX * distance + sideX * offset
      const bubbleY = this.ball.y + backY * distance + sideY * offset
      const radius = 2 + this.qianyingNoise(seed + i * 41) * 5
      this.skillFxGfx.fillStyle(0xd9ffff, (1 - t) * 0.22)
      this.skillFxGfx.fillCircle(bubbleX, bubbleY, radius)
    }

    this.skillFxGfx.fillStyle(0x0077ff, 0.18)
    this.skillFxGfx.fillCircle(this.ball.x, this.ball.y, BALL_RADIUS + 28)
    this.skillFxGfx.fillStyle(color, 0.32)
    this.skillFxGfx.fillCircle(this.ball.x, this.ball.y, BALL_RADIUS + 18)
    this.skillFxGfx.fillStyle(0xdfffff, 0.88)
    this.skillFxGfx.fillCircle(this.ball.x - forwardX * 4, this.ball.y - forwardY * 4, BALL_RADIUS + 5)
  }

  private drawFoldSkillEffect(skill: ActiveSkillState, phase: number, color: number): void {
    const trail = this.trailPoints.slice(-22).map((point) => ({ x: point.x, y: point.y }))
    const last = trail[trail.length - 1]
    if (!last || Math.hypot(last.x - this.ball.x, last.y - this.ball.y) > 1) {
      trail.push({ x: this.ball.x, y: this.ball.y })
    }

    const speed = Math.max(Math.hypot(this.ballVx, this.ballVy), 1)
    const backX = -this.ballVx / speed
    const backY = -this.ballVy / speed
    const seed = skill.aiSkillSeed ?? 8129
    const electricPath = this.buildThunderElectricPath(
      trail.length >= 2 ? trail : [
        { x: this.ball.x + backX * 150, y: this.ball.y + backY * 150 },
        { x: this.ball.x, y: this.ball.y },
      ],
      phase + 1.7,
      seed,
      13
    )

    this.drawThunderTaperedPolyline(electricPath, 20, 4, 0x42007a, 0.16)
    this.drawThunderTaperedPolyline(electricPath, 11, 2.5, color, 0.42)
    this.drawThunderTaperedPolyline(electricPath, 4, 1.2, 0xffe6ff, 0.92)
    this.drawFoldPurpleBranches(electricPath, phase, seed + 41)

    if (skill.breakIndex > 0 && skill.elapsed < 0.36) {
      const crack = Phaser.Math.Clamp(1 - (skill.elapsed - 0.23) / 0.13, 0, 1)
      this.skillFxGfx.fillStyle(0x9d4cff, 0.2 * crack)
      this.skillFxGfx.fillCircle(this.ball.x + backX * 46, this.ball.y + backY * 46, 42)
      for (let i = 0; i < 8; i++) {
        const angle = phase * 1.5 + i * 0.785
        const inner = 14 + this.thunderNoise(seed + i * 13) * 10
        const outer = 44 + this.thunderNoise(seed + i * 17) * 38
        const bolt = [
          {
            x: this.ball.x + backX * 48 + Math.cos(angle) * inner,
            y: this.ball.y + backY * 48 + Math.sin(angle) * inner,
          },
          {
            x: this.ball.x + backX * 48 + Math.cos(angle) * outer,
            y: this.ball.y + backY * 48 + Math.sin(angle) * outer,
          },
        ]
        this.drawThunderPolyline(bolt, 2, 0xffecff, 0.52 * crack, false)
      }
    }

    this.skillFxGfx.fillStyle(0x4a006f, 0.18)
    this.skillFxGfx.fillCircle(this.ball.x, this.ball.y, BALL_RADIUS + 20)
    this.skillFxGfx.fillStyle(color, 0.45)
    this.skillFxGfx.fillCircle(this.ball.x, this.ball.y, BALL_RADIUS + 10)
    this.skillFxGfx.fillStyle(0xffecff, 0.9)
    this.skillFxGfx.fillCircle(this.ball.x - backX * 2, this.ball.y - backY * 2, BALL_RADIUS + 3)
  }

  private drawFoldPurpleBranches(points: Array<{ x: number; y: number }>, phase: number, seed: number): void {
    if (points.length < 5) return
    const flicker = Math.floor(phase * 12)

    for (let i = 2; i < points.length - 2; i += 3) {
      const gate = this.thunderNoise(seed + flicker * 47 + i * 19)
      if (gate < 0.45) continue

      const prev = points[i - 1]
      const current = points[i]
      const next = points[i + 1]
      const dx = next.x - prev.x
      const dy = next.y - prev.y
      const length = Math.max(Math.hypot(dx, dy), 1)
      const normalX = -dy / length
      const normalY = dx / length
      const tangentX = dx / length
      const tangentY = dy / length
      const sign = this.thunderSignedNoise(seed + i * 31) >= 0 ? 1 : -1
      const branchLength = 18 + this.thunderNoise(seed + i * 37) * 44
      const fork = 8 + this.thunderNoise(seed + i * 41) * 28
      const branch = [
        { x: current.x, y: current.y },
        {
          x: current.x + normalX * sign * branchLength + tangentX * fork,
          y: current.y + normalY * sign * branchLength + tangentY * fork,
        },
      ]
      const alpha = 0.32 + this.thunderNoise(seed + i * 43) * 0.36

      this.drawThunderPolyline(branch, 5, 0x4b0088, alpha * 0.22, false)
      this.drawThunderPolyline(branch, 2, 0xd483ff, alpha * 0.62, false)
      this.drawThunderPolyline(branch, 1, 0xffecff, alpha, false)
    }
  }

  private drawDoubleSkillEffect(phase: number, color: number): void {
    this.skillFxGfx.lineStyle(3, color, 0.72)
    this.skillFxGfx.strokeCircle(this.ball.x, this.ball.y, BALL_RADIUS + 9 + Math.sin(phase) * 3)
    this.skillFxGfx.fillStyle(color, 0.14)
    this.skillFxGfx.fillCircle(this.ball.x, this.ball.y, BALL_RADIUS + 17)
    for (const fake of this.fakeBalls) {
      for (let i = 1; i < fake.trail.length; i++) {
        const prev = fake.trail[i - 1]
        const current = fake.trail[i]
        const t = i / fake.trail.length
        this.skillFxGfx.lineStyle(2 + t * 3, fake.color, t * 0.48)
        this.skillFxGfx.lineBetween(prev.x, prev.y, current.x, current.y)
      }

      this.skillFxGfx.lineStyle(3, 0xfff2ff, 0.7)
      this.skillFxGfx.strokeCircle(fake.body.x, fake.body.y, BALL_RADIUS + 11 + Math.sin(phase * 1.7) * 3)
      this.skillFxGfx.fillStyle(fake.color, 0.18)
      this.skillFxGfx.fillCircle(fake.body.x, fake.body.y, BALL_RADIUS + 18)
    }
  }

  private drawSpinRing(): void {
    this.spinGfx.clear()
    if (this.activeSkill?.id === 'qianying') return
    const spinAmount = Math.min(Math.abs(this.ballSpin) / SPIN_MAX, 1)
    if (spinAmount < 0.05) return

    const color = this.getBallVisualColor()
    const radius = BALL_RADIUS + 5 + spinAmount * 6
    const phase = this.time.now * 0.008 * Math.sign(this.ballSpin || 1)

    this.spinGfx.lineStyle(2 + spinAmount * 2, color, 0.35 + spinAmount * 0.45)
    this.spinGfx.beginPath()
    this.spinGfx.arc(this.ball.x, this.ball.y, radius, phase, phase + Math.PI * 1.35)
    this.spinGfx.strokePath()
    this.spinGfx.lineStyle(1, 0xffffff, 0.2 + spinAmount * 0.25)
    this.spinGfx.beginPath()
    this.spinGfx.arc(this.ball.x, this.ball.y, radius + 4, phase + Math.PI, phase + Math.PI * 1.65)
    this.spinGfx.strokePath()
  }

  private updateBallAppearance(): void {
    const spinAmount = Math.min(Math.abs(this.ballSpin) / SPIN_MAX, 1)
    this.ball.setAlpha(this.activeSkill?.id === 'qianying' ? 0 : 1)
    this.ball.setFillStyle(this.getBallVisualColor(), 1)
    this.ball.setScale(1 + spinAmount * 0.12 + this.pressureLevel * 0.03 + (this.activeSkill ? 0.18 : 0))
  }

  private flashBall(zone: HitZone, charge: number, returnFxTier = 0): void {
    this.ball.setFillStyle(this.getBallVisualColor(), 1)
    this.ball.setScale(1.28 + charge * 0.4 + returnFxTier * 0.12)
    this.tweens.add({
      targets: this.ball,
      scale: 1,
      duration: 125 + returnFxTier * 16,
      ease: 'Back.easeOut',
    })
  }

  private createReturnFxParticleTextures(): void {
    if (this.textures.exists(RETURN_SPARK_TEXTURE_KEY)) return

    const gfx = this.add.graphics()

    gfx.fillStyle(0xffffff, 1)
    gfx.fillCircle(8, 8, 4)
    gfx.fillStyle(0xffffff, 0.28)
    gfx.fillCircle(8, 8, 7)
    gfx.generateTexture(RETURN_SPARK_TEXTURE_KEY, 16, 16)

    gfx.clear()
    gfx.fillStyle(0xffffff, 1)
    gfx.fillRoundedRect(2, 6, 26, 4, 2)
    gfx.fillStyle(0xffffff, 0.42)
    gfx.fillRoundedRect(0, 5, 30, 6, 3)
    gfx.generateTexture(RETURN_STREAK_TEXTURE_KEY, 32, 16)

    gfx.clear()
    gfx.fillStyle(0xffffff, 0.2)
    gfx.fillCircle(16, 16, 15)
    gfx.fillStyle(0xffffff, 0.54)
    gfx.fillCircle(16, 16, 9)
    gfx.fillStyle(0xffffff, 0.88)
    gfx.fillCircle(16, 16, 4)
    gfx.generateTexture(RETURN_GLOW_TEXTURE_KEY, 32, 32)
    gfx.destroy()
  }

  private createReturnFxEmitters(): void {
    this.createReturnFxParticleTextures()

    this.returnGlowEmitter = this.add.particles(0, 0, RETURN_GLOW_TEXTURE_KEY, { emitting: false })
    this.returnGlowEmitter.setDepth(8.8)
    this.returnGlowEmitter.setBlendMode(Phaser.BlendModes.ADD)

    this.returnSparkEmitter = this.add.particles(0, 0, RETURN_SPARK_TEXTURE_KEY, { emitting: false })
    this.returnSparkEmitter.setDepth(9.4)
    this.returnSparkEmitter.setBlendMode(Phaser.BlendModes.ADD)

    this.returnStreakEmitter = this.add.particles(0, 0, RETURN_STREAK_TEXTURE_KEY, { emitting: false })
    this.returnStreakEmitter.setDepth(9.7)
    this.returnStreakEmitter.setBlendMode(Phaser.BlendModes.ADD)
  }

  private createReturnEmitZone(radius: number): Phaser.Types.GameObjects.Particles.EmitZoneData {
    return {
      type: 'random',
      source: new Phaser.Geom.Circle(0, 0, radius),
    } as Phaser.Types.GameObjects.Particles.EmitZoneData
  }

  private spawnPlayerReturnBurst(zone: HitZone, charge: number, returnFxTier: number): void {
    const color = this.getBallVisualColor()
    const tierT = Phaser.Math.Clamp(returnFxTier / 6.2, 0, 1)
    const chargeT = Phaser.Math.Clamp(charge, 0, 1)
    const zoneT = zone === 'center' ? 0.65 : zone === 'slice' ? 0.86 : 1
    const speed = Math.max(Math.hypot(this.ballVx, this.ballVy), 1)
    const forwardRad = Math.atan2(this.ballVy, this.ballVx)
    const forwardDeg = Phaser.Math.RadToDeg(forwardRad)
    const backDeg = forwardDeg + 180
    const x = this.ball.x
    const y = this.ball.y
    const emitRadius = 7 + returnFxTier * 2.35

    this.returnGlowEmitter.setConfig({
      emitting: false,
      frequency: -1,
      lifespan: { min: 110, max: 185 + returnFxTier * 24 },
      speed: { min: 10, max: 54 + returnFxTier * 18 },
      angle: { min: forwardDeg - 180, max: forwardDeg + 180 },
      scale: { start: 0.46 + tierT * 0.62 + chargeT * 0.16, end: 0 },
      alpha: { start: 0.7 + tierT * 0.22, end: 0 },
      color: [0xffffff, color],
      emitZone: this.createReturnEmitZone(emitRadius),
      blendMode: Phaser.BlendModes.ADD,
    })
    this.returnGlowEmitter.explode(Math.round(7 + returnFxTier * 3), x, y)

    this.returnSparkEmitter.setConfig({
      emitting: false,
      frequency: -1,
      lifespan: { min: 170, max: 265 + returnFxTier * 32 },
      speed: { min: 135 + returnFxTier * 36, max: 300 + returnFxTier * 82 + chargeT * 70 },
      angle: { min: forwardDeg - 40, max: forwardDeg + 40 },
      accelerationX: Math.cos(forwardRad) * (58 + returnFxTier * 34),
      accelerationY: Math.sin(forwardRad) * (58 + returnFxTier * 34),
      scale: { start: 0.42 + tierT * 0.42, end: 0 },
      alpha: { start: 0.92, end: 0 },
      color: [0xffffff, color, 0xff5266],
      emitZone: this.createReturnEmitZone(emitRadius + 2),
      blendMode: Phaser.BlendModes.ADD,
    })
    this.returnSparkEmitter.explode(Math.round(18 + returnFxTier * 8 + zoneT * 7), x, y)

    this.returnStreakEmitter.setConfig({
      emitting: false,
      frequency: -1,
      lifespan: { min: 125, max: 205 + returnFxTier * 24 },
      speed: { min: 250 + returnFxTier * 62, max: 470 + returnFxTier * 118 + speed * 0.045 },
      angle: { min: forwardDeg - 16, max: forwardDeg + 16 },
      rotate: { min: forwardDeg - 5, max: forwardDeg + 5 },
      scaleX: { start: 0.95 + tierT * 1.15 + chargeT * 0.32, end: 0.05 },
      scaleY: { start: 0.34 + tierT * 0.22, end: 0.02 },
      alpha: { start: 0.92 + tierT * 0.08, end: 0 },
      color: [0xffffff, color],
      emitZone: this.createReturnEmitZone(emitRadius * 0.75),
      blendMode: Phaser.BlendModes.ADD,
    })
    this.returnStreakEmitter.explode(Math.round(7 + returnFxTier * 3), x, y)

    this.returnSparkEmitter.setConfig({
      emitting: false,
      frequency: -1,
      lifespan: { min: 135, max: 220 + returnFxTier * 22 },
      speed: { min: 70 + returnFxTier * 18, max: 150 + returnFxTier * 38 },
      angle: { min: backDeg - 34, max: backDeg + 34 },
      scale: { start: 0.28 + tierT * 0.18, end: 0 },
      alpha: { start: 0.68, end: 0 },
      color: [color, 0xff5266],
      emitZone: this.createReturnEmitZone(emitRadius),
      blendMode: Phaser.BlendModes.ADD,
    })
    this.returnSparkEmitter.explode(Math.round(8 + returnFxTier * 3), x, y)

    if (returnFxTier >= 4) {
      this.returnStreakEmitter.setConfig({
        emitting: false,
        frequency: -1,
        lifespan: { min: 95, max: 155 + returnFxTier * 16 },
        speed: { min: 330 + returnFxTier * 72, max: 560 + returnFxTier * 118 },
        angle: { min: forwardDeg - 28, max: forwardDeg + 28 },
        rotate: { min: forwardDeg - 10, max: forwardDeg + 10 },
        scaleX: { start: 1.25 + tierT * 1.35, end: 0.08 },
        scaleY: { start: 0.26 + tierT * 0.18, end: 0.02 },
        alpha: { start: 0.76, end: 0 },
        color: [0xffffff, color, 0xff5266],
        emitZone: this.createReturnEmitZone(emitRadius * 0.5),
        blendMode: Phaser.BlendModes.ADD,
      })
      this.returnStreakEmitter.explode(Math.round(4 + returnFxTier * 2), x, y)
    }
  }

  private spawnHitBurst(paddle: Phaser.GameObjects.Image, zone: HitZone, charge: number): void {
    const isPlayer = paddle === this.p1Paddle
    const color = this.getBallVisualColor()
    const dir = isPlayer ? -1 : 1
    const strength = zone === 'center' ? 1 : zone === 'slice' ? 1.35 : 1.75
    const x = this.ball.x
    const y = this.ball.y

    const ring = this.add.circle(x, y, BALL_RADIUS + 3, color, 0)
    ring.setDepth(9)
    ring.setStrokeStyle(2 + charge * 3, color, 0.86)
    this.tweens.add({
      targets: ring,
      scale: 2.4 + strength + charge,
      alpha: 0,
      duration: 220 + charge * 90,
      ease: 'Quad.easeOut',
      onComplete: () => ring.destroy(),
    })

    const particleCount = Math.round(5 + strength * 3 + charge * 5)
    for (let i = 0; i < particleCount; i++) {
      const angle = Phaser.Math.DegToRad(-42 + (84 / Math.max(1, particleCount - 1)) * i)
      const speed = 44 + Math.random() * 48 + charge * 40
      const particle = this.add.rectangle(x, y, 10 + charge * 9, 2, color, 0.9)
      particle.setDepth(9)
      particle.rotation = angle
      this.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * speed * dir,
        y: y + Math.sin(angle) * speed,
        alpha: 0,
        scaleX: 0.35,
        duration: 180 + Math.random() * 110,
        ease: 'Quad.easeOut',
        onComplete: () => particle.destroy(),
      })
    }
  }

  private spawnSkillBurst(name: string, color: number, strength: number): void {
    const ring = this.add.circle(this.ball.x, this.ball.y, BALL_RADIUS + 4, color, 0)
    ring.setDepth(9.4)
    ring.setStrokeStyle(3 + strength, color, 0.9)
    this.tweens.add({
      targets: ring,
      scale: 3.2 + strength,
      alpha: 0,
      duration: 260 + strength * 90,
      ease: 'Quad.easeOut',
      onComplete: () => ring.destroy(),
    })

    const slashCount = Math.round(8 + strength * 5)
    for (let i = 0; i < slashCount; i++) {
      const angle = (Math.PI * 2 * i) / slashCount + Math.random() * 0.3
      const dist = 44 + Math.random() * 42 + strength * 18
      const slash = this.add.rectangle(this.ball.x, this.ball.y, 22 + strength * 10, 3, color, 0.82)
      slash.setDepth(9.2)
      slash.rotation = angle
      this.tweens.add({
        targets: slash,
        x: this.ball.x + Math.cos(angle) * dist,
        y: this.ball.y + Math.sin(angle) * dist,
        alpha: 0,
        scaleX: 0.25,
        duration: 210 + Math.random() * 110,
        ease: 'Quad.easeOut',
        onComplete: () => slash.destroy(),
      })
    }

    this.showSkillNotice(name, color)
  }

  private spawnSkillSpark(color: number, verticalSign: number): void {
    const spark = this.add.rectangle(this.ball.x, this.ball.y, 72, 4, color, 0.92)
    spark.setDepth(9.5)
    spark.rotation = Math.atan2(this.ballVy, this.ballVx) + verticalSign * 0.35
    this.tweens.add({
      targets: spark,
      alpha: 0,
      scaleX: 0.18,
      duration: 130,
      ease: 'Quad.easeOut',
      onComplete: () => spark.destroy(),
    })
  }

  private showSkillNotice(text: string, color: number): void {
    if (!this.skillNoticeText) return

    const cssColor = Phaser.Display.Color.IntegerToColor(color).rgba
    this.skillNoticeText.setText(text)
    this.skillNoticeText.setColor(cssColor)
    this.skillNoticeText.setAlpha(1)
    this.skillNoticeText.setScale(1.35)
    this.skillNoticeText.setAngle(Phaser.Math.Between(-3, 3))
    this.tweens.killTweensOf(this.skillNoticeText)
    this.tweens.add({
      targets: this.skillNoticeText,
      alpha: 0,
      scale: 1,
      angle: 0,
      duration: 650,
      ease: 'Quad.easeOut',
    })
  }

  private pulsePaddle(paddle: Phaser.GameObjects.Image, color: number): void {
    const pulse = this.add.rectangle(paddle.x, paddle.y, paddle.displayWidth + 18, PADDLE_VISUAL_HEIGHT + 18)
    pulse.setDepth(4)
    pulse.setStrokeStyle(2, color, 0.72)
    this.tweens.add({
      targets: pulse,
      scaleX: 1.45,
      scaleY: 1.2,
      alpha: 0,
      duration: 320,
      ease: 'Quad.easeOut',
      onComplete: () => pulse.destroy(),
    })
  }

  // ===== 合成音效 =====

  private getAudioContext(): AudioContext | null {
    if (this.audioContext) return this.audioContext

    const AudioCtor = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioCtor) return null

    this.audioContext = new AudioCtor()
    return this.audioContext
  }

  private resumeAudio(): void {
    const ctx = this.getAudioContext()
    if (ctx?.state === 'suspended') {
      void ctx.resume()
    }
  }

  private playTone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    endFrequency = frequency
  ): void {
    const ctx = this.audioContext
    if (!ctx || ctx.state !== 'running') return

    const now = ctx.currentTime
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    const filter = ctx.createBiquadFilter()
    const outputVolume = this.gameSfxVolume(volume)

    osc.type = type
    osc.frequency.setValueAtTime(Math.max(20, frequency), now)
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, endFrequency), now + duration)
    filter.type = 'lowpass'
    filter.frequency.setValueAtTime(2200, now)
    filter.Q.setValueAtTime(5, now)
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.exponentialRampToValueAtTime(outputVolume, now + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration)

    osc.connect(filter)
    filter.connect(gain)
    gain.connect(ctx.destination)
    osc.start(now)
    osc.stop(now + duration + 0.025)
    osc.onended = () => {
      osc.disconnect()
      filter.disconnect()
      gain.disconnect()
    }
  }

  private getRunningAudioContext(): AudioContext | null {
    const ctx = this.getAudioContext()
    if (!ctx) return null
    if (ctx.state === 'suspended') void ctx.resume()
    return ctx.state === 'running' ? ctx : null
  }

  private gameSfxVolume(volume: number, cap = 0.32): number {
    return Math.min(volume * GAME_SFX_VOLUME_SCALE, cap)
  }

  private collisionSfxVolume(volume: number, minVolume: number, cap = 0.68): number {
    return Phaser.Math.Clamp(this.gameSfxVolume(volume, cap), minVolume, cap)
  }

  private createNoiseSource(ctx: AudioContext, duration: number, texture: 'white' | 'soft' = 'white'): AudioBufferSourceNode {
    const sampleCount = Math.max(1, Math.floor(ctx.sampleRate * duration))
    const buffer = ctx.createBuffer(1, sampleCount, ctx.sampleRate)
    const data = buffer.getChannelData(0)
    let soft = 0

    for (let i = 0; i < sampleCount; i++) {
      const white = Math.random() * 2 - 1
      if (texture === 'soft') {
        soft = soft * 0.86 + white * 0.14
        data[i] = Phaser.Math.Clamp(soft * 2.4, -1, 1)
      } else {
        data[i] = white
      }
    }

    const source = ctx.createBufferSource()
    source.buffer = buffer
    return source
  }

  private disconnectAudioNodes(nodes: AudioNode[], delayMs: number): void {
    window.setTimeout(() => {
      for (const node of nodes) {
        try {
          node.disconnect()
        } catch {
          // The node may already be disconnected after a browser-level cleanup.
        }
      }
    }, delayMs)
  }

  private skillSfxVolume(volume: number, cap = 1.45): number {
    return Math.min(volume * SKILL_SFX_VOLUME_SCALE, cap)
  }

  private createDistortionCurve(amount: number): Float32Array<ArrayBuffer> {
    const samples = 256
    const curve = new Float32Array(new ArrayBuffer(samples * Float32Array.BYTES_PER_ELEMENT))
    const deg = Math.PI / 180

    for (let i = 0; i < samples; i++) {
      const x = (i * 2) / samples - 1
      curve[i] = ((3 + amount) * x * 20 * deg) / (Math.PI + amount * Math.abs(x))
    }

    return curve
  }

  private playSkillTone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    endFrequency = frequency
  ): void {
    this.playTone(frequency, duration, type, this.skillSfxVolume(volume, 0.24), endFrequency)
  }

  private createCompressedSfxBus(ctx: AudioContext, gainValue: number, now: number): { bus: GainNode; compressor: DynamicsCompressorNode } {
    const bus = ctx.createGain()
    const compressor = ctx.createDynamicsCompressor()
    bus.gain.setValueAtTime(gainValue, now)
    compressor.threshold.setValueAtTime(-16, now)
    compressor.knee.setValueAtTime(12, now)
    compressor.ratio.setValueAtTime(4.5, now)
    compressor.attack.setValueAtTime(0.0015, now)
    compressor.release.setValueAtTime(0.08, now)
    bus.connect(compressor)
    compressor.connect(ctx.destination)
    return { bus, compressor }
  }

  private playNoiseLayer(
    ctx: AudioContext,
    bus: AudioNode,
    startTime: number,
    duration: number,
    filterType: BiquadFilterType,
    fromFrequency: number,
    toFrequency: number,
    volume: number,
    q = 1.2,
    texture: 'white' | 'soft' = 'white'
  ): AudioNode[] {
    const noise = this.createNoiseSource(ctx, duration, texture)
    const filter = ctx.createBiquadFilter()
    const gain = ctx.createGain()
    filter.type = filterType
    filter.frequency.setValueAtTime(Math.max(20, fromFrequency), startTime)
    filter.frequency.exponentialRampToValueAtTime(Math.max(20, toFrequency), startTime + Math.max(duration * 0.72, 0.012))
    filter.Q.setValueAtTime(q, startTime)
    gain.gain.setValueAtTime(0.0001, startTime)
    gain.gain.linearRampToValueAtTime(volume, startTime + Math.min(duration * 0.22, 0.018))
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration)
    noise.connect(filter)
    filter.connect(gain)
    gain.connect(bus)
    noise.start(startTime)
    noise.stop(startTime + duration)
    return [noise, filter, gain]
  }

  private playOscLayer(
    ctx: AudioContext,
    bus: AudioNode,
    startTime: number,
    duration: number,
    type: OscillatorType,
    fromFrequency: number,
    toFrequency: number,
    volume: number,
    filterFrequency = 3400,
    q = 2.8
  ): AudioNode[] {
    const osc = ctx.createOscillator()
    const filter = ctx.createBiquadFilter()
    const gain = ctx.createGain()
    osc.type = type
    osc.frequency.setValueAtTime(Math.max(20, fromFrequency), startTime)
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, toFrequency), startTime + Math.max(duration * 0.78, 0.012))
    filter.type = 'lowpass'
    filter.frequency.setValueAtTime(filterFrequency, startTime)
    filter.Q.setValueAtTime(q, startTime)
    gain.gain.setValueAtTime(0.0001, startTime)
    gain.gain.linearRampToValueAtTime(volume, startTime + Math.min(duration * 0.18, 0.014))
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration)
    osc.connect(filter)
    filter.connect(gain)
    gain.connect(bus)
    osc.start(startTime)
    osc.stop(startTime + duration + 0.02)
    return [osc, filter, gain]
  }

  private playQianyingSkillSound(): void {
    const ctx = this.getRunningAudioContext()
    if (!ctx) return

    const now = ctx.currentTime
    const master = ctx.createGain()
    const delay = ctx.createDelay()
    const feedback = ctx.createGain()
    const wet = ctx.createGain()
    master.gain.setValueAtTime(this.skillSfxVolume(0.72, 1.35), now)
    delay.delayTime.setValueAtTime(0.095, now)
    feedback.gain.setValueAtTime(0.48, now)
    wet.gain.setValueAtTime(0.52, now)

    master.connect(ctx.destination)
    delay.connect(wet)
    wet.connect(master)
    delay.connect(feedback)
    feedback.connect(delay)

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(720, now)
    osc.frequency.exponentialRampToValueAtTime(860, now + 0.34)
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.linearRampToValueAtTime(this.skillSfxVolume(0.052, 0.14), now + 0.045)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.42)
    osc.connect(gain)
    gain.connect(master)
    gain.connect(delay)
    osc.start(now)
    osc.stop(now + 0.46)

    const echoNodes: AudioNode[] = [master, delay, feedback, wet, osc, gain]
    for (let i = 0; i < 4; i++) {
      const t = now + 0.08 + i * 0.072
      const echo = ctx.createOscillator()
      const echoGain = ctx.createGain()
      echo.type = 'sine'
      echo.frequency.setValueAtTime(650 + i * 76 + Math.random() * 48, t)
      echo.frequency.exponentialRampToValueAtTime(820 + i * 42, t + 0.2)
      echoGain.gain.setValueAtTime(0.0001, t)
      echoGain.gain.linearRampToValueAtTime(this.skillSfxVolume(0.018 * Math.pow(0.72, i), 0.07), t + 0.018)
      echoGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.24)
      echo.connect(echoGain)
      echoGain.connect(master)
      echo.start(t)
      echo.stop(t + 0.28)
      echoNodes.push(echo, echoGain)
    }

    this.disconnectAudioNodes(echoNodes, 980)
  }

  private playThunderSkillSound(): void {
    const ctx = this.getRunningAudioContext()
    if (!ctx) return

    const now = ctx.currentTime
    const noise = this.createNoiseSource(ctx, 0.48)
    const filter = ctx.createBiquadFilter()
    const gain = ctx.createGain()
    filter.type = 'bandpass'
    filter.frequency.setValueAtTime(3200, now)
    filter.frequency.exponentialRampToValueAtTime(6800, now + 0.26)
    filter.Q.setValueAtTime(2.2, now)
    gain.gain.setValueAtTime(0.0001, now)
    for (let i = 0; i < 8; i++) {
      const t = now + i * 0.042
      gain.gain.linearRampToValueAtTime(this.skillSfxVolume(0.068 + Math.random() * 0.032, 0.19), t + 0.004)
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.03)
    }
    noise.connect(filter)
    filter.connect(gain)
    gain.connect(ctx.destination)
    noise.start(now)
    noise.stop(now + 0.48)

    this.playSkillTone(980, 0.09, 'square', 0.04, 1800)
    this.time.delayedCall(70, () => this.playSkillTone(1450, 0.08, 'square', 0.025, 2600))
    this.disconnectAudioNodes([noise, filter, gain], 720)
  }

  private playLightSkillSound(): void {
    const ctx = this.getRunningAudioContext()
    if (!ctx) return

    const now = ctx.currentTime
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'triangle'
    osc.frequency.setValueAtTime(2300, now)
    osc.frequency.exponentialRampToValueAtTime(145, now + 0.27)
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.linearRampToValueAtTime(this.skillSfxVolume(0.06, 0.16), now + 0.018)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.29)
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(now)
    osc.stop(now + 0.32)

    const wind = this.createNoiseSource(ctx, 0.32)
    const windFilter = ctx.createBiquadFilter()
    const windGain = ctx.createGain()
    windFilter.type = 'highpass'
    windFilter.frequency.setValueAtTime(850, now)
    windFilter.frequency.exponentialRampToValueAtTime(2600, now + 0.2)
    windGain.gain.setValueAtTime(0.0001, now)
    windGain.gain.linearRampToValueAtTime(this.skillSfxVolume(0.034, 0.1), now + 0.016)
    windGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3)
    wind.connect(windFilter)
    windFilter.connect(windGain)
    windGain.connect(ctx.destination)
    wind.start(now)
    wind.stop(now + 0.32)

    this.disconnectAudioNodes([osc, gain, wind, windFilter, windGain], 620)
  }

  private playFlameSkillSound(): void {
    const ctx = this.getRunningAudioContext()
    if (!ctx) return

    const now = ctx.currentTime
    const fire = this.createNoiseSource(ctx, 0.68, 'soft')
    const filter = ctx.createBiquadFilter()
    const gain = ctx.createGain()
    const lfo = ctx.createOscillator()
    const lfoGain = ctx.createGain()
    filter.type = 'lowpass'
    filter.frequency.setValueAtTime(150, now)
    filter.frequency.linearRampToValueAtTime(720, now + 0.12)
    filter.frequency.exponentialRampToValueAtTime(105, now + 0.62)
    filter.Q.setValueAtTime(0.72, now)
    lfo.type = 'sine'
    lfo.frequency.setValueAtTime(8.5, now)
    lfoGain.gain.setValueAtTime(150, now)
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.linearRampToValueAtTime(this.skillSfxVolume(0.07, 0.18), now + 0.04)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.65)
    lfo.connect(lfoGain)
    lfoGain.connect(filter.frequency)
    fire.connect(filter)
    filter.connect(gain)
    gain.connect(ctx.destination)
    fire.start(now)
    fire.stop(now + 0.68)
    lfo.start(now)
    lfo.stop(now + 0.68)

    this.playSkillTone(86, 0.42, 'sawtooth', 0.036, 42)
    this.time.delayedCall(95, () => this.playSkillTone(220, 0.16, 'triangle', 0.022, 92))
    this.disconnectAudioNodes([fire, filter, gain, lfo, lfoGain], 920)
  }

  private playSerpentSkillSound(): void {
    const ctx = this.getRunningAudioContext()
    if (!ctx) return

    const now = ctx.currentTime
    const water = this.createNoiseSource(ctx, 0.58, 'soft')
    const filter = ctx.createBiquadFilter()
    const gain = ctx.createGain()
    filter.type = 'bandpass'
    filter.frequency.setValueAtTime(520, now)
    filter.frequency.linearRampToValueAtTime(1180, now + 0.18)
    filter.frequency.exponentialRampToValueAtTime(420, now + 0.54)
    filter.Q.setValueAtTime(0.85, now)
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.linearRampToValueAtTime(this.skillSfxVolume(0.044, 0.13), now + 0.05)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.56)
    water.connect(filter)
    filter.connect(gain)
    gain.connect(ctx.destination)
    water.start(now)
    water.stop(now + 0.58)

    const glide = ctx.createOscillator()
    const glideGain = ctx.createGain()
    glide.type = 'sine'
    glide.frequency.setValueAtTime(410, now)
    glide.frequency.linearRampToValueAtTime(610, now + 0.2)
    glide.frequency.exponentialRampToValueAtTime(360, now + 0.52)
    glideGain.gain.setValueAtTime(0.0001, now)
    glideGain.gain.linearRampToValueAtTime(this.skillSfxVolume(0.025, 0.08), now + 0.04)
    glideGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.56)
    glide.connect(glideGain)
    glideGain.connect(ctx.destination)
    glide.start(now)
    glide.stop(now + 0.58)

    for (let i = 0; i < 4; i++) {
      this.time.delayedCall(70 + i * 62, () => this.playSkillTone(900 + i * 115, 0.045, 'sine', 0.009, 1280 + i * 80))
    }

    this.disconnectAudioNodes([water, filter, gain, glide, glideGain], 820)
  }

  private playFoldSkillSound(): void {
    const ctx = this.getRunningAudioContext()
    if (!ctx) return

    const now = ctx.currentTime
    const bus = ctx.createGain()
    const compressor = ctx.createDynamicsCompressor()
    bus.gain.setValueAtTime(1.05, now)
    compressor.threshold.setValueAtTime(-18, now)
    compressor.knee.setValueAtTime(14, now)
    compressor.ratio.setValueAtTime(5, now)
    compressor.attack.setValueAtTime(0.002, now)
    compressor.release.setValueAtTime(0.12, now)
    bus.connect(compressor)
    compressor.connect(ctx.destination)

    const arc = this.createNoiseSource(ctx, 0.46)
    const arcFilter = ctx.createBiquadFilter()
    const arcDrive = ctx.createWaveShaper()
    const arcGain = ctx.createGain()
    arcFilter.type = 'bandpass'
    arcFilter.frequency.setValueAtTime(3800, now)
    arcFilter.frequency.linearRampToValueAtTime(7600, now + 0.1)
    arcFilter.frequency.exponentialRampToValueAtTime(3100, now + 0.4)
    arcFilter.Q.setValueAtTime(6.8, now)
    arcDrive.curve = this.createDistortionCurve(46)
    arcDrive.oversample = '2x'
    arcGain.gain.setValueAtTime(0.0001, now)
    for (let i = 0; i < 12; i++) {
      const t = now + i * 0.034 + Math.random() * 0.012
      arcGain.gain.setValueAtTime(0.0001, t)
      arcGain.gain.linearRampToValueAtTime(this.skillSfxVolume(0.062 + Math.random() * 0.05, 0.22), t + 0.003)
      arcGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.026 + Math.random() * 0.018)
    }
    arc.connect(arcFilter)
    arcFilter.connect(arcDrive)
    arcDrive.connect(arcGain)
    arcGain.connect(bus)
    arc.start(now)
    arc.stop(now + 0.46)

    const hiss = this.createNoiseSource(ctx, 0.28)
    const hissFilter = ctx.createBiquadFilter()
    const hissGain = ctx.createGain()
    hissFilter.type = 'highpass'
    hissFilter.frequency.setValueAtTime(2200, now)
    hissFilter.frequency.exponentialRampToValueAtTime(5200, now + 0.16)
    hissGain.gain.setValueAtTime(0.0001, now)
    hissGain.gain.linearRampToValueAtTime(this.skillSfxVolume(0.034, 0.11), now + 0.012)
    hissGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28)
    hiss.connect(hissFilter)
    hissFilter.connect(hissGain)
    hissGain.connect(bus)
    hiss.start(now)
    hiss.stop(now + 0.28)

    const snap = ctx.createOscillator()
    const snapGain = ctx.createGain()
    snap.type = 'square'
    snap.frequency.setValueAtTime(1180, now)
    snap.frequency.exponentialRampToValueAtTime(4100, now + 0.028)
    snap.frequency.exponentialRampToValueAtTime(260, now + 0.13)
    snapGain.gain.setValueAtTime(0.0001, now)
    snapGain.gain.linearRampToValueAtTime(this.skillSfxVolume(0.055, 0.16), now + 0.006)
    snapGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14)
    snap.connect(snapGain)
    snapGain.connect(bus)
    snap.start(now)
    snap.stop(now + 0.16)

    const nodes: AudioNode[] = [bus, compressor, arc, arcFilter, arcDrive, arcGain, hiss, hissFilter, hissGain, snap, snapGain]
    for (let i = 0; i < 8; i++) {
      const t = now + 0.035 + i * 0.041 + Math.random() * 0.017
      const spark = ctx.createOscillator()
      const sparkGain = ctx.createGain()
      spark.type = i % 2 === 0 ? 'square' : 'sawtooth'
      spark.frequency.setValueAtTime(2600 + Math.random() * 3400, t)
      spark.frequency.exponentialRampToValueAtTime(480 + Math.random() * 1300, t + 0.045)
      sparkGain.gain.setValueAtTime(0.0001, t)
      sparkGain.gain.linearRampToValueAtTime(this.skillSfxVolume(0.02 + Math.random() * 0.022, 0.095), t + 0.004)
      sparkGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.052)
      spark.connect(sparkGain)
      sparkGain.connect(bus)
      spark.start(t)
      spark.stop(t + 0.068)
      nodes.push(spark, sparkGain)
    }

    this.disconnectAudioNodes(nodes, 760)
  }

  private playDoubleSkillSound(): void {
    const ctx = this.getRunningAudioContext()
    if (!ctx) return

    const now = ctx.currentTime
    const master = ctx.createGain()
    const delay = ctx.createDelay()
    const feedback = ctx.createGain()
    const wet = ctx.createGain()
    master.gain.setValueAtTime(this.skillSfxVolume(0.7, 1.3), now)
    delay.delayTime.setValueAtTime(0.12, now)
    feedback.gain.setValueAtTime(0.52, now)
    wet.gain.setValueAtTime(0.5, now)
    master.connect(ctx.destination)
    delay.connect(wet)
    wet.connect(master)
    delay.connect(feedback)
    feedback.connect(delay)

    const nodes: AudioNode[] = [master, delay, feedback, wet]
    const voices = [
      { start: 0, from: 680, to: 820, volume: 0.042 },
      { start: 0.055, from: 820, to: 1060, volume: 0.026 },
      { start: 0.055, from: 620, to: 410, volume: 0.024 },
    ]

    for (const voice of voices) {
      const t = now + voice.start
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.setValueAtTime(voice.from, t)
      osc.frequency.exponentialRampToValueAtTime(voice.to, t + 0.34)
      gain.gain.setValueAtTime(0.0001, t)
      gain.gain.linearRampToValueAtTime(this.skillSfxVolume(voice.volume, 0.12), t + 0.03)
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.48)
      osc.connect(gain)
      gain.connect(master)
      gain.connect(delay)
      osc.start(t)
      osc.stop(t + 0.52)
      nodes.push(osc, gain)
    }

    this.disconnectAudioNodes(nodes, 1180)
  }

  private playHitSound(zone: HitZone, charge: number, returnFxTier = 0): void {
    const ctx = this.getRunningAudioContext()
    if (!ctx) return

    const now = ctx.currentTime
    const speed = Math.hypot(this.ballVx, this.ballVy)
    const speedT = Phaser.Math.Clamp((speed - 420) / 980, 0, 1)
    const chargeT = Phaser.Math.Clamp(charge, 0, 1)
    const pressureT = Phaser.Math.Clamp(this.pressureLevel / 3, 0, 1)
    const tierT = Phaser.Math.Clamp(returnFxTier / 4, 0, 1)
    const tierPitch = returnFxTier * 46
    const zoneT = zone === 'center' ? 0 : zone === 'slice' ? 0.5 : 1
    const impactVolume = this.collisionSfxVolume(
      0.28 + zoneT * 0.035 + speedT * 0.055 + pressureT * 0.035 + chargeT * 0.09 + tierT * 0.065,
      PADDLE_HIT_SFX_MIN_VOLUME,
      0.78
    )
    const { bus, compressor } = this.createCompressedSfxBus(ctx, returnFxTier > 0 ? 1.22 + tierT * 0.12 : 1.04, now)
    const nodes: AudioNode[] = [bus, compressor]

    if (returnFxTier > 0) {
      window.dispatchEvent(new CustomEvent('ping-pong:return-hit', { detail: { tier: returnFxTier } }))
    }

    const attackFreq = (zone === 'center' ? 1850 : zone === 'slice' ? 2250 : 2850) + tierPitch
    nodes.push(...this.playNoiseLayer(
      ctx,
      bus,
      now,
      0.022 + tierT * 0.008,
      'bandpass',
      attackFreq,
      attackFreq * 0.92,
      impactVolume * (zone === 'edge' ? 1.02 : 0.9),
      zone === 'edge' ? 8.2 : 6.6
    ))

    const bodyStart = (zone === 'center' ? 760 : zone === 'slice' ? 840 : 920) + tierPitch * 0.45
    const bodyEnd = (zone === 'center' ? 610 : zone === 'slice' ? 680 : 740) + tierPitch * 0.28
    nodes.push(...this.playOscLayer(
      ctx,
      bus,
      now + 0.002,
      0.048 + chargeT * 0.018,
      'triangle',
      bodyStart + pressureT * 55,
      bodyEnd + speedT * 35,
      impactVolume * (0.62 + tierT * 0.08),
      2600,
      3.8
    ))

    nodes.push(...this.playOscLayer(
      ctx,
      bus,
      now + 0.004,
      0.055 + chargeT * 0.025,
      'sine',
      220 + chargeT * 64 + speedT * 32,
      150 + pressureT * 18,
      impactVolume * (0.32 + chargeT * 0.2 + tierT * 0.08),
      1200,
      1.4
    ))

    if (zone === 'slice') {
      nodes.push(...this.playNoiseLayer(ctx, bus, now + 0.012, 0.028, 'bandpass', 2450 + tierPitch, 2200 + tierPitch * 0.7, impactVolume * 0.34, 5.8, 'soft'))
    } else if (zone === 'edge') {
      nodes.push(...this.playNoiseLayer(ctx, bus, now + 0.009, 0.024, 'bandpass', 3300 + tierPitch, 3050 + tierPitch * 0.72, impactVolume * 0.4, 8.6))
      nodes.push(...this.playOscLayer(ctx, bus, now + 0.012, 0.038, 'triangle', 1320 + tierPitch * 0.4, 1050 + tierPitch * 0.25, impactVolume * 0.22, 3600, 4.8))
    } else {
      nodes.push(...this.playNoiseLayer(ctx, bus, now + 0.01, 0.024, 'bandpass', 1550 + tierPitch * 0.5, 1420 + tierPitch * 0.32, impactVolume * 0.24, 5.2, 'soft'))
    }

    if (chargeT > 0.14) {
      const chargeVolume = impactVolume * Phaser.Math.Linear(0.35, 0.58, chargeT)
      nodes.push(...this.playNoiseLayer(ctx, bus, now + 0.012, 0.038, 'bandpass', 1900, 1700, chargeVolume * 0.22, 4.2))
      nodes.push(...this.playOscLayer(ctx, bus, now + 0.006, 0.072, 'triangle', 180 + chargeT * 80, 112, chargeVolume * 0.42, 850, 1.1))
    }

    if (returnFxTier > 0) {
      const variant = Math.random() * 0.16 - 0.08
      const whooshVolume = impactVolume * (0.68 + tierT * 0.38 + speedT * 0.16)
      const startFreq = 4200 + returnFxTier * 640 + speedT * 1450 + variant * 900
      const endFreq = 760 + returnFxTier * 220 + variant * 160
      const duration = 0.095 + returnFxTier * 0.03 + speedT * 0.04

      nodes.push(...this.playNoiseLayer(
        ctx,
        bus,
        now + 0.006,
        duration,
        'bandpass',
        startFreq,
        endFreq,
        whooshVolume,
        3.6 + returnFxTier * 0.72,
        returnFxTier >= 3 ? 'white' : 'soft'
      ))
      nodes.push(...this.playNoiseLayer(
        ctx,
        bus,
        now,
        0.012 + returnFxTier * 0.002,
        'highpass',
        5600 + returnFxTier * 580,
        3600 + returnFxTier * 280,
        impactVolume * (0.4 + tierT * 0.16),
        1.1
      ))

      if (returnFxTier >= 2) {
        nodes.push(...this.playOscLayer(
          ctx,
          bus,
          now + 0.004,
          0.052 + tierT * 0.018,
          'sine',
          92 + speedT * 18,
          54,
          impactVolume * (0.16 + tierT * 0.1),
          520,
          0.8
        ))
      }
    }

    this.disconnectAudioNodes(nodes, 220 + returnFxTier * 55)
  }

  private playWallSound(): void {
    const ctx = this.getRunningAudioContext()
    if (!ctx) return

    const now = ctx.currentTime
    const speed = Math.hypot(this.ballVx, this.ballVy)
    const speedT = Phaser.Math.Clamp((speed - 380) / 920, 0, 1)
    const pressureT = Phaser.Math.Clamp(this.pressureLevel / 3, 0, 1)
    const impactVolume = this.collisionSfxVolume(
      0.18 + speedT * 0.04 + pressureT * 0.032,
      WALL_HIT_SFX_MIN_VOLUME,
      0.42
    )
    const { bus, compressor } = this.createCompressedSfxBus(ctx, 0.88, now)
    const nodes: AudioNode[] = [bus, compressor]
    const ping = 720 + speedT * 180 + this.pressureLevel * 42

    nodes.push(...this.playNoiseLayer(ctx, bus, now, 0.014, 'bandpass', 2050, 1850, impactVolume * 0.62, 5.8))
    nodes.push(...this.playOscLayer(ctx, bus, now + 0.002, 0.045 + pressureT * 0.012, 'triangle', ping, ping * 0.78, impactVolume * 0.56, 2700, 4.2))
    nodes.push(...this.playOscLayer(ctx, bus, now + 0.006, 0.04, 'sine', ping * 1.36, ping * 1.08, impactVolume * 0.12, 3200, 5.2))

    if (speedT > 0.48 || this.pressureLevel >= 2) {
      nodes.push(...this.playNoiseLayer(ctx, bus, now + 0.008, 0.024, 'bandpass', 2650, 2300, impactVolume * 0.16, 5.6))
    }

    this.disconnectAudioNodes(nodes, 150)
  }

  private playServeSound(): void {
    this.playTone(420, 0.08, 'sine', 0.016, 660)
  }

  private playScoreSound(scorer: 'p1' | 'p2'): void {
    if (scorer === 'p1') {
      this.playTone(520, 0.12, 'triangle', 0.026, 880)
      this.playTone(780, 0.16, 'sine', 0.018, 1040)
    } else {
      this.playTone(260, 0.16, 'sawtooth', 0.02, 130)
    }
  }

  private playChargeReadySound(): void {
    this.playTone(620, 0.08, 'sine', 0.018, 820)
    this.time.delayedCall(70, () => this.playTone(920, 0.08, 'triangle', 0.015, 1180))
  }

  private playPressureSound(): void {
    this.playTone(460 + this.pressureLevel * 70, 0.1, 'triangle', 0.018, 690 + this.pressureLevel * 90)
    this.time.delayedCall(80, () => {
      this.playTone(620 + this.pressureLevel * 90, 0.12, 'sine', 0.016, 880 + this.pressureLevel * 120)
    })
  }

  private playSkillCueSound(id: SkillId): void {
    const pitch = this.getSkillPitch(id)
    this.playTone(pitch * 0.72, 0.07, 'sine', 0.014, pitch)
  }

  private playSkillActivateSound(id: SkillId): void {
    switch (id) {
      case 'qianying':
        this.playQianyingSkillSound()
        return
      case 'thunder':
        this.playThunderSkillSound()
        return
      case 'light':
        this.playLightSkillSound()
        return
      case 'flame':
        this.playFlameSkillSound()
        return
      case 'serpent':
        this.playSerpentSkillSound()
        return
      case 'fold':
        this.playFoldSkillSound()
        return
      case 'double':
        this.playDoubleSkillSound()
        return
    }
  }

  private getSkillPitch(id: SkillId): number {
    switch (id) {
      case 'qianying':
        return 720
      case 'flame':
        return 380
      case 'thunder':
        return 980
      case 'light':
        return 1180
      case 'serpent':
        return 640
      case 'fold':
        return 840
      case 'double':
        return 760
    }
  }
}
